import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from "react";
import { db, staff, supabase, voicemailAudio } from "./supabase";
import { initGoogleAuth, signIn, signOut, isSignedIn, getAuthEmail } from './googleAuth';
import { sendEmail, sendBulkEmails, createDraft, getEmailsForContact } from './gmailService';
import { deliveryStateOf, bubbleStyleFor, stateBadge, HANDLED_OFFLINE } from './lib/messageStatus';
import { escalationHandledLabel, markOpenHandledOffline } from './lib/escalationState';
import { isNeedsHuman, formTypePillFor } from './lib/leadClassification';
import {
  ALL, NO_FILTERS, isUnfiltered, activityByConversation, activityFor, sortByActivity,
  channelOf, channelsPresent, statusesPresent, applyFilters, describeFilters, countLabel,
} from './lib/conversationView';
import { decisionsByLead, decisionsFor, decisionDisplay, decisionsSummary } from './lib/leadDecisions';
import {
  waitingOn, WAITING_ON_US, WAITING_ON_THEM,
  waitingMinutes, waitingBadge, orderOldestWaitingFirst, noReplyLabel, isOverdue,
} from './lib/conversationTriage';
import { isSpamMarked, spamButtonFor, applySpamMark, leadColumns, isSpamTagged, spamTagLabel, spamTaggedCount } from './lib/spamMark';
import { reviewRequestText } from './lib/reviewRequest';
import { parseConversationParam, findConversationById, withoutConversationParam } from './lib/conversationDeepLink';
import { eligibleIds, confirmText, runBulk, resultText } from './lib/bulkHandledOffline';
import { isHandled, needsAttention, HANDLED_FILTERS, passesHandledFilter, performHandled, withHandledColumns, markLeadHandled, applyHandled as applyLeadHandled } from './lib/leadHandled';
import NeedsHumanView from './NeedsHumanView';
import { needsHumanRows } from './lib/needsHuman';
import AppSettings from './AppSettings';
import GatherBar from './GatherBar';
import { ANY as F_ANY, DISCIPLINES as LEAD_DISCIPLINES, KINDS, SOURCES, FILTER_DEFAULTS, passesLeadFilters, kindCounts, readLeadFilters, writeLeadFilters, filtersActive } from './lib/leadFilters';
import { signedUpFor, consentLine } from './lib/signedUp';
import { syncAppBadge, deliveryLine, TONE_COLOR } from './lib/appPolish';
import { leadMatches, candidateMatches } from './lib/textSearch';
import Timeline from './Timeline';
import DuplicateStrip from './DuplicateStrip';
import AlertRows from './AlertRows';
import Today from './Today';
import { buildToday, withoutDone, doneForToday, etDay, withRates } from './lib/today';
import { NAV, navOf, resolveView, NEEDS_FILTERS, CANDIDATE_TABS, CANDIDATE_FILTERS } from './lib/nav';   // NAV-2026-10-22
import FilterRow from './FilterRow';
import JobsMatch from './JobsMatch';
import Reviews from './Reviews';                                       // NIGHT-DIRECTSTAFF-2026-10-23 §1
import { makeReviewsApi, pendingCount as reviewsPending } from './lib/reviews';
import { makeFacilityPortalApi, slotText, telHref, interviewAlertId } from './lib/facilityPortal';
import InstallBanner from './InstallBanner';                           // NIGHT-DIRECTSTAFF-2026-10-24 §7             // NIGHT-DIRECTSTAFF-2026-10-23 §2
import { candidateMatch, cachedIndex, setMergedCandidates } from './lib/personMatch';         // NIGHT-DIRECTSTAFF-2026-10-23 §4                                   // "Jobs that match" (Matt, 2026-10-23)
import { fetchMatchingJobs } from './lib/jobsMatch';
import { textConsentOf } from './lib/sendLink';
import { examLink as todayExamLink } from './lib/exams';
import FacilityCard from './FacilityCard';
import PortalContact from './PortalContact';   // DAY-DIRECTSTAFF-2026-10-25 §1/§4: a portal-only facility contact
import GlobalSearch from './GlobalSearch';     // NIGHT-DIRECTSTAFF-2026-10-25b §2
import { searchEverywhere, flatItems } from './lib/globalSearch';
import { alertRows, isFacilityLead, saveFacilityPipeline, probeFacilityColumns, facilityNewFirst, facilityFields, FACILITY_STATUSES } from './lib/facility';
import SavedViews from './SavedViews';
import { snapshot, applied, sortRows, SORTS, makeViewsApi, newViewId } from './lib/savedViews';
import { isMerged, dupIds, mergeLeads, undoMerge, probeMergeColumns } from './lib/duplicates';
import { loadTimeline, isTrace, buildTimeline } from './lib/timeline';
import PullToRefresh from './PullToRefresh';
import { isTestMarked, withoutTests, testCount, readShowTests, writeShowTests, probeTestColumns, markLeadTest, testLabel, testAlertIds, testAlertsWrite } from './lib/testMark';
import PushSettings from './PushSettings';
import { makePushApi } from './lib/push';
import { LIVE_MS, LIVE_VIEWS, readSeen, writeSeen, freshCounts, titleWith, shouldRefresh } from './lib/liveRefresh';
import { HandledCell, HandledRowForm, HandledBulkBar, HandledUndoBanner } from './LeadHandled';
import { TextButton, TextCompose } from './StaffText';
import { STAFF_SENDER_LABEL, SMS_TEMPLATES_KEY, parseTemplates, serializeTemplates, textButtonState } from './lib/staffText';
import { SEARCH_DEFAULTS, CONSENT_OPTIONS, DRAFT_OPTIONS, STATE_OPTIONS, disciplineOptions, matchesSearch, searchActive,
  parseLeadParam, leadHref } from './lib/leadSearch';
// SECOND-TOUCH-2026-10-02: drafted second contacts, sent only by a human from here.
import { DraftsView, DraftPanel, LeadDraftButton } from './Drafts';
import { NewCandidateButton, NewCandidateModal } from './NewCandidate';
import { NewOnboardingLinkButton, NewOnboardingLinkModal } from './NewOnboardingLink';
import LocalTimeBadge from './LocalTimeBadge';
import { leadForConversation } from './lib/localTime';
import { parseDraftParam, withoutDraftParam, awaitingCount, latestByLead } from './lib/drafts';
import { makeDraftHandlers } from './lib/draftHandlers';
import OnboardingTab from './OnboardingTab';
import OnboardingView from './OnboardingView';
import { makeOnboardingApi, onboardingConfig } from './lib/onboarding';
import { makeExamsApi } from './lib/exams';
import { makeSendApi } from './lib/sendLink';
import EmailCompose, { EmailButton, NoEmailNote } from './EmailCompose';
import Placements from './Placements';
import Offers from './Offers';                                         // NIGHT-DIRECTSTAFF-2026-10-23b §1
import CandidateDuplicates from './CandidateDuplicates';               // §2
import CandidateTestMark from './CandidateTestMark';                   // NIGHT-DIRECTSTAFF-2026-10-23c §4
import CandidateEmail from './CandidateEmail';                         // DAY-DIRECTSTAFF-2026-10-24 §4
import OnboardingProgress from './OnboardingProgress';                 // NIGHT-DIRECTSTAFF-2026-10-24 §4
import CandidateAvailability from './CandidateAvailability';           // NIGHT-DIRECTSTAFF-2026-10-24 §1
import { makeAvailabilityApi, availabilityShort } from './lib/availability';
import CandidateProfile, { ProfileSuggestion, makeProfileLink } from './CandidateProfile';   // DAY-DIRECTSTAFF-2026-10-30b
import { jobOf } from './lib/jobRef';                                  // DAY-DIRECTSTAFF-2026-10-31 §1
import { makeCandidateFromLeadApi, canCreateFromLead, leadLinked, applicantsWithoutCandidate } from './lib/candidateFromLead';   // NIGHT-DIRECTSTAFF-2026-10-29 addendum
import { timeInStage, pastSlaCounts, makeNotSameApi, weekStrip, duplicateList, bulkCloseNote } from './lib/night29';   // NIGHT-DIRECTSTAFF-2026-10-29 §1 §2
import DuplicatesView from './DuplicatesView';                           // NIGHT-DIRECTSTAFF-2026-10-29 §1
import OnboardingDocs from './OnboardingDocs';                           // NIGHT-DIRECTSTAFF-2026-10-29 §4
import WorkView from './WorkView';                                         // DAY-DIRECTSTAFF-2026-11-05 §1/§2: the recruiter's working view
import { presentTargets, PRESENT_LOG_TYPE, funnelDays, contactInputs } from './lib/workView';
import BulkClose72 from './BulkClose72';                                 // DAY-DIRECTSTAFF-2026-10-29b §2
import { handoffGroups } from './lib/handoffGroups';                       // DAY-DIRECTSTAFF-2026-11-03 §2: the YES hand-offs on Today's week
import { bookkeepingRows } from './lib/exportCsv';                     // NIGHT-DIRECTSTAFF-2026-10-29 §5
import { readyToPresent } from './lib/presentReady';   // DAY-DIRECTSTAFF-2026-11-08 §1
import { offersWaiting, callOnlyCandidates } from './lib/today';   // DAY-DIRECTSTAFF-2026-11-10 §1
import { makeProfileApi, makeSummary, briefProfileCounts, newestLinks, notStarted, notOpened, resendByEmail, interestedHandoff, candidateForLead, suggestProfile, profileReminder, profileNumbers, profileWatch, watchLine, plainError as profileError } from './lib/profile';
import { usableEmail } from './lib/emailUsable';
import { isTestMarked as isCandTest, probeCandidateTestCols, markCandidateTest } from './lib/candidateTest';
import { makeOffersApi } from './lib/offers';
import { makeRequestOfferApi } from './lib/requestOffer';                // DAY-DIRECTSTAFF-2026-10-24 §1
import { isMergedCandidate, probeCandidateMerge, mergeCandidates, resolveMerged } from './lib/candidateDupes';
import { makePlacementsApi, probePlacements, currentPlacement, headerLine } from './lib/placements';
import { endingSoon, endingSoonIds } from './lib/endingSoon';
import { startChecks } from './lib/placementNudges';                    // NIGHT-DIRECTSTAFF-2026-10-23c §5
import { voiceRows } from './lib/voice';                                // NIGHT-DIRECTSTAFF-2026-10-24 §5
import { makeCallsApi, callBacksDue, calledSince, refOfCall } from './lib/calls';   // NIGHT-DIRECTSTAFF-2026-10-25 §2
import { makeAssignApi, assignees as assigneesOf } from './lib/assign';           // NIGHT-DIRECTSTAFF-2026-10-25b §1
import { SNOOZE_KEY, activeSnoozes, withSnooze, overdueCount } from './lib/snooze';                // NIGHT-DIRECTSTAFF-2026-10-25c §1
import CredentialsReport from './CredentialsReport';
import { expiryCounts } from './lib/credReport';                     // NIGHT-DIRECTSTAFF-2026-10-28b §1                                  // §2
import PipelineBoard from './PipelineBoard';                                          // NIGHT-DIRECTSTAFF-2026-10-25d §1
import { buildBoard } from './lib/pipeline';                          // NIGHT-DIRECTSTAFF-2026-10-28b §5: Numbers' pipeline counts
import ThreadView from './ThreadView';                                                // §3
import ExportPanel from './ExportPanel';                                              // §4
import EsignAudit from './EsignAudit';
import StaffRoles from './StaffRoles';
import { startsThisWeek } from './lib/placementSchedule';                                              // NIGHT-DIRECTSTAFF-2026-10-28 §4
import { makeStaffApi } from './lib/staff';                                                // §5
import { loadRole, isOwner as ownerRole } from './lib/roles';                          // §2
import FacilityProblems from './FacilityProblems';                                    // §4
import { problemReports } from './lib/facilityPortal';
import LogCall from './LogCall';
import { makeMatchApi } from './lib/facilityMatch';
import Credentials from './Credentials';
import Reports from './Reports';
import { periodStart } from './lib/reports';
import CandidateMemory, { MEMORY_NOTE_TYPE } from './CandidateMemory';
import BulkMarks from './BulkMarks';
import AuditView from './AuditView';
import { actionFor, moveIndex, isDesktop, isTyping, KEYS } from './lib/keyboard';
import { makeCredentialsApi, probeCredentialColumns, expiringSoon } from './lib/credentials';
import OfflineLists, { OutboxBar, OfflineBanner } from './OfflineLists';
import { snapshotOf, saveSnapshot, readSnapshot, readOutbox, enqueue, flushOutbox, isOffline, removeQueued, sendQueued } from './lib/offline';
import { makeEmailApi, probeEmailColumns, leadOptedOut, isOptedOut, leadsWithAddress } from './lib/email';
import { shapeOverview, filterCounts } from './lib/onboardingOverview';
import ExpirationsView from './ExpirationsView';
import SystemAlerts from './SystemAlerts';
import { openSystemAlertCount, markSystemAlertHandled, applyHandled, shortAlertId } from './lib/systemAlerts';
import { parseAlertParam, withoutAlertParam, alertTarget } from './lib/alertDeepLink';
import { makeExpirationsApi } from './lib/expirations';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const onboardingApi = makeOnboardingApi(supabase);
// ONBOARD-EXAMS-v2-2026-10-16: exams, a separate send (lib/exams.js); dark until the exams migration is applied
const examsApi = makeExamsApi(supabase);
// EXAMS-SEND-2026-10-16: the Send dialog's copy / text / email, and the timeline entries (lib/sendLink.js)
const sendApi = makeSendApi({ supabase, staff });
const emailApi = makeEmailApi({ supabase, staff });   // EMAIL-CRM-2026-10-22: every CRM email, from Matt, through crm-mailer
const placementsApi = makePlacementsApi({ supabase }); // NIGHT-DIRECTSTAFF-2026-10-22 §1: the agents window's placements table
const matchApi = makeMatchApi({ supabase });           // NIGHT-DIRECTSTAFF-2026-10-22 §3: Match candidates (activity_log)
const credentialsApi = makeCredentialsApi({ supabase }); // §8 (10-22): credentials on the onboarding's own documents
// NIGHT-DIRECTSTAFF-2026-10-22 §6: offline, a text or an email is kept on this device ("Sending when online") and sent once
// when the connection is back (lib/offline.js). Every compose box sends through these two.
const lsOrNull = () => { try { return window.localStorage; } catch { return null; } };
const outboxEvents = typeof EventTarget !== "undefined" ? new EventTarget() : null;
const queueSend = (kind, payload) => { const ls = lsOrNull(); if (ls) enqueue(ls, { kind, payload }); if (outboxEvents) outboxEvents.dispatchEvent(new Event("change")); return { queued: true }; };
const textOrQueue = (leadId, body, sendAnyway) => (isOffline() ? Promise.resolve(queueSend("text", { leadId, body, sendAnyway: sendAnyway === true })) : staff.textLead(leadId, body, sendAnyway));
const emailOrQueue = (p) => (isOffline() ? Promise.resolve({ ...queueSend("email", p), logged: false }) : emailApi.send(p));
const textToast = (r) => (r && r.queued ? "Saved: sending when online" : r && r.duplicate ? "Already sent; not sent twice" : "Text sent");
// §4 (10-22): the status board's "documents missing" -- which kinds each candidate has uploaded (no file, no path); null = not readable
const loadBoardDocuments = async () => {
  const { data, error } = await supabase.from('onboarding_documents').select('candidate_id,kind,uploaded_at');
  return error ? null : (data || []);
};
// DAY-DIRECTSTAFF-2026-10-22: which health uploads each candidate's facility requires (null before the hub's migration)
// Matt 2026-10-23: "Jobs that match" -- through the CRM's /api/jobs (functions/api/jobs.js holds JOBS_API_TOKEN)
const reviewsApi = makeReviewsApi(supabase);
const offersApi = makeOffersApi(supabase);                             // NIGHT-DIRECTSTAFF-2026-10-23b §1
const availabilityApi = makeAvailabilityApi(supabase);                 // NIGHT-DIRECTSTAFF-2026-10-24 §1
const profileApi = makeProfileApi(supabase);                           // DAY-DIRECTSTAFF-2026-10-30b: the candidate profile
const cflApi = makeCandidateFromLeadApi(supabase);                     // NIGHT-DIRECTSTAFF-2026-10-29 addendum: candidate_from_lead
const notSameApi = makeNotSameApi({ getSetting: (k) => db.getSetting(k), saveSettingChecked: (k, v) => db.saveSettingChecked(k, v) });   // NIGHT-DIRECTSTAFF-2026-10-29 §1
const facilityPortalApi = makeFacilityPortalApi(supabase);
const callsApi = makeCallsApi(supabase);                                // NIGHT-DIRECTSTAFF-2026-10-25 §2: Log call / call-backs
const assignApi = makeAssignApi(supabase);                              // NIGHT-DIRECTSTAFF-2026-10-25b §1: bulk Assign + the bulk log
// DAY-DIRECTSTAFF-2026-10-24 §1: after facilityPortalApi (it uses it -- a module-level const read before its line throws at load)
const requestOfferApi = makeRequestOfferApi({ supabase, portalApi: facilityPortalApi, saveLeadStatus: (id, patch) => saveFacilityPipeline(supabase, id, patch) });
const JOBS_KIT = { load: ({ discipline, states, startAfter }) => fetchMatchingJobs({ supabase, discipline, states, startAfter }) };
const loadBoardHealth = async () => {
  const { data, error } = await supabase.from('onboarding_health_required').select('candidate_id,tb_test,physical');
  return error ? null : (data || []);
};
// CRM-LEADS-2026-10-16 §5: Web Push opt-in per device (lib/push.js)
const pushApi = makePushApi({ supabase });
const onboardingCfg = onboardingConfig();
const expirationsApi = makeExpirationsApi(supabase);

const STAGES = ["New Lead","Outreach Sent","Contacted","Scheduling Call","Engaged","Docs Collecting","Submission Ready","Submitted","Needs Resubmission","Negotiating","Active Contract","On Hold","Partnership","Completed","Inactive"];
const PRIORITY_COLORS = { "🔥 Hot":"#ef4444","Warm":"#f59e0b","Cold":"#6b7280","Placed":"#22c55e","Opportunity":"#8b5cf6","On Hold":"#94a3b8" };
const SPEC_COLOR = { PT:"#3b82f6",OT:"#8b5cf6",SLP:"#ec4899",PTA:"#10b981",COTA:"#f59e0b",SLPA:"#a855f7" };
const STAGE_COLOR = { "New Lead":"#06b6d4","Outreach Sent":"#6b7280","Contacted":"#6b7280","Scheduling Call":"#8b5cf6","Engaged":"#3b82f6","Docs Collecting":"#f59e0b","Submission Ready":"#06b6d4","Submitted":"#f97316","Needs Resubmission":"#ef4444","Negotiating":"#a855f7","Active Contract":"#22c55e","On Hold":"#94a3b8","Partnership":"#8b5cf6","Completed":"#374151","Inactive":"#374151" };

// ── Lead / conversation matching helpers ─────────────────────────────────────
const digits10 = (s) => { const d = String(s || '').replace(/\D/g, ''); return d.length >= 10 ? d.slice(-10) : ''; };
const emailKey = (s) => String(s || '').trim().toLowerCase();
/** The candidate a lead belongs to: by email, by the candidate's phone, or (NIGHT-DIRECTSTAFF-2026-10-23 §4) by the phone on one of the
 *  candidate's own leads -- so a phone-only lead joins the candidate made from their email (lib/personMatch.js). */
function findCandidateForLead(lead, candidates, leads = null) {
  const m = candidateMatch(lead, cachedIndex(candidates, leads || []));
  return m ? m.candidate : null;
}
/** Find an agent_conversation matching a phone (last-10). */
function findConversationForPhone(phone, conversations) {
  const p = digits10(phone);
  if (!p) return null;
  return (conversations || []).find(cv => digits10(cv.candidate_phone) === p) || null;
}
const CONTRACT_ALERT_DAYS = 42;
const FOLLOW_UP_DAYS = { "New Lead":1,"Outreach Sent":3,"Contacted":2,"Scheduling Call":1,"Engaged":3,"Docs Collecting":2,"Submission Ready":1,"Submitted":5,"Needs Resubmission":1,"Negotiating":3,"Active Contract":56,"On Hold":30,"Partnership":7,"Completed":90,"Inactive":999 };
const DISCIPLINES = ["PT","OT","SLP","PTA","COTA","SLPA"];
const JOB_CSV_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vSWopxXjZFfRc1ORAB9ZwMi4DFnlqQsSjx9Py2EkB4SIdMu5iDWBlfHM2wRCSn6jTnt0tNAqq92kzA_/pub?gid=1621626963&single=true&output=csv";

const EMAIL_TEMPLATES_DEFAULT = [
  { id:"outreach", label:"Thanks for Reaching Out", icon:"👋", stage:"New Lead",
    subject:"Welcome to ProTherapy Staffing — {name}",
    body:`Hi {name},\n\nThank you so much for reaching out to Pro Therapy Staffing! My name is Matthew Michuda, PT, DPT — I'm the owner and a fellow clinician. I started this agency because I believe travel therapists deserve better.\n\nWe're the #1 ranked travel therapy company on TravelTherapyReviews.com and have been therapist-owned and operated since 2012.\n\nI'd love to learn more about what you're looking for so I can start matching you with the best opportunities. What's the best time to connect this week?\n\nLooking forward to it!\n\nMatthew Michuda, PT, DPT\nPro Therapy Staffing\n260-241-7887\nmmichuda@protherapystaffing.com` },
  { id:"whatneeded", label:"What's Needed to Submit", icon:"📋", stage:"Docs Collecting",
    subject:"Quick checklist to get you submitted — {name}",
    body:`Hi {name},\n\nGreat talking with you! To get you submitted for the {specialty} position, I'll need the following:\n\n1. Updated resume\n2. Preferred start date\n3. Any requested time off\n4. Date of birth & last 4 of SSN\n5. Copy of BLS certification\n6. 3 professional references (supervisor preferred) — name, email, and phone for each\n7. Skills checklist (attached)\n\nOnce I have these, I can submit you right away. Let me know if you have any questions!\n\nBest,\nMatthew` },
  { id:"extension", label:"Contract Extension", icon:"🔄", stage:"Active Contract",
    subject:"Contract extension — let's talk next steps, {name}",
    body:`Hi {name},\n\nYour current contract is wrapping up soon and I wanted to check in. How's everything going?\n\nI have a few options:\n1. Extend at your current facility (if available)\n2. New contracts in your preferred locations\n3. Take a break and reconnect when you're ready\n\nWhat are you thinking? I've been pulling some great {specialty} contracts that might interest you.\n\nLet me know!\nMatthew` },
  { id:"jobpitch", label:"Job Pitch", icon:"🎯", stage:"Engaged",
    subject:"{specialty} opportunity in {state} — {name}",
    body:`Hi {name},\n\nJust wanted to flag a {specialty} contract that came in:\n\n📍 Location: {city}, {state}\n🏥 Setting: {setting}\n💰 Pay: {pay}/week\n📅 Start: {start}\n\nThis looked like it could be a great fit based on what we discussed. Want me to submit you?\n\nMatthew` },
  { id:"followup", label:"Follow-up Check-in", icon:"⏰", stage:"Contacted",
    subject:"Checking in — {name}",
    body:`Hi {name},\n\nJust wanted to follow up and see if you had any questions about travel therapy or if your timeline has shifted at all.\n\nI'm here whenever you're ready — no pressure. Just want to make sure you have the best options when the time comes.\n\nBest,\nMatthew` },
  { id:"vip", label:"VIP Signup Welcome", icon:"⭐", stage:"New Lead",
    subject:"Welcome to VIP Job Alerts — {name}",
    body:`Hi {name},\n\nThanks for signing up for VIP job alerts from Pro Therapy Staffing!\n\nYou'll be among the first to hear about new {specialty} travel contracts as they come in. In the meantime, feel free to check out our current openings at protherapystaffing.com/jobs.\n\nIf you'd like to chat about what you're looking for, I'm always happy to connect.\n\nMatthew Michuda, PT, DPT\nPro Therapy Staffing\n260-241-7887` }
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const today = () => new Date().toISOString().split('T')[0];
const daysUntil = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : 999;
const daysSince = (d) => d ? Math.ceil((new Date() - new Date(d)) / 86400000) : 999;
const fmtDate = (d) => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'}) : '—';
const fmtDateFull = (d) => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';

function calcNextFollowUp(c) {
  // Check for explicit followup tag
  const fuTag = (c.tags||[]).find(t => typeof t === 'string' && t.startsWith('followup:'));
  if (fuTag) return fuTag.split(':')[1];
  if (!c.last_contact) return today();
  const days = FOLLOW_UP_DAYS[c.stage] || 7;
  const d = new Date(c.last_contact);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function getAvailableDate(c) {
  const tag = (c.tags||[]).find(t => typeof t === 'string' && t.startsWith('available:'));
  return tag ? tag.split(':')[1] : null;
}

function getSource(c) {
  const tag = (c.tags||[]).find(t => typeof t === 'string' && t.startsWith('source:'));
  return tag ? tag.replace('source:','').replace(/-/g,' ') : null;
}

function credCount(c) {
  if (!c.creds) return 0;
  const creds = typeof c.creds === 'string' ? JSON.parse(c.creds) : c.creds;
  return Object.values(creds).filter(Boolean).length;
}

function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().replace(/"/g,''));
  return lines.slice(1).map(line => {
    const vals = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { vals.push(current.trim()); current = ''; }
      else { current += ch; }
    }
    vals.push(current.trim());
    const obj = {};
    headers.forEach((h,i) => { obj[h] = (vals[i]||'').trim(); });
    return obj;
  });
}

// Collapse in-feed duplicate rows (same job listed multiple times).
// Key mirrors the site's job identity: Discipline|State|City|Setting|Start Date|Pay.
function dedupeJobs(rows) {
  const seen = new Set();
  return rows.filter(r => {
    const key = ['Discipline','State','City','Setting','Start Date','Pay']
      .map(k => (r[k]||'').trim().toLowerCase()).join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ─── ICON COMPONENT ──────────────────────────────────────────────────────────
function I({n,s=16,c="currentColor"}) {
  const paths = {
    dashboard:"M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z",
    users:"M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z",
    pipeline:"M3 3v18h18V3H3zm8 16H5v-6h6v6zm0-8H5V5h6v6zm8 8h-6v-6h6v6zm0-8h-6V5h6v6z",
    jobs:"M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z",
    msg:"M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z",
    mail:"M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
    form:"M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z",
    remind:"M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z",
    settings:"M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z",
    search:"M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z",
    check:"M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
    x:"M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
    edit:"M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z",
    send:"M2.01 21L23 12 2.01 3 2 10l15 2-15 2z",
    phone:"M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
    star:"M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",
    add:"M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z",
    copy:"M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z",
    sync:"M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z",
    filter:"M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z",
    back:"M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z",
    draft:"M21.99 8c0-.72-.37-1.35-.94-1.7L12 1 2.95 6.3C2.38 6.65 2 7.28 2 8v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2l-.01-10zm-10 5L3.74 7.84 12 3l8.26 4.84L12 13z",
    clock:"M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z",
    pin:"M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
  };
  return <svg width={s} height={s} viewBox="0 0 24 24" fill={c}><path d={paths[n]||paths.check}/></svg>;
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const STYLES = `
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif; background:#f1f5f9; }
  /* §5 (NIGHT-DIRECTSTAFF-2026-10-17): the lists' own pull-to-refresh, not the browser's page reload on top of it */
  html, body { overscroll-behavior-y: contain; }
  .nb { background:none; border:none; cursor:pointer; font-family:inherit; }
  .input { border:1.5px solid #e2e8f0; border-radius:9px; padding:9px 13px; font-size:14px; font-family:inherit; outline:none; transition:border .15s; width:100%; }
  .input:focus { border-color:#3b82f6; }
  .card { background:white; border-radius:12px; border:1px solid #e2e8f0; }
  .card-hover:hover { border-color:#bfdbfe; box-shadow:0 4px 12px #3b82f620; }
  .pill { display:inline-flex; align-items:center; gap:4px; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
  select { border:1.5px solid #e2e8f0; border-radius:9px; padding:8px 12px; font-size:13px; font-family:inherit; outline:none; background:white; cursor:pointer; }
  /* NIGHT-DIRECTSTAFF-2026-10-28b §4: one visible focus ring for the keyboard, everywhere. The fields and selects above turn the
     browser's off (a select had nothing in its place); a mouse click does not show it (:focus-visible). */
  :focus-visible { outline:3px solid #1d4ed8; outline-offset:2px; }
  .input:focus-visible, select:focus-visible { outline:2px solid #1d4ed8; outline-offset:1px; border-color:#1d4ed8; }
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
  @keyframes spin { to{transform:rotate(360deg)} }
  .fade-in { animation: fadeUp .3s ease; }
  .tab-btn { padding:8px 16px; border-radius:8px 8px 0 0; font-size:13px; font-weight:600; border:none; cursor:pointer; font-family:inherit; transition:all .15s; }
  .tab-btn.active { background:white; color:#1d4ed8; border-bottom:2px solid #1d4ed8; }
  .tab-btn:not(.active) { background:transparent; color:#475569; }   /* 10-19: #64748b on the page grey was 4.34:1 */
  .tab-btn:not(.active):hover { color:#1d4ed8; background:#eff6ff; }
  .email-row { padding:12px 16px; border-bottom:1px solid #f1f5f9; cursor:pointer; transition:background .1s; }
  .email-row:hover { background:#f8fafc; }
  /* CRM-ONBOARDING-TAB-2026-10-13: on a phone the sidebar becomes a scrolling top bar, so the nav (Onboarding included) is one tap away */
  @media (max-width: 760px) {
    .crm-shell { flex-direction:column !important; height:auto !important; min-height:100vh; overflow:visible !important; }
    .crm-side { width:100% !important; }
    .crm-side-brand { padding:10px 12px !important; }
    .crm-nav { display:flex; flex:none !important; overflow-x:auto !important; overflow-y:hidden !important; padding:6px !important; gap:2px; }
    .crm-nav > button { width:auto !important; flex:0 0 auto; white-space:nowrap; margin-bottom:0 !important; }
    .crm-side-foot { display:none; }
    .crm-main { overflow:visible !important; }
    .crm-topbar { padding:0 12px !important; }
    .crm-search { width:100% !important; }
    .crm-content { padding:12px !important; overflow:visible !important; }
    /* NIGHT-DIRECTSTAFF-2026-10-16 §2: the lead screens, System alerts and Onboarding at 360px -- every control thumb-sized,
       and the lead table becomes one stacked card per lead (each cell carries its column name) instead of scrolling sideways */
    .phone-tap button, .phone-tap select, .phone-tap .tap-link, .phone-tap label { min-height:44px; }
    .phone-tap .tap-link { display:inline-flex; align-items:center; justify-content:center; min-width:44px; }
    .phone-tap input[type=checkbox] { width:24px; height:24px; }
    .lead-table { min-width:0 !important; }
    .lead-table thead th:not(:first-child) { display:none; }
    .lead-table, .lead-table thead, .lead-table tbody, .lead-table tr, .lead-table th, .lead-table td { display:block; width:100%; }
    .lead-table tbody tr { border-bottom:1px solid #e2e8f0; padding:6px 0; }
    .lead-table td { border-bottom:none !important; padding:4px 12px !important; white-space:normal !important; overflow-wrap:anywhere; }
    .lead-table td[data-label]::before { content:attr(data-label); display:block; font-size:11px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.3px; }
    .lead-table .lead-actions { display:flex; flex-wrap:wrap; gap:8px; }
    .lead-table .lead-actions > * { margin-left:0 !important; }
    /* NIGHT-DIRECTSTAFF-2026-10-28b §4: the candidates table and the conversations table the same way (both scrolled sideways
       inside their card at 360px); the candidates' cells are named by their place, the conversations' by data-label */
    .stack-table { min-width:0 !important; }
    .stack-table thead th:not(:first-child) { display:none; }
    .stack-table, .stack-table thead, .stack-table tbody, .stack-table tr, .stack-table th, .stack-table td { display:block; width:100% !important; }
    .stack-table tbody tr { border-bottom:1px solid #e2e8f0; padding:6px 0; }
    .stack-table td { border-bottom:none !important; padding:4px 12px !important; white-space:normal !important; overflow-wrap:anywhere; }
    .stack-table td:empty { display:none; }
    .stack-table td[data-label]::before { content:attr(data-label); display:block; font-size:11px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.3px; }
    .cand-table tbody td:nth-child(3)::before { content:"Specialty"; display:block; font-size:11px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.3px; }
    .cand-table tbody td:nth-child(4)::before { content:"Stage"; display:block; font-size:11px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.3px; }
    .cand-table tbody td:nth-child(5)::before { content:"Priority"; display:block; font-size:11px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.3px; }
    .cand-table tbody td:nth-child(6)::before { content:"State"; display:block; font-size:11px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.3px; }
    .cand-table tbody td:nth-child(7)::before { content:"Creds"; display:block; font-size:11px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.3px; }
    .cand-table tbody td:nth-child(8)::before { content:"Last contact"; display:block; font-size:11px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.3px; }
    .cand-table tbody td:nth-child(9)::before { content:"Source"; display:block; font-size:11px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.3px; }
  }
  .toast { position:fixed; bottom:24px; right:24px; background:#0f172a; color:white; padding:12px 20px; border-radius:10px; font-size:13px; font-weight:600; z-index:9999; animation:fadeUp .3s ease; box-shadow:0 8px 24px #0002; }
`;

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  // NIGHT-DIRECTSTAFF-2026-10-16 §1: Matt lands on "Needs a human" -- the leads waiting on a person -- unless the
  // address is a ?lead= link, which opens the Leads view on that lead (LeadsView reads the same parameter).
  // NIGHT-DIRECTSTAFF-2026-10-21 §4: on a phone the CRM opens on Today (the morning call list); on a desktop, Needs a human
  const [view, setViewRaw] = useState(() => (parseLeadParam(typeof window !== "undefined" ? window.location.search : "") ? "leads"
    : (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 768px)").matches ? "today" : "needs")));
  // NAV-2026-10-22: a request for a retired view (Dashboard, Pipeline, Jobs, Messaging, Inbox, Formspree, Reminders) lands on its replacement
  const setView = useCallback((v) => setViewRaw(resolveView(v)), []);
  // DAY-DIRECTSTAFF-2026-11-09 §2: Today opens a lead inside the app -- the address carries ?lead= as before (Back works, the link can be
  // shared) and LeadsView reads it on mount or on popstate; it was a full page load, every list read again (seconds, live)
  const viewNow = useRef(view); viewNow.current = view;
  const openLeadHere = useCallback((id) => {
    window.history.pushState({}, '', leadHref(id));
    if (viewNow.current === 'leads') window.dispatchEvent(new PopStateEvent('popstate')); else setView('leads');
    window.scrollTo(0, 0);
  }, [setView]);
  const [candidates, setCandidates] = useState([]);
  // NIGHT-DIRECTSTAFF-2026-10-23b §2: records merged into another -- out of every list; their phone / email still find the person
  const [mergedCands, setMergedCands] = useState([]);
  // DAY-DIRECTSTAFF-2026-10-24 §1: "Make offer" on a facility request's Interested card -> this candidate's Offers form, pre-filled
  const [offerDraft, setOfferDraft] = useState(null);
  const [candMergeCols, setCandMergeCols] = useState(false);
  // 10-23c §4: test records (candidates.test_marked_at) are out of `candidates` -- every list, count, Today and the duplicate check;
  // "Show tests" puts them back in the Candidates list only
  const [testCands, setTestCands] = useState([]);
  const [candTestCols, setCandTestCols] = useState(false);
  const [availCols, setAvailCols] = useState(false);     // NIGHT-DIRECTSTAFF-2026-10-24 §1: the availability columns applied?
  // DAY-DIRECTSTAFF-2026-10-30b: the profile (null = the hub's file not there, or not readable); status rows + every link, ids only
  const [profileData, setProfileData] = useState(null);
  // DAY-DIRECTSTAFF-2026-10-29 §4: Today's "This week" strip -- interviews booked in the next 7 days (null = not readable), and the
  // pairs staff said are not the same (for "Duplicates pending")
  const [ivBooked, setIvBooked] = useState(null);
  const [notSameSet, setNotSameSet] = useState(null);
  const [cflOn, setCflOn] = useState(false);              // NIGHT-DIRECTSTAFF-2026-10-29 addendum: candidate_from_lead applied?
  const loadProfiles = useCallback(async () => {
    // 11-08 §1: + the hub's profile_ready_unpresented (null = not readable: no "Ready to present" on Today)
    try { const [status, sessions, ready] = await Promise.all([profileApi.status(), profileApi.sessions(), profileApi.readyUnpresented()]); setProfileData({ status, sessions, ready }); }
    catch (e) { console.error("Profile load error:", e); }
  }, []);
  const profileByCand = useMemo(() => new Map(((profileData && profileData.status) || []).map((r) => [String(r.candidate_id), r])), [profileData]);
  const profileLinks = useMemo(() => newestLinks((profileData && profileData.sessions) || []), [profileData]);
  const takeCandidates = (all) => { const list = all || []; const merged = list.filter(isMergedCandidate); setMergedCandidates(merged); setMergedCands(merged);
    const rest = list.filter((c) => !isMergedCandidate(c)); setTestCands(rest.filter(isCandTest)); setCandidates(rest.filter((c) => !isCandTest(c))); };
  const [allActivity, setAllActivity] = useState([]);
  const [allEmailSync, setAllEmailSync] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  // New onboarding link (hub 2026-10-09): the modal, and the tab a candidate opens on (the duplicate guard opens Onboarding)
  const [showOnbLink, setShowOnbLink] = useState(false);
  const [detailTab, setDetailTab] = useState(null);
  const [presentFor, setPresentFor] = useState(null);   // 11-08 §1: a candidate opened from Today's "Ready to present" -> the Present panel open
  useEffect(() => { if (!selected) setDetailTab(null); }, [selected]);
  // CRM-ONBOARDING-TAB-2026-10-13: the Onboarding tab's rows, loaded here because the nav badge counts "Needs me"
  const [onbOverview, setOnbOverview] = useState(null);
  const [onbFocus, setOnbFocus] = useState(null);     // the modal's duplicate guard, on the Onboarding tab: open that panel
  const loadOnbOverview = useCallback(async () => {
    try { setOnbOverview(await onboardingApi.overview()); } catch (e) { setOnbOverview({ error: e.message }); }
  }, []);
  useEffect(() => { loadOnbOverview(); }, [loadOnbOverview]);
  const [search, setSearch] = useState("");
  // NIGHT-DIRECTSTAFF-2026-10-19 §1: the top search box searches the list you are on -- leads on Leads, candidates elsewhere
  const [leadQuery, setLeadQuery] = useState("");
  const [liveTick, setLiveTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [dbOk, setDbOk] = useState(false);
  const [candidateFilter, setCandidateFilter] = useState("all");
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [gmailAuth, setGmailAuth] = useState({ signedIn: false, email: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [templates, setTemplates] = useState([]);
  // Leads + Conversations tabs
  // NIGHT-DIRECTSTAFF-2026-10-17 §4: allLeads is everything loaded; `leads` -- what every list and count uses -- leaves out
  // the leads marked as a test, unless "Show tests" (per device) is on. Nothing is deleted.
  const [allLeads, setLeads] = useState([]);
  const [testCols, setTestCols] = useState(false);   // the columns' probe; false until it answers present
  const [mergeCols, setMergeCols] = useState(false);  // NIGHT-DIRECTSTAFF-2026-10-19 §4: leads.merged_into there yet?
  const [facilityCols, setFacilityCols] = useState(false);   // NIGHT-DIRECTSTAFF-2026-10-21 §1: the facility pipeline columns there yet?
  const [emailCols, setEmailCols] = useState(false);         // EMAIL-CRM-2026-10-22: leads.email_opt_out there yet? (the hub's SQL)
  const [emailTo, setEmailTo] = useState(null);               // the open Email box: { to, who, leadId, subject, body, optedOut }
  const [placementsReady, setPlacementsReady] = useState(false);   // NIGHT-DIRECTSTAFF-2026-10-22 §1: the placements table there yet?
  const [placements, setPlacements] = useState([]);
  const [todayText, setTodayText] = useState(null);           // §2 (10-22): the Text box opened from "Ending soon" (no thread yet)
  const [portalContact, setPortalContact] = useState(null);   // 10-25 §1/§4: { email, name } -- a facility portal contact with no website lead
  // NIGHT-DIRECTSTAFF-2026-10-25b §2: search everywhere (under the top box) -- open, the highlighted row, the facility side (read on first use)
  const [gsOpen, setGsOpen] = useState(false);
  const [gsActive, setGsActive] = useState(-1);
  const [gsData, setGsData] = useState(null);
  const [credExtra, setCredExtra] = useState(false);          // §8 (10-22): the hub's credential columns there yet?
  const [credDocs, setCredDocs] = useState(null);             // every credential row (null = not readable)
  // §6 (10-22): offline -> the saved lists; the queue of sends written offline, sent once when back online
  const [offline, setOffline] = useState(() => isOffline());
  const [outbox, setOutbox] = useState(() => (lsOrNull() ? readOutbox(lsOrNull()) : []));
  const [outboxSending, setOutboxSending] = useState(false);
  const [showTests, setShowTestsRaw] = useState(() => readShowTests(typeof window !== "undefined" ? window.localStorage : null));
  const setShowTests = (on) => { setShowTestsRaw(on); writeShowTests(typeof window !== "undefined" ? window.localStorage : null, on); };
  // §4 (10-19): a lead merged into another is out of every list and count too (its id still opens the lead it joined)
  const leads = useMemo(() => withoutTests(allLeads, showTests).filter((l) => !isMerged(l)), [allLeads, showTests]);
  // {} until the probe answers -- so the action is hidden while we do not yet know,
  // which is the same state as "not there". Never optimistic.
  const [spamCols, setSpamCols] = useState({});
  // processed_at / processed_note present? false until the probe answers, so the
  // note input stays hidden while unknown (the "Mark handled" button itself only
  // needs `processed`, which exists).
  const [handledCols, setHandledCols] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [convMsgRefs, setConvMsgRefs] = useState([]);
  // §1 addendum: is the agents window's CRM text route (CRM-TEXT-2026-10-16) live? Until it is, Text says "coming soon".
  const [textReady, setTextReady] = useState(false);
  useEffect(() => { let live = true; staff.textReady().then((ok) => { if (live) setTextReady(ok); }); return () => { live = false; }; }, []);
  // agent_notifications, every row (the timeline reads them all); `notifications` -- what every alert list and count uses --
  // leaves out the TRACE rows (reason 'handed_back', born resolved: agents window 10-19), which are events, not alerts
  const [allNotifications, setNotifications] = useState([]);
  const notifications = useMemo(() => allNotifications.filter((n) => !isTrace(n)), [allNotifications]);
  // DAY-DIRECTSTAFF-2026-10-28: a lead's test mark closed (or reopened) its alerts -- folded in without a reload
  const onLeadAlertsChanged = (ids, patch) => setNotifications((ns) => ns.map((n) => (ids.includes(n.id) ? { ...n, ...patch } : n)));
  // System alerts bulk close: the note box is live only once agent_notifications.handled_note exists (probe, once).
  const [alertNoteColumn, setAlertNoteColumn] = useState(false);
  useEffect(() => { let live = true; db.alertNoteColumnPresent().then((ok) => { if (live) setAlertNoteColumn(ok === true); }); return () => { live = false; }; }, []);
  const [leadDecisions, setLeadDecisions] = useState([]);
  const [convDeepLink, setConvDeepLink] = useState(null); // phone to auto-open in Conversations
  // ?conversation= deep link (2026-09-28): the escalation email and the morning brief
  // link a thread by id. Read ONCE, only a well-formed UUID (lib/conversationDeepLink),
  // and stripped from the address bar so a refresh or a copied URL does not carry it.
  // Opened only once the agent tables have loaded and the id is a real thread; anything
  // else leaves the recruiter on the normal view. Never logged.
  const deepLinkIdRef = useRef(null);
  if (deepLinkIdRef.current === null) {
    deepLinkIdRef.current = parseConversationParam(window.location.search) || "";
    if (deepLinkIdRef.current && window.history && window.history.replaceState) {
      window.history.replaceState(null, "", window.location.pathname + withoutConversationParam(window.location.search) + window.location.hash);
    }
  }
  const [convDeepLinkId, setConvDeepLinkId] = useState(null);
  // end ?conversation= deep link
  // Second-touch drafts (agent_drafts) + the ?draft= deep link from the brief's block D.
  // Read once, only a well-formed UUID, stripped from the address bar like ?conversation=.
  const [drafts, setDrafts] = useState([]);
  const [draftBusy, setDraftBusy] = useState(null);
  const [draftFocus, setDraftFocus] = useState(null);
  const draftLinkRef = useRef(null);
  if (draftLinkRef.current === null) {
    draftLinkRef.current = parseDraftParam(window.location.search) || "";
    if (draftLinkRef.current && window.history && window.history.replaceState) {
      window.history.replaceState(null, "", window.location.pathname + withoutDraftParam(window.location.search) + window.location.hash);
    }
  }

  // ?alert=<id> deep link (FIX-AGENTS-2026-10-12 §7): the last line of every staff SMS and email. Read once, only a
  // well-formed UUID (lib/alertDeepLink), stripped from the address bar like ?conversation=; never logged.
  const [alertFocus, setAlertFocus] = useState(null);
  const alertLinkRef = useRef(null);
  if (alertLinkRef.current === null) {
    alertLinkRef.current = parseAlertParam(window.location.search) || "";
    if (alertLinkRef.current && window.history && window.history.replaceState) {
      window.history.replaceState(null, "", window.location.pathname + withoutAlertParam(window.location.search) + window.location.hash);
    }
  }

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  useEffect(() => {
    initGoogleAuth((authState) => { setGmailAuth(authState); });
  }, []);

  // ── AGENT TABLES: loadable on demand, not only at mount ──────────────────
  //
  // These four are the ONLY data in the CRM that a recruiter watches change during
  // a shift, and until now they were fetched once in a mount effect with no
  // interval, no realtime subscription and no refresh button. A tab left open since
  // Monday showed Monday. That is indistinguishable from an idle agent, and it is
  // the first thing the 2026-09-08 conversations audit had to rule out before any
  // other finding could be trusted.
  //
  // Extracted verbatim from the mount effect; the mount effect now calls it.
  const [agentRefreshing, setAgentRefreshing] = useState(false);
  const [agentLoadedAt, setAgentLoadedAt] = useState(null);

  // CRM-LEADS-2026-10-16 §2: { quiet: true } is the automatic refresh -- no "Refreshing..." flicker every 45 seconds
  const loadAgentTables = useCallback(async (opts = {}) => {
    if (!(opts && opts.quiet === true)) setAgentRefreshing(true);
    try {
    // One try/catch EACH, not one around a Promise.all.
    //
    // The comment above says these are "independent + tolerant: a missing RLS
    // grant on one table must not blank the whole CRM" — and the Promise.all
    // made that false. Promise.all rejects on the first failure, so a missing
    // grant on agent_notifications alone left conversations AND message refs
    // unset, and the Conversations tab said "No conversations yet" about a
    // table it had successfully read. Three awaits, three catches, so each
    // table's failure costs only that table. (They still run concurrently:
    // allSettled starts them together and cannot reject.)
    const [convsR, refsR, notesR, decsR, draftsR] = await Promise.allSettled([
      db.getAgentConversations(),
      db.getAgentMessageRefs(),
      db.getAgentNotifications(),
      db.getLeadDecisions(),
      db.getDrafts(),
    ]);
    // agent_drafts is the newest table (migrations/2026-10-02-agent-drafts.sql); absent
    // or unreadable leaves the Drafts tab empty and nothing else.
    if (draftsR.status === "fulfilled") {
      setDrafts(draftsR.value || []);
      if (draftLinkRef.current) {
        const hit = (draftsR.value || []).find((d) => d.id === draftLinkRef.current);
        draftLinkRef.current = "";
        if (hit) { setDraftFocus(hit.id); setView("drafts"); }
      }
    } else console.error("Drafts load error:", draftsR.reason);
    if (convsR.status === "fulfilled") setConversations(convsR.value || []);
    else console.error("Conversations load error:", convsR.reason);
    if (refsR.status === "fulfilled") setConvMsgRefs(refsR.value || []);
    else console.error("Message refs load error:", refsR.reason);
    if (notesR.status === "fulfilled") setNotifications(notesR.value || []);
    else console.error("Notifications load error:", notesR.reason);
    // The decision log is the newest table and the most likely to be absent
    // (its migration is run by hand). An empty log is a legitimate state, so a
    // failure here is logged and nothing else changes.
    if (decsR.status === "fulfilled") setLeadDecisions(decsR.value || []);
    else console.error("Lead decisions load error:", decsR.reason);
      setAgentLoadedAt(new Date());
      // ?conversation= deep link: resolve once, against what actually loaded.
      if (deepLinkIdRef.current && convsR.status === "fulfilled") {
        const hit = findConversationById(convsR.value, deepLinkIdRef.current);
        deepLinkIdRef.current = "";
        if (hit) { setConvDeepLinkId(hit.id); setView("conversations"); }
      }
      // ?alert= deep link: resolve once, against the alerts that loaded -- a thread's alert opens the thread, any other
      // opens System alerts on that row. Unknown id -> the normal view.
      if (alertLinkRef.current && notesR.status === "fulfilled") {
        const t = alertTarget(notesR.value, alertLinkRef.current);
        alertLinkRef.current = "";
        const conv = t && t.view === "conversations" && convsR.status === "fulfilled" ? findConversationById(convsR.value, t.conversationId) : null;
        if (conv) { setConvDeepLinkId(conv.id); setView("conversations"); }
        else if (t && t.view === "alerts") { setAlertFocus(t); setView("alerts"); }
      }
      // end ?conversation= deep link (resolve)
    } finally {
      setAgentRefreshing(false);
    }
  }, []);

  // ── LOAD DATA ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        // NAV-2026-10-22: the old `messages` table fed only the retired Messaging placeholder -- no longer read
        const [cands, acts, emails] = await Promise.all([
          db.getCandidates(),
          db.getAllActivity(),
          db.getAllEmailSync(),
        ]);
        takeCandidates(cands);
        setAllActivity(acts || []);
        setAllEmailSync(emails || []);
        setDbOk(true);
      } catch (e) {
        console.error("DB load error:", e);
        setDbOk(false);
      }
      // Leads + agent conversations/messages/notifications — independent + tolerant:
      // a missing RLS grant on one table must not blank the whole CRM.
      try { setLeads(await db.getLeads()); } catch (e) { console.error("Leads load error:", e); }
      // ONE probe per session for spam_marked_at. The migration
      // (pt-agents/db/2026-09-12-spam-marked-at.sql) is applied by hand and may not
      // have been; the Leads view hides the action entirely when it has not, rather
      // than offering a button whose write cannot land.
      try { setSpamCols(await db.spamColumnPresent()); } catch (e) { console.error("Spam column probe error:", e); }
      // §4 (NIGHT-DIRECTSTAFF-2026-10-17): ONE probe for leads.test_marked_at; Mark as test and Show tests stay hidden until it's there
      try { setTestCols(await probeTestColumns(supabase)); } catch (e) { console.error("Test column probe error:", e); }
      try { setMergeCols(await probeMergeColumns(supabase)); } catch (e) { console.error("Merge column probe error:", e); }
      try { setCandMergeCols(await probeCandidateMerge(supabase)); } catch (e) { console.error("Candidate merge probe error:", e); }
      try { setCandTestCols(await probeCandidateTestCols(supabase)); } catch (e) { console.error("Candidate test probe error:", e); }
      try { setAvailCols(await availabilityApi.probe()); } catch (e) { console.error("Availability probe error:", e); }
      try { if (await profileApi.probe()) await loadProfiles(); } catch (e) { console.error("Profile probe error:", e); }
      try { setCflOn(await cflApi.probe()); } catch (e) { console.error("candidate_from_lead probe error:", e); }
      try { setFacilityCols(await probeFacilityColumns(supabase)); } catch (e) { console.error("Facility column probe error:", e); }
      try { setEmailCols(await probeEmailColumns(supabase)); } catch (e) { console.error("Email column probe error:", e); }
      try { setPlacementsReady(await probePlacements(supabase)); } catch (e) { console.error("Placements probe error:", e); }
      try { setCredExtra(await probeCredentialColumns(supabase)); } catch (e) { console.error("Credential column probe error:", e); }
      // ONE probe per session for processed_at + processed_note
      // (migrations/2026-10-01-leads-processed-note.sql, applied by hand).
      try { setHandledCols(await db.handledColumnsPresent()); } catch (e) { console.error("Handled column probe error:", e); }
      await loadAgentTables();
      // Load templates
      try {
        let tpls = await db.getTemplates();
        if (tpls.length === 0) {
          for (const t of EMAIL_TEMPLATES_DEFAULT) {
            try { await db.addTemplate({ label:t.label, icon:t.icon, subject:t.subject||'', body:t.body, stage:t.stage||'', sort_order:0 }); } catch {}
          }
          tpls = await db.getTemplates();
        }
        setTemplates(tpls.length > 0 ? tpls : EMAIL_TEMPLATES_DEFAULT);
      } catch { setTemplates(EMAIL_TEMPLATES_DEFAULT); }
      // Load jobs CSV
      try {
        const res = await fetch(JOB_CSV_URL);
        const text = await res.text();
        setJobs(dedupeJobs(parseCSV(text)));
      } catch (e) { console.error("Jobs load error:", e); }
      setLoading(false);
    })();
  }, []);

  // ── CANDIDATE OPERATIONS ─────────────────────────────────
  // DAY-DIRECTSTAFF-2026-10-24 §4: a real address in place of a placeholder -- throws so the field can say "Not saved"
  const saveCandidateEmail = async (id, email) => {
    const updated = await db.updateCandidate(id, { email });
    if (!updated || usableEmail(updated.email) !== email) throw new Error("Not saved: the database did not accept the change (are you signed in?)");
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, email: updated.email } : c)));
    setSelected((s0) => (s0 && s0.id === id ? { ...s0, email: updated.email } : s0));
  };
  const updateCandidate = async (id, updates) => {
    try {
      const updated = await db.updateCandidate(id, updates);
      setCandidates(prev => prev.map(c => c.id === id ? { ...c, ...updated } : c));
      showToast("Updated");
    } catch (e) { showToast("Error: " + e.message); }
  };
  // NIGHT-DIRECTSTAFF-2026-10-25d §2: this staff account's role (owner until the hub applies the roles file and sets a recruiter)
  const [role, setRole] = useState("owner");
  useEffect(() => { loadRole(supabase).then(setRole).catch(() => setRole("owner")); }, []);
  const owner = ownerRole(role);
  const staffApi = useMemo(() => makeStaffApi(supabase), []);   // 10-28 §4
  const [staffEmail, setStaffEmail] = useState("");
  // DAY-DIRECTSTAFF-2026-11-01 §3: a presented card's "Attach candidate summary" -- the same PDF as Download (its own log line, not that one)
  const summaryKit = useMemo(() => (profileData ? { by: staffEmail, make: (candidate) => makeSummary({ api: { ...profileApi, logSummary: async () => {} }, candidate,
    row: profileByCand.get(String(candidate.id)) || null, fetchBytes: async (u) => { const r = await fetch(u); if (!r.ok) throw new Error(`The resume could not be read (HTTP ${r.status})`); return new Uint8Array(await r.arrayBuffer()); } }) } : null),
    [profileData, profileByCand, staffEmail]);   // eslint-disable-line
  useEffect(() => { supabase.auth.getUser().then(({ data }) => setStaffEmail((data && data.user && data.user.email) || "")).catch(() => {}); }, []);
  // NIGHT-DIRECTSTAFF-2026-10-25d §1: Candidates -> List | Pipeline, and the open offers the board reads
  const [candMode, setCandMode] = useState("list");
  const [pipeOffers, setPipeOffers] = useState([]);
  useEffect(() => { if (candMode !== "pipeline") return; supabase.from("offers").select("id,candidate_id,status").limit(5000)
    .then(({ data, error }) => setPipeOffers(error ? [] : (data || []))).catch(() => setPipeOffers([])); }, [candMode, liveTick]);   // eslint-disable-line
  const movePipeline = async (c, stage) => {
    const updated = await db.updateCandidate(c.id, { stage });
    setCandidates((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...updated, stage } : x)));
    let by = ""; try { const { data } = await supabase.auth.getUser(); by = (data && data.user && data.user.email) || ""; } catch { by = ""; }
    try { const act = await db.addActivity(c.id, "stage", `Stage ${c.stage || "none"} → ${stage} (moved on the Pipeline)${by ? ` · by ${by}` : ""}`); setAllActivity((prev) => [act, ...prev]); } catch { /* the move stands; the line is best-effort */ }
  };
  const addActivity = async (candidateId, type, note) => {
    try {
      const act = await db.addActivity(candidateId, type, note);
      setAllActivity(prev => [act, ...prev]);
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, last_contact: today() } : c));
      showToast("Activity logged");
    } catch (e) { showToast("Error: " + e.message); }
  };

  const addCandidate = async (data) => {
    try {
      const newC = await db.addCandidate({ ...data, created_at: new Date().toISOString(), updated_at: new Date().toISOString() });
      setCandidates(prev => [newC, ...prev]);
      showToast("Candidate added");
      return newC;
    } catch (e) { showToast("Error: " + e.message); }
  };

  const deleteCandidate = async (id, name) => {
    try {
      await db.deleteCandidate(id);
      setCandidates(prev => prev.filter(c => c.id !== id));
      setAllActivity(prev => prev.filter(a => a.candidate_id !== id));
      setSelected(null);
      showToast(`Deleted ${name}`);
    } catch (e) { showToast("Error: " + e.message); }
  };

  const saveTemplate = async (tpl) => {
    try {
      if (tpl.id) {
        const updated = await db.updateTemplate(tpl.id, { label:tpl.label, icon:tpl.icon, subject:tpl.subject, body:tpl.body, stage:tpl.stage, sort_order:tpl.sort_order||0 });
        setTemplates(prev => prev.map(t => t.id === tpl.id ? updated : t));
      } else {
        const added = await db.addTemplate({ label:tpl.label, icon:tpl.icon||'📧', subject:tpl.subject||'', body:tpl.body||'', stage:tpl.stage||'', sort_order:tpl.sort_order||0 });
        setTemplates(prev => [...prev, added]);
      }
      showToast("Template saved");
    } catch (e) { showToast("Error: " + e.message); }
  };

  const delTemplate = async (id) => {
    try {
      await db.deleteTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      showToast("Template deleted");
    } catch (e) { showToast("Error: " + e.message); }
  };

  // ── COMPUTED ─────────────────────────────────────────────
  // NIGHT-DIRECTSTAFF-2026-10-22 §2: placements ending within 4 weeks (with the re-placement agent's status, read only)
  const endingItems = useMemo(() => endingSoon({ placements, candidates, decisions: leadDecisions, leads: allLeads, conversations, now: Date.now() }),
    [placements, candidates, leadDecisions, allLeads, conversations]);
  const endingIds = endingSoonIds(endingItems);
  // §9 (10-22): credentials expiring within 60 / 30 / 14 days
  const expiringItems = useMemo(() => expiringSoon({ documents: credDocs || [], candidates, now: Date.now() }), [credDocs, candidates]);
  // NIGHT-DIRECTSTAFF-2026-10-29 §2: + pastSla, the cards past their stage's soft SLA (lib/night29.js), on the same candidates
  const pipeCounts = useMemo(() => ({ ...buildBoard({ candidates, offers: pipeOffers, placements }), pastSla: pastSlaCounts({ candidates, activity: allActivity, offers: pipeOffers, placements }) }),
    [candidates, pipeOffers, placements, allActivity]);   // 28b §5: Numbers = the board
  const loadExportRequests = useCallback(() => facilityPortalApi.searchData(), []);   // 28b §3: the portal's requests, read on download
  const credCounts = useMemo(() => (credDocs === null ? null : expiryCounts({ documents: credDocs, candidates, now: Date.now() })), [credDocs, candidates]);   // 28b §1
  const filtered = (showTests && candTestCols ? [...candidates, ...testCands] : candidates).filter(c => {
    // Apply candidateFilter first
    if (candidateFilter === "hot" && c.priority !== "🔥 Hot") return false;
    if (candidateFilter === "active" && c.stage !== "Active Contract") return false;
    if (candidateFilter === "overdue") {
      const nf = calcNextFollowUp(c);
      if (!(daysUntil(nf) < 0 && c.stage !== "Inactive" && c.stage !== "Completed")) return false;
    }
    if (candidateFilter === "ending") {
      // §2 (10-22): a placement ending within 4 weeks, or (as before) the profile's contract end
      if (!endingIds.has(String(c.id)) && !(c.contract_end && daysUntil(c.contract_end) <= CONTRACT_ALERT_DAYS && daysUntil(c.contract_end) > 0)) return false;
    }
    // Then apply search
    // §1 (NIGHT-DIRECTSTAFF-2026-10-19): name, email, phone in any format, state, specialty, stage and notes -- every word
    return candidateMatches(c, search);
  });
  const hotCount = candidates.filter(c => c.priority === "🔥 Hot").length;
  const activeCount = candidates.filter(c => c.stage === "Active Contract").length;
  const overdueFollowUps = candidates.filter(c => {
    const nf = calcNextFollowUp(c);
    return daysUntil(nf) < 0 && c.stage !== "Inactive" && c.stage !== "Completed";
  });
  const contractAlerts = candidates.filter(c => endingIds.has(String(c.id)) || (c.contract_end && daysUntil(c.contract_end) <= CONTRACT_ALERT_DAYS && daysUntil(c.contract_end) > 0));
  const needsAction = overdueFollowUps.length + contractAlerts.length;

  // Leads/Conversations computed: a lead "needs attention" when a HUMAN must pick
  // it up and no candidate matches it by email or phone. Escalation flags come
  // from agent_notifications.
  //
  // The isNeedsHuman() half is not decoration. A review_submission or a
  // calculator_capture can never match a candidate, so without it every such row is
  // permanently unmatched: it inflates this amber badge forever and teaches
  // recruiters to ignore the number.
  //
  // It is isNeedsHuman() and NOT isCandidateLead(): the outreach whitelist is
  // narrower than the work queue on purpose. meta_lead_dark is a paid Meta lead
  // the agent must never text and a person must therefore call — see
  // ./lib/leadClassification.
  // A lead marked handled (leads.processed = true) needs nobody -- the same rule the
  // morning brief applies -- so it leaves the badge and the default view together.
  const unmatchedLeads = leads.filter(l => needsAttention(l, !!findCandidateForLead(l, candidates, allLeads), isNeedsHuman));
  const contactProvidedConvIds = new Set(
    notifications.filter(n => n.reason === 'contact_provided' && n.conversation_id).map(n => n.conversation_id)
  );

  const leadsById = Object.fromEntries(leads.map((l) => [l.id, l]));
  const candidatesById = Object.fromEntries(candidates.map((c) => [c.id, c]));   // onboarding nudges (hub 2026-10-08)
  const draftHandlers = makeDraftHandlers({
    client: supabase, staffReply: staff.reply, setDrafts, setBusy: setDraftBusy, toast: showToast,
    sendEmail: (p) => emailApi.send(p), leadState: (id) => (leadsById[id] || {}).state,
    // Set state (OVERNIGHT-AGENTS-2026-10-03 §3): the written state lands in `leads` at once, so the draft panel's
    // window and send button follow it without a reload.
    onLeadUpdated: (id, patch) => setLeads((rows) => rows.map((r) => (r && r.id === id ? { ...r, ...patch } : r))),
    loadThread: (conversationId) => db.getConversationMessages(conversationId),   // A4: state from the reply
  });
  const openDraft = (id) => { setDraftFocus(id); setView("drafts"); };
  const draftsAwaiting = awaitingCount(drafts);
  // System alerts (hub 2026-10-09): notifications with no conversation; the badge counts the open ones of priority
  // normal or above (OVERNIGHT-AGENTS-2026-10-10 §2). The bulk low-priority button calls this same write per row,
  // quietly: the panel shows its one result line instead of a toast per row.
  const systemAlertsOpen = openSystemAlertCount(notifications);
  const markSystemAlert = async (id, { bulk = false, note = null } = {}) => {
    const at = new Date().toISOString();
    const r = await markSystemAlertHandled(supabase, id, at, note, alertNoteColumn);
    setNotifications((prev) => applyHandled(prev, id, at, note, alertNoteColumn));
    if (!bulk) showToast(r === "handled" ? "Alert marked handled" : "Already handled");
    if (r !== "handled" && !bulk) loadAgentTables();
    return r;
  };

  // CRM-ONBOARDING-TAB-2026-10-13: the Onboarding badge = the tab's "Needs me" count (a test candidate never counts)
  const onbNeedsMe = onbOverview && onbOverview.allowed ? filterCounts(shapeOverview(onbOverview.rows, candidates)).needs : 0;
  // §4 (10-21): every candidate's exam links and attempts, once, for Today's "exam reminders due" (re-read with the live refresh)
  const [examAll, setExamAll] = useState(null);
  // NIGHT-DIRECTSTAFF-2026-10-24 §4: the packet's sections (onboarding_progress(), keys only; null before the hub applies it) and
  // which health uploads each candidate's setting requires (null when not readable)
  const [onbProgress, setOnbProgress] = useState(null);
  const [healthReqRows, setHealthReqRows] = useState(null);
  useEffect(() => {
    let live = true;
    supabase.rpc("onboarding_progress").then(({ data, error }) => { if (live) setOnbProgress(error ? null : (Array.isArray(data) ? data : null)); }, () => { if (live) setOnbProgress(null); });
    loadBoardHealth().then((r) => { if (live) setHealthReqRows(r); }, () => { if (live) setHealthReqRows(null); });
    return () => { live = false; };
  }, [liveTick]);
  useEffect(() => { let live = true; examsApi.load().then((d) => { if (live) setExamAll(d); }).catch(() => { if (live) setExamAll(null); }); return () => { live = false; }; }, [liveTick]);

  // NIGHT-DIRECTSTAFF-2026-10-16 §1: leads who replied with no human follow-up (lib/needsHuman.js), from what is loaded
  // NEEDS-HUMAN-FIX-2026-10-16: + each row's last-reply text (one small read for the listed conversations) and the voicemail
  // notifications, so a row says who, how they came in, what they asked for and what they last said without a tap
  const [lastInbound, setLastInbound] = useState(() => new Map());
  // §4: a test lead's conversations (and their messages) leave the lists and counts with it, unless Show tests is on
  const shownConversations = useMemo(() => (showTests ? conversations : conversations.filter((c) => { const l = leadForConversation(c, allLeads); return !(l && isTestMarked(l)); })),
    [conversations, allLeads, showTests]);
  const shownRefs = useMemo(() => { if (shownConversations === conversations) return convMsgRefs; const ok = new Set(shownConversations.map((c) => c.id));
    return convMsgRefs.filter((m) => ok.has(m.conversation_id)); }, [shownConversations, conversations, convMsgRefs]);
  // NIGHT-DIRECTSTAFF-2026-10-25c §1: Snooze on Needs a human -- the shared settings row (every staff account), and the alerts'
  // snoozed_until for the agents' SLA reminder (skipped, said nowhere, while the hub has not applied 2026-10-26-needs-snooze.sql)
  const [snoozeRaw, setSnoozeRaw] = useState(null);
  const [lastSnooze, setLastSnooze] = useState(null);       // { kind, row, until, name } -- Undo, this session
  useEffect(() => { db.getSetting(SNOOZE_KEY).then((v) => setSnoozeRaw(v || null)).catch(() => {}); }, [liveTick]);   // eslint-disable-line
  const snoozes = useMemo(() => activeSnoozes(snoozeRaw), [snoozeRaw, liveTick]);   // eslint-disable-line
  const needsHuman = useMemo(() => needsHumanRows({ conversations: shownConversations, refs: shownRefs, leads, notifications, lastInbound, now: Date.now() }),
    [shownConversations, shownRefs, leads, notifications, lastInbound]);
  const needsHumanConvKey = needsHuman.map((r) => r.conversationId).sort().join(',');
  useEffect(() => {
    let live = true;
    const ids = needsHumanConvKey ? needsHumanConvKey.split(',') : [];
    if (!ids.length) return undefined;
    db.getLastInbound(ids).then((m) => { if (live) setLastInbound(m); }).catch((e) => console.error('Last replies load error:', e));
    return () => { live = false; };
  }, [needsHumanConvKey, convMsgRefs.length]);
  const humanFollowedUp = (conversationId) => setConvMsgRefs((prev) => [...prev, { conversation_id: conversationId, role: 'human', created_at: new Date().toISOString() }]);
  // NIGHT-DIRECTSTAFF-2026-10-21 §1/§2: the agents' facility / call-only alerts as rows at the top of Needs a human
  const needsAlertRows = useMemo(() => alertRows({ notifications, leads: allLeads, now: Date.now() }), [notifications, allLeads]);
  // 10-25c §1: what Needs a human shows (and its badge counts) -- the not-snoozed rows
  const needsShown = useMemo(() => needsHuman.filter((r) => !snoozes.has(`nh:${r.key}`)), [needsHuman, snoozes]);
  const alertsShown = useMemo(() => needsAlertRows.filter((r) => !snoozes.has(`ar:${r.key}`)), [needsAlertRows, snoozes]);
  const snoozeAlertIds = (kind, row) => (kind === "ar" ? (row.alertIds || []) : notifications.filter((n) => n.conversation_id && n.conversation_id === row.conversationId
    && String(n.status || "open") === "open" && !n.handled_at).map((n) => n.id));
  const setSnooze = async (kind, row, until) => {
    let by = ""; try { const { data } = await supabase.auth.getUser(); by = (data && data.user && data.user.email) || ""; } catch { by = ""; }
    const fresh = await db.getSetting(SNOOZE_KEY).catch(() => snoozeRaw);
    const next = JSON.stringify(withSnooze(fresh, `${kind}:${row.key}`, until, by));
    await db.saveSettingChecked(SNOOZE_KEY, next);
    setSnoozeRaw(next);
    const ids = snoozeAlertIds(kind, row);
    if (ids.length) {   // the agents' SLA reminder reads these; a database without the columns yet (42703) is skipped quietly
      const { error } = await supabase.from("agent_notifications").update({ snoozed_until: until, snoozed_by: until ? by : null }).in("id", ids);
      if (error && !/42703|snoozed_/.test(`${error.code} ${error.message}`)) throw new Error(`Snoozed here, not for the agents' reminder: ${error.message}`);
    }
  };
  // EMAIL-CRM-2026-10-22: No email = the lead's email_opt_out, or the agent's email_suppressed / email_unsubscribed decision
  const emailDecisions = useMemo(() => decisionsByLead(leadDecisions), [leadDecisions]);
  const emailOptedOut = (lead) => !!lead && (leadOptedOut(lead, emailDecisions) || leadsWithAddress(lead.email, allLeads).some((l) => leadOptedOut(l, emailDecisions)));
  const openEmail = (lead, opts = {}) => {
    if (!lead) return;
    const to = String(lead.email || (lead.raw_payload && lead.raw_payload.email) || "").trim();
    setEmailTo({ to, who: lead.name || "", leadId: lead.id || null, subject: opts.subject || "", body: opts.body || "", optedOut: emailOptedOut({ ...lead, email: to }),
      // NIGHT-DIRECTSTAFF-2026-10-25 §3: the approved templates the Email box can fill for this lead
      templateCtx: { name: lead.name || "", discipline: lead.discipline || (lead.raw_payload && lead.raw_payload.discipline) || "", state: lead.state || "", lead } });
  };
  // §1 (NIGHT-DIRECTSTAFF-2026-10-22): every placement, read once the table exists and again with the live refresh
  const loadPlacements = useCallback(async () => {
    if (!placementsReady) return;
    try { setPlacements(await placementsApi.list()); } catch (e) { console.error("Placements load error:", e); }
  }, [placementsReady]);
  useEffect(() => { loadPlacements(); }, [loadPlacements, liveTick]);
  // §8/§9 (10-22): every credential row, read with the live refresh (the staff allowlist; null when not readable)
  const loadCredentials = useCallback(async () => { try { setCredDocs(await credentialsApi.list(credExtra)); } catch (e) { setCredDocs(null); } }, [credExtra]);
  useEffect(() => { loadCredentials(); }, [loadCredentials, liveTick]);
  // §6 (10-22): online/offline. Back online -> every queued send goes once (taken off first), the outcome said, lists reloaded.
  useEffect(() => {
    const changed = () => { const ls = lsOrNull(); setOutbox(ls ? readOutbox(ls) : []); };
    const goOffline = () => setOffline(true);
    const goOnline = async () => {
      setOffline(false);
      const ls = lsOrNull();
      if (ls && readOutbox(ls).length) {
        setOutboxSending(true);
        const res = await flushOutbox(ls, (it) => (it.kind === "text" ? staff.textLead(it.payload.leadId, it.payload.body, it.payload.sendAnyway) : emailApi.send(it.payload)));
        setOutboxSending(false); changed();
        const bad = res.filter((x) => !x.ok);
        const held = readOutbox(ls).length;   // queued over 30 minutes ago: "Send this now?" (agents window, 10-22)
        if (res.length || held) showToast([res.length && (bad.length ? `Back online: ${res.length - bad.length} sent, ${bad.length} not sent (${bad[0].error})` : `Back online: ${res.length} sent`),
          held && `${held} waiting: older than 30 minutes, so Send now or Cancel each`].filter(Boolean).join('. '));
      }
      setLiveTick((n) => n + 1);
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    if (outboxEvents) outboxEvents.addEventListener("change", changed);
    if (!isOffline() && lsOrNull() && readOutbox(lsOrNull()).length) goOnline();   // opened online with sends still queued
    return () => { window.removeEventListener("offline", goOffline); window.removeEventListener("online", goOnline); if (outboxEvents) outboxEvents.removeEventListener("change", changed); };
  }, []);   // eslint-disable-line
  const cancelQueued = (id) => { const ls = lsOrNull(); if (ls && removeQueued(ls, id)) { setOutbox(readOutbox(ls)); showToast("Cancelled: not sent"); } };
  const sendQueuedNow = async (id) => {
    const ls = lsOrNull(); if (!ls) return;
    setOutboxSending(true);
    const r = await sendQueued(ls, id, (it) => (it.kind === "text" ? staff.textLead(it.payload.leadId, it.payload.body, it.payload.sendAnyway) : emailApi.send(it.payload)));
    setOutboxSending(false); setOutbox(readOutbox(ls));
    if (r) showToast(r.ok ? "Sent" : `Not sent: ${r.error}`);
  };
  const waitingFor = (leadId) => outbox.filter((q) => q.kind === "text" && q.payload && q.payload.leadId === leadId);
  // §10 (10-22): what the Reports page reads beyond what the CRM holds -- messages (status, no body) and email rows for the period
  const loadReports = useCallback(async (period) => {
    const since = new Date(periodStart(period) - 864e5).toISOString();
    const [m, e] = await Promise.all([
      supabase.from("agent_messages").select("conversation_id,role,status,error_code,created_at").gte("created_at", since).limit(20000),
      supabase.from("activity_log").select("type,note,created_at").eq("type", "email").gte("created_at", since).limit(20000),
    ]);
    if (m.error) throw new Error("The messages could not be read: " + m.error.message);
    return { leads: allLeads, conversations, messages: m.data || [], notifications: allNotifications, placements, emailRows: e.error ? [] : (e.data || []),
      decisions: leadDecisions, examAttempts: (examAll && examAll.attempts) || [], isFacility: isFacilityLead };
  }, [allLeads, conversations, allNotifications, placements, leadDecisions, examAll]);
  // NIGHT-DIRECTSTAFF-2026-10-25 §4: the Activity page's other rows -- reviews decided, offers, portal status changes (each [] when unreadable)
  const loadAuditMore = useCallback(async () => {
    const [r, o, e, a] = await Promise.all([
      supabase.from("reviews").select("id,approved_at,approved_by,rejected_at,rejected_by").or("approved_at.not.is.null,rejected_at.not.is.null").limit(5000),
      supabase.from("offers").select("id,candidate_id,status,status_at,status_by,created_at,created_by").limit(5000),
      supabase.from("facility_events").select("at,kind,ref_id,staff_user_id").eq("kind", "status_set").limit(5000),
      supabase.from("activity_log").select("id,candidate_id,type,note,date,created_at").in("type", ["call_log", "call_followup", "lead_assign", "bulk_action", "facility_jd", "availability", "facility_portal"])
        .order("created_at", { ascending: false }).limit(2000),
    ]);
    return { reviews: r.error ? [] : (r.data || []), offers: o.error ? [] : (o.data || []), facilityEvents: e.error ? [] : (e.data || []), recent: a.error ? [] : (a.data || []) };
  }, []);
  // DAY-DIRECTSTAFF-2026-10-24 §3: the Numbers page's inputs -- the last 14 days of messages, offers, approved reviews; test candidates
  const loadNumbers = useCallback(async () => {
    const since = new Date(Date.now() - 15 * 864e5).toISOString();
    const [m, o, r, fac, pres] = await Promise.all([
      supabase.from("agent_messages").select("conversation_id,role,status,error_code,created_at").gte("created_at", since).limit(20000),
      supabase.from("offers").select("id,candidate_id,status,status_at,created_at").order("created_at", { ascending: false }).limit(5000),
      supabase.from("reviews").select("approved_at,rejected_at").gte("approved_at", since).limit(5000),
      facilityPortalApi.numbers(),                                   // DAY-DIRECTSTAFF-2026-10-25 §3: null before portal v1.6
      // DAY-DIRECTSTAFF-2026-11-05 §3: "presented" for the funnel -- the CRM's own lines (Present, Attach candidate summary)
      supabase.from("activity_log").select("candidate_id,type,note,date,created_at").in("type", [PRESENT_LOG_TYPE, "facility_summary"]).gte("created_at", since).limit(5000),
    ]);
    if (m.error) throw new Error("The messages could not be read: " + m.error.message);
    const testIds = new Set([...testCands, ...candidates.filter((c) => Array.isArray(c.tags) && c.tags.some((x) => String(x).toLowerCase() === "test"))].map((c) => String(c.id)));
    return { leads: allLeads, conversations, messages: m.data || [], notifications: allNotifications, offers: o.error ? [] : (o.data || []), placements,
      reviews: r.error ? [] : (r.data || []), testCandidateIds: testIds, isFacility: isFacilityLead, facility: fac,
      profile: profileData ? profileNumbers({ sessions: profileData.sessions, status: profileData.status, testIds })
        .concat(briefProfileCounts({ decisions: leadDecisions, status: profileData.status })) : null,
      funnel: funnelDays({ leads: allLeads, candidates: [...candidates, ...testCands], sessions: profileData ? profileData.sessions : [], status: profileData ? profileData.status : [],
        activity: pres && !pres.error ? (pres.data || []) : [], isFacility: isFacilityLead }) };   // 11-05 §3   // DAY-DIRECTSTAFF-2026-10-30b §3; 11-04 §4: + the brief's own count
  }, [allLeads, conversations, allNotifications, placements, testCands, candidates, profileData, leadDecisions]);   // eslint-disable-line
  // §11 (10-22): "Wrong?" on what they've told us -- a staff note on their timeline; the memory itself is never edited
  const saveMemoryNote = async (candidateId, text) => {
    let by = ""; try { const { data } = await supabase.auth.getUser(); by = (data && data.user && data.user.email) || ""; } catch { by = ""; }
    const { error } = await supabase.from("activity_log").insert({ candidate_id: candidateId, date: new Date().toISOString().slice(0, 10), type: MEMORY_NOTE_TYPE,
      note: `What they told us, flagged: ${text}${by ? ` · by ${by}` : ""}` });
    if (error) throw new Error("Not saved: " + error.message);
    setLiveTick((n) => n + 1);
  };
  // §14 (10-22): the desktop keyboard -- j/k rows, Enter opens, r compose, Esc closes, / search, ? the list. Never on a phone or in a field.
  const [kbdHelp, setKbdHelp] = useState(false);
  const kbdIdx = useRef(-1);
  const kbdG = useRef(0);                 // 10-25b §3: when "g" was pressed (the first key of g t / g n / g r)
  useEffect(() => {
    const onKey = (e) => {
      const act = actionFor(e, { desktop: isDesktop(), active: document.activeElement, gAt: kbdG.current });
      if (act !== "chord") kbdG.current = 0;
      if (!act) return;
      if (act === "chord") { kbdG.current = Date.now(); return; }
      if (act.startsWith("go:")) { e.preventDefault(); setView(act.slice(3)); kbdIdx.current = -1; return; }
      const rows = [...document.querySelectorAll(".crm-content [data-kbd-row]")].filter((el) => el.offsetParent !== null);
      if (act === "next" || act === "prev") {
        e.preventDefault();
        kbdIdx.current = moveIndex(Math.min(kbdIdx.current, rows.length - 1), act, rows.length);
        rows.forEach((r) => r.removeAttribute("data-kbd-on"));
        const r = rows[kbdIdx.current]; if (r) { r.setAttribute("data-kbd-on", "true"); r.scrollIntoView({ block: "nearest" }); }
      } else if (act === "open") {
        const r = rows.find((x) => x.getAttribute("data-kbd-on") === "true");
        if (r) { e.preventDefault(); (r.querySelector("[data-kbd-open], a[href^='?lead=']") || r).click(); }
      } else if (act === "reply") {
        const box = document.querySelector("[data-testid='text-body'], [data-testid='email-body'], [data-testid='send-link-body']");
        if (box) { e.preventDefault(); box.focus(); }
      } else if (act === "search") {
        const s = document.querySelector("[data-testid='top-search']");
        if (s) { e.preventDefault(); s.focus(); }
      } else if (act === "help") { e.preventDefault(); setKbdHelp((h) => !h); }
      else if (act === "close") {
        if (isTyping(document.activeElement)) { document.activeElement.blur(); return; }
        const c = document.querySelector("[data-testid='kbd-help-close'], [data-testid='text-close'], [data-testid='email-close'], [data-testid='today-remind-done'], [data-testid='today-expiring-done'], [data-testid='onb-board-remind-done']");
        if (c) { e.preventDefault(); c.click(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const openEmailCandidate = (c) => {
    if (!c) return;
    const last = placements.filter((p) => String(p.candidate_id) === String(c.id)).sort((a, b) => String(b.start_date || "").localeCompare(String(a.start_date || "")))[0];
    setEmailTo({ to: String(c.email || "").trim(), who: c.name || "", leadId: null, candidateId: c.id, subject: "", body: "", optedOut: emailOptedOut({ email: c.email }),
      templateCtx: { name: c.name || "", discipline: c.specialty || "", state: (last && last.state) || c.state || "" } });     // 10-25 §3
  };
  const markAddressNoEmail = (address) => setLeads((prev) => prev.map((l) => (leadsWithAddress(address, [l]).length ? { ...l, email_opt_out: true } : l)));
  const setNoEmail = async (lead, value) => {
    const ids = [...new Set([lead.id, ...leadsWithAddress(lead.email, allLeads).map((l) => l.id)])];
    const ok = await emailApi.setOptOut(ids, value);
    if (!ok) { showToast("Not saved: the No email setting isn't switched on yet"); return; }
    setLeads((prev) => prev.map((l) => (ids.includes(l.id) ? { ...l, email_opt_out: value } : l)));
    setLiveTick((n) => n + 1);
    showToast(value ? "Marked No email" : "Email allowed again");
  };
  // NIGHT-DIRECTSTAFF-2026-10-21 §4: Today -- facility requests, call requests, hand-offs over 24 h, exam reminders due, onboarding stuck
  // DAY-DIRECTSTAFF-2026-10-24 §2: cards with interview times waiting (null before facility portal v1.4 is applied: nothing shows)
  const [interviewsWaiting, setInterviewsWaiting] = useState(null);
  useEffect(() => { facilityPortalApi.interviewsWaiting().then(setInterviewsWaiting).catch(() => setInterviewsWaiting(null)); }, [liveTick]);   // eslint-disable-line
  // DAY-DIRECTSTAFF-2026-10-23b: the facilities' rates for Today's facility rows (null before facility portal v1.2 is applied)
  const [facilityRates, setFacilityRates] = useState(null);
  useEffect(() => { facilityPortalApi.rates().then(setFacilityRates).catch(() => setFacilityRates(null)); }, [liveTick]);   // eslint-disable-line
  // DAY-DIRECTSTAFF-2026-10-25 §4 (portal v1.5): pending contacts > 24 h and job descriptions not yet read (null before v1.5)
  // NIGHT-DIRECTSTAFF-2026-10-25 §2: the last 90 days of logged calls (Today's call-backs; "not yet called back" on voicemail leads)
  const [callRows, setCallRows] = useState([]);
  const [callTick, setCallTick] = useState(0);
  const bumpCalls = useCallback(() => setCallTick((n) => n + 1), []);
  // NIGHT-DIRECTSTAFF-2026-10-25b §1: who each lead is assigned to (the newest 'lead_assign' row)
  const [assignRows, setAssignRows] = useState([]);
  const [assignTick, setAssignTick] = useState(0);
  const bumpAssign = useCallback(() => setAssignTick((n) => n + 1), []);
  useEffect(() => { assignApi.recent().then(setAssignRows).catch(() => setAssignRows([])); }, [liveTick, assignTick]);   // eslint-disable-line
  useEffect(() => { callsApi.recent().then(setCallRows).catch(() => setCallRows([])); }, [liveTick, callTick]);   // eslint-disable-line
  const [portalToday, setPortalToday] = useState(null);
  useEffect(() => {   // DAY-DIRECTSTAFF-2026-10-29 §4: Today's "This week" -- interviews booked from now to 7 days out; Duplicates pending
    const now = new Date(); const wk = new Date(Date.now() + 7 * 864e5);
    supabase.from("facility_presented").select("id", { count: "exact", head: true }).gte("interview_booked_at", now.toISOString()).lte("interview_booked_at", wk.toISOString())
      .then(({ count, error }) => setIvBooked(error ? null : count || 0)).catch(() => setIvBooked(null));
    notSameApi.load().then(setNotSameSet).catch(() => setNotSameSet(null));
  }, [liveTick]);   // eslint-disable-line
  useEffect(() => { facilityPortalApi.todayV15().then(setPortalToday).catch(() => setPortalToday(null)); }, [liveTick, portalContact]);   // eslint-disable-line
  // DAY-DIRECTSTAFF-2026-11-01 §2: portal requests still Received (null = unreadable: Today lists the alerts alone)
  const [portalWaiting, setPortalWaiting] = useState(null);
  // DAY-DIRECTSTAFF-2026-11-10 §1: the offers still Offered (null before the offers table / unreadable: no section)
  const [openOffers, setOpenOffers] = useState(null);
  useEffect(() => { offersApi.openOffers().then(setOpenOffers).catch(() => setOpenOffers(null)); }, [liveTick]);   // eslint-disable-line
  useEffect(() => { facilityPortalApi.waitingRequests().then(setPortalWaiting).catch(() => setPortalWaiting(null)); }, [liveTick, portalContact]);   // eslint-disable-line
  // DAY-DIRECTSTAFF-2026-10-28 §3: Today leaves out what is snoozed on Needs a human (its alerts and its hand-offs), like the badge there
  const today = useMemo(() => buildToday({ alertRows: withRates(alertsShown, facilityRates), snoozed: snoozes, notifications, refs: shownRefs, conversations: shownConversations, leads: allLeads,
    candidates, examData: examAll, onboardingRows: onbOverview && onbOverview.allowed ? shapeOverview(onbOverview.rows, candidates) : [], ending: endingItems, expiring: expiringItems,
    // NAV-2026-10-22: the Reminders tab, folded in (a candidate whose placement is already under "ending soon" is not listed twice)
    followUps: overdueFollowUps.map((c) => ({ candidate: c, daysOverdue: Math.abs(daysUntil(calcNextFollowUp(c))), lastContact: c.last_contact || null })),
    contracts: contractAlerts.filter((c) => c.contract_end && !(endingItems || []).some((x) => String(x.candidate.id) === String(c.id))).map((c) => ({ candidate: c, endsOn: c.contract_end })),
    starts: startChecks({ placements, candidates }), startsSoon: startsThisWeek({ placements, candidates }),   // 10-28 §5
    interviews: interviewsWaiting || [], portalWaiting,   // 11-01 §2
    voice: voiceRows({ notifications: allNotifications, leads: allLeads }).filter((r) => !(r.leadId && calledSince(callRows, r.leadId, r.at))),   // 10-25 §2: a call logged since = called back
    pending: (portalToday && portalToday.pending) || [], jd: (portalToday && portalToday.jd) || [],
    callbacks: callBacksDue({ activity: callRows, who: (c) => {
      if (c.leadId) { const l = allLeads.find((x) => String(x.id) === String(c.leadId)); if (!l) return null; if (l.test_marked_at || l.merged_into || l.spam_marked_at) return false;
        return { label: l.name || `lead ${String(l.id).slice(0, 8)}`, phone: l.phone || (l.raw_payload && l.raw_payload.phone) || '' }; }
      if (c.candidateId) { const k = candidates.find((x) => String(x.id) === String(c.candidateId)); if (!k) return null;
        if (k.merged_into || k.test_marked_at || (Array.isArray(k.tags) && k.tags.some((t) => String(t).toLowerCase() === "test"))) return false;
        return { label: `#${k.id} ${k.name || ''}`.trim(), phone: k.phone || '' }; }
      return { label: 'the facility contact', phone: '' }; } }),
    profiles: profileData ? notStarted({ sessions: profileData.sessions, status: profileData.status, candidates }) : [],   // DAY-DIRECTSTAFF-2026-10-30b §3
    ready: profileData ? readyToPresent({ rows: profileData.ready, candidates, waiting: portalWaiting || [] }) : [],   // DAY-DIRECTSTAFF-2026-11-08 §1
    applicants: applicantsWithoutCandidate({ leads: allLeads, candidates: [...candidates, ...testCands, ...mergedCands] }),   // DAY-DIRECTSTAFF-2026-11-09 §1
    // DAY-DIRECTSTAFF-2026-11-10 §1: offers waiting on an answer; candidates only a call reaches
    offers: offersWaiting({ offers: openOffers || [], candidates }),
    callOnly: callOnlyCandidates({ candidates, consentOf: (c) => textConsentOf(c, allLeads), emailOff: (c) => !String(c.email || '').trim() || emailOptedOut({ email: c.email }), calls: callRows }),
    now: Date.now() }),
    [alertsShown, snoozes, facilityRates, notifications, allNotifications, shownRefs, shownConversations, allLeads, candidates, examAll, onbOverview, endingItems, expiringItems, placements, interviewsWaiting, portalToday, callRows, profileData, portalWaiting, testCands, mergedCands, openOffers]);   // eslint-disable-line
  // NIGHT-DIRECTSTAFF-2026-10-23 §5: "Done for today" -- per staff account (public.settings "crm.todayDone.v1.<user id>", the table saved
  // views use; no migration), until midnight Eastern; the badge and the lists leave those rows out. Undo for the last one, this session.
  const [todayDone, setTodayDone] = useState(() => new Set());
  const [lastDone, setLastDone] = useState(null);
  const todayDoneKey = useRef(null);
  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      const uid = data && data.user && data.user.id;
      if (!uid) return;
      todayDoneKey.current = `crm.todayDone.v1.${uid}`;
      setTodayDone(doneForToday(await db.getSetting(todayDoneKey.current)));
    }).catch(() => {});
  }, []);
  const saveDone = (set) => {
    setTodayDone(set);
    if (todayDoneKey.current) db.saveSettingChecked(todayDoneKey.current, JSON.stringify({ day: etDay(), keys: [...set] })).catch((e) => showToast("Not saved: " + e.message));
  };
  const markDone = (k) => { const n = new Set(todayDone); n.add(k); saveDone(n); setLastDone(k); };
  const undoDone = () => { if (!lastDone) return; const n = new Set(todayDone); n.delete(lastDone); saveDone(n); setLastDone(null); };
  const todayShown = useMemo(() => withoutDone(today, todayDone), [today, todayDone]);
  // NIGHT-DIRECTSTAFF-2026-10-22 §6: the last-loaded Today / Needs a human / Leads / Facilities, saved for offline -- display
  // rows only (lib/offline.js snapshotOf: no phone numbers), replaced on every load, cleared on sign-out
  useEffect(() => {
    if (offline || (!leads.length && !needsHuman.length)) return;
    const ls = lsOrNull();
    if (ls) saveSnapshot(ls, snapshotOf({ today, needs: needsHuman, leads: leads.filter((l) => !isFacilityLead(l)), facilities: leads.filter(isFacilityLead).map(facilityFields), now: Date.now() }));
  }, [today, needsHuman, leads, offline]);
  // Call: the number shows once tapped (as everywhere in the CRM) and the phone dials it
  const callLead = (rowOrLead) => {
    const lead = rowOrLead && rowOrLead.lead !== undefined ? rowOrLead.lead : rowOrLead;
    // an alert row carries candidate_phone (call_requested, 10-21) for a lead the CRM hasn't loaded
    const phone = (lead && (lead.phone || (lead.raw_payload && lead.raw_payload.phone))) || (rowOrLead && rowOrLead.phone);
    if (!phone) return '';
    try { window.location.href = `tel:${String(phone).replace(/[^\d+]/g, '')}`; } catch { /* a desktop with no dialler */ }
    return String(phone);
  };
  // Mark handled after the call: the lead handled WITH the note (the agents count that as staff action), and its alert rows
  // handled with the same note, so System alerts doesn't keep them open
  const markAlertRowHandled = async (row, note) => {
    if (row.leadId) {
      const r = await markLeadHandled(supabase, row.leadId, note || null, { noteColumns: handledCols });
      setLeads((prev) => applyLeadHandled(prev, [row.leadId], r.patch));
    }
    for (const id of row.alertIds) await markSystemAlert(id, { bulk: true, note: note || null });
  };
  const saveFacility = async (leadId, patch) => {
    const p = await saveFacilityPipeline(supabase, leadId, patch);
    setLeads((rs) => rs.map((r) => (r.id === leadId ? { ...r, ...p } : r)));
    if (patch.status === 'called' || patch.status === 'quoting') await closeFacilityReminder(leadId, patch.status === 'quoting' ? 'Quote started in the CRM' : 'Called (status set in the CRM)');
  };
  // NIGHT-DIRECTSTAFF-2026-10-28 §3: the agents' facility reminder (an open facility_request alert on the lead) is closed by the
  // first touch -- a call logged, Called or Quoting set -- through the same close as "Mark handled" (markSystemAlert). -> how many
  const closeFacilityReminder = async (leadId, why) => {
    const row = needsAlertRows.find((r) => r.kind === 'facility' && String(r.leadId || '') === String(leadId));
    if (!row) return 0;
    let n = 0;
    for (const id of row.alertIds) { try { if ((await markSystemAlert(id, { bulk: true, note: why })) === 'handled') n += 1; } catch (e) { /* the call stands */ } }
    if (n) showToast(`The facility reminder is closed (${why.toLowerCase()})`);
    return n;
  };
  const markNeedsHumanHandled = async (row) => {
    if (row.leadId) {
      const r = await markLeadHandled(supabase, row.leadId, null, { noteColumns: handledCols });
      setLeads((prev) => applyLeadHandled(prev, [row.leadId], r.patch));
    } else {
      await staff.handledOffline(row.conversationId);   // no lead record: the conversation's own "handled off-system"
      humanFollowedUp(row.conversationId);
    }
  };
  // Text = the same compose box as the Leads view's Text button (StaffText.jsx, CRM-TEXT-2026-10-16): templates, "Send
  // anyway" for quiet hours, one send per click; the Worker owns every rule. A sent text drops the row at once.
  const [needsText, setNeedsText] = useState(null);   // the row whose compose box is open
  // NIGHT-DIRECTSTAFF-2026-10-23 §1: reviews waiting for a decision (the chip, and the Needs a human badge); 0 before the migration
  const [reviewsWaiting, setReviewsWaiting] = useState(0);
  useEffect(() => { reviewsApi.load().then((d) => setReviewsWaiting(d.ready ? reviewsPending(d.rows) : 0)).catch(() => setReviewsWaiting(0)); }, []);
  const needsTextBlock = (row) => {
    if (row.optOut) return 'Opted out';
    if (!row.leadId) return 'No lead record';
    return textButtonState(leads.find((l) => l.id === row.leadId)).enabled ? '' : 'No textable number';
  };
  // CRM-LEADS-2026-10-16 §3: "Hand back to agent" -- clears human_takeover through /api/staff-release; the list follows locally
  const handBackToAgent = async (conversationId) => {
    await staff.release(conversationId);
    setConversations((prev) => prev.map((c) => (c.id === conversationId ? { ...c, human_takeover: false, status: 'active' } : c)));
  };
  const refreshNeedsHuman = async () => {
    await loadAgentTables();
    try { setLeads(await db.getLeads()); } catch (e) { console.error('Leads load error:', e); }
  };

  // CRM-LEADS-2026-10-16 §2: the lists refresh themselves (lib/liveRefresh.js) -- every 45 s while this tab is visible, and
  // at once when it comes back -- and what arrived since this device last opened a list shows as "+N" on its nav item
  // and in the tab title. No reload needed for a new Meta lead.
  const lastLiveRef = useRef(Date.now());
  const refreshLive = useCallback(async () => {
    lastLiveRef.current = Date.now();
    setLiveTick((n) => n + 1);   // NIGHT-DIRECTSTAFF-2026-10-19 §6: an open timeline re-reads too
    await loadAgentTables({ quiet: true });
    try { setLeads(await db.getLeads()); } catch (e) { console.error('Leads load error:', e); }
  }, [loadAgentTables]);
  useEffect(() => {
    const visible = () => typeof document !== 'undefined' && document.visibilityState === 'visible';
    const id = setInterval(() => { if (shouldRefresh({ visible: visible(), lastAt: lastLiveRef.current, minGap: LIVE_MS - 2000 })) refreshLive(); }, LIVE_MS);
    const onVis = () => { if (shouldRefresh({ visible: visible(), lastAt: lastLiveRef.current })) refreshLive(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [refreshLive]);
  const store = () => { try { return window.localStorage; } catch { return null; } };
  const [seen, setSeen] = useState(() => readSeen(store()));
  useEffect(() => {                                    // on a list, what lands there is seen
    if (!LIVE_VIEWS.includes(view)) return;
    setSeen((s) => { const n = { ...s, [view]: Date.now() }; writeSeen(store(), n); return n; });
  }, [view, leads, convMsgRefs]);
  const fresh = useMemo(() => freshCounts({ leads, refs: shownRefs, needsHuman, seen }), [leads, shownRefs, needsHuman, seen]);
  // NIGHT-DIRECTSTAFF-2026-10-17 §5: the installed app's icon badge = the Needs-a-human count (where the platform has one)
  // NIGHT-DIRECTSTAFF-2026-10-24 §7: the app icon's badge = Today's count (what is left after "Done for today"), the number on Today's nav item
  useEffect(() => { syncAppBadge(todayShown.total || 0); }, [todayShown.total]);
  useEffect(() => { document.title = titleWith(document.title, fresh.total); }, [fresh.total]);

  // ── NAV ──────────────────────────────────────────────────
  // NAV-2026-10-22: eight items (lib/nav.js); the views that moved sit under one of them as a filter row. Needs a human's badge
  // now carries the drafts to approve and the open system alerts too, since they live under it.
  const NAV_EXTRA = {
    today: { icon:"clock", badge: todayShown.total > 0 ? todayShown.total : null, badgeColor:"#b91c1c" },
    leads: { icon:"draft", badge: unmatchedLeads.length > 0 ? unmatchedLeads.length : null, badgeColor:"#f59e0b", fresh: fresh.leads },
    candidates: { icon:"users", badge: candidates.length },
    facilities: { icon:"pipeline" },
    needs: { icon:"msg", badge: needsShown.length + alertsShown.length + draftsAwaiting + systemAlertsOpen + reviewsWaiting > 0 ? needsShown.length + alertsShown.length + draftsAwaiting + systemAlertsOpen + reviewsWaiting : null,   // 10-25c §1: snoozed left out
      // "+N" (new since last opened) for Needs a human and Conversations together -- never on the item you are on
      badgeColor:"#dc2626", fresh: navOf(view) === "needs" ? 0 : (fresh.needs || 0) + (fresh.conversations || 0) },
    reports: { icon:"dashboard" },
    activity: { icon:"remind" },
    settings: { icon:"settings" },
  };
  const navItems = NAV.filter((n) => n.id !== "settings" || owner).map((n) => ({ ...n, ...NAV_EXTRA[n.id] }));   // 10-25d §2: Settings is owner-only
  const leadKindView = (k) => setView(k === "facilities" ? "facilities" : "leads");   // a saved view of the other kind opens under its nav item
  // the filter rows: Needs a human (and the three views under it); Candidates (List | Onboarding, and the List's filters)
  const needsRow = <FilterRow label="Needs a human" testid="needs-filter" items={NEEDS_FILTERS} current={view} onPick={(id) => setView(id)}
    counts={{ needs: needsHuman.length + needsAlertRows.length, drafts: draftsAwaiting, alerts: systemAlertsOpen, reviews: reviewsWaiting }} />;
  const candidatesRows = (
    <>
      <FilterRow label="Candidates" testid="cand-tab" items={CANDIDATE_TABS} current={view === "onboarding" ? "onboarding" : "candidates"}
        onPick={(id) => { setSelected(null); if (id === "candidates") setCandidateFilter("all"); setView(id); }} counts={{ onboarding: onbNeedsMe }} />
      {view !== "onboarding" && <FilterRow label="Filter candidates" testid="cand-filter" items={CANDIDATE_FILTERS}
        current={view === "expirations" || view === "duplicates" ? view : candidateFilter}
        onPick={(id) => { if (id === "expirations") setView("expirations"); else if (id === "duplicates") { setSelected(null); setView("duplicates"); } else { setCandidateFilter(id); setView("candidates"); } }}
        counts={{ hot: hotCount, active: activeCount, overdue: overdueFollowUps.length, ending: contractAlerts.length }} />}
    </>
  );

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"#0f172a" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:32, marginBottom:16 }}>⚡</div>
        <div style={{ color:"white", fontSize:18, fontWeight:700 }}>TherapyLink CRM</div>
        <div style={{ color:"#64748b", fontSize:13, marginTop:6 }}>Loading...</div>
      </div>
    </div>
  );

  return (
    <>
      <style>{STYLES}</style>
      <div className="crm-shell" style={{ display:"flex", height:"100vh", overflow:"hidden" }}>
        {/* SIDEBAR */}
        <div className="crm-side" style={{ width:220, background:"#0f172a", display:"flex", flexDirection:"column", flexShrink:0 }}>
          <div className="crm-side-brand" style={{ padding:"20px 16px 12px", borderBottom:"1px solid #ffffff14" }}>
            <div style={{ color:"white", fontSize:17, fontWeight:800, letterSpacing:"-0.3px" }}>⚡ TherapyLink</div>
            <div style={{ color:"#64748b", fontSize:11, marginTop:2 }}>CRM v5.1 · {candidates.length} candidates</div>
            {!dbOk && <div style={{ color:"#ef4444", fontSize:10, marginTop:4 }}>⚠ Database connection failed</div>}
          </div>
          <nav className="crm-nav" style={{ flex:1, padding:"10px 8px", overflowY:"auto" }}>
            {navItems.map(n => (
              <button key={n.id} className="nb" data-testid={`nav-${n.id}`} aria-current={navOf(view)===n.id ? "page" : undefined} onClick={() => { setView(n.id); setSelected(null); setCandidateFilter("all"); }} style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:9, marginBottom:2, background:navOf(view)===n.id?"#3b82f620":"transparent", color:navOf(view)===n.id?"#93c5fd":"#7ba3c8", fontSize:13, fontWeight:navOf(view)===n.id?600:400, textAlign:"left" }}>
                <I n={n.icon} s={16} c="currentColor"/>
                {n.label}
                {n.badge && <span style={{ marginLeft:"auto", background:n.badgeColor||"#334155", color:"white", fontSize:10, fontWeight:700, padding:"1px 7px", borderRadius:20 }}>{n.badge}</span>}
                {n.fresh > 0 && <span data-testid={`nav-fresh-${n.id}`} title={`${n.fresh} new since you last opened it`} aria-label={`${n.fresh} new`}
                  style={{ marginLeft: n.badge ? 4 : "auto", background:"#1d4ed8", color:"white", fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:20 }}>+{n.fresh}</span>}
              </button>
            ))}
          </nav>
          <div className="crm-side-foot" style={{ padding:"10px 8px", borderTop:"1px solid #ffffff14" }}>
            {gmailAuth.signedIn ? (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"6px 8px", background:"#0d3d2a", borderRadius:8, marginBottom:6 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:7, height:7, borderRadius:"50%", background:"#22c55e", flexShrink:0 }}/>
                  <span style={{ color:"#86efac", fontSize:10, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:120 }}>{gmailAuth.email}</span>
                </div>
                <button className="nb" onClick={signOut} style={{ color:"#4ade80", fontSize:10, fontWeight:600, padding:"1px 4px", opacity:.7 }}>×</button>
              </div>
            ) : (
              <button className="nb" onClick={signIn} style={{ width:"100%", background:"#1d4ed820", color:"#60a5fa", border:"1px solid #1d4ed840", borderRadius:8, padding:"7px 10px", fontSize:11, fontWeight:600, marginBottom:6, textAlign:"left" }}>
                📧 Connect Gmail
              </button>
            )}
            {[["🔥 Hot",hotCount,"#ef4444"],["✅ Active",activeCount,"#22c55e"],["⚡ Action",needsAction,"#f59e0b"]].map(([l,v,c])=>(
              <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"2px 6px" }}>
                <span style={{ color:"#6a8aa8", fontSize:11 }}>{l}</span>
                <span style={{ color:v>0?c:"#4a6a8a", fontWeight:700, fontSize:12 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="crm-main" style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
          {/* TOP BAR */}
          <div className="crm-topbar" style={{ background:"white", borderBottom:"1px solid #e2e8f0", height:56, display:"flex", alignItems:"center", padding:"0 24px", justifyContent:"space-between", flexShrink:0 }}>
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", color:"#94a3b8" }}><I n="search" s={15}/></span>
              {(() => { const onLeads = view === "leads"; const val = onLeads ? leadQuery : search; const set = onLeads ? setLeadQuery : setSearch;
                const groups = gsOpen ? searchEverywhere(val, { leads: allLeads, candidates, contacts: (gsData && gsData.contacts) || [], requests: (gsData && gsData.requests) || [], placements }) : [];
                const items = flatItems(groups);
                const openHit = (x) => {
                  setGsOpen(false); setGsActive(-1);
                  if (x.open.type === "lead") window.location.assign(leadHref(x.open.id));
                  else if (x.open.type === "candidate") { const c = candidates.find((k) => String(k.id) === String(x.open.id)); if (c) { setSelected(c); setView("candidates"); } }
                  else { const hit = allLeads.find((l) => String(l.email || "").trim().toLowerCase() === String(x.open.email || "").trim().toLowerCase());
                    if (hit) window.location.assign(leadHref(hit.id)); else setPortalContact({ email: x.open.email, name: x.open.name, userId: x.open.userId }); }
                };
                return (<>
                <input className="input crm-search" data-testid="top-search" type="text" enterKeyHint="search" autoComplete="off" aria-label={onLeads ? "Search leads, and everywhere" : "Search candidates, and everywhere"}
                  role="combobox" aria-expanded={gsOpen && String(val).trim().length >= 2} aria-controls={gsOpen && String(val).trim().length >= 2 ? "global-search" : undefined}
                  style={{ paddingLeft:34, paddingRight:val ? 40 : 12, width:280, fontSize:13 }}
                  placeholder={onLeads ? "Search leads: name, email, phone, state, message" : "Search candidates: name, email, phone, state, notes"}
                  value={val} onChange={e => { set(e.target.value); setGsOpen(true); setGsActive(-1); }}
                  onFocus={() => { setGsOpen(true); if (!gsData) facilityPortalApi.searchData().then(setGsData).catch(() => setGsData({ contacts: [], requests: [] })); }}
                  onBlur={() => setTimeout(() => setGsOpen(false), 150)}
                  onKeyDown={e => {
                    if (e.key === "Escape") { if (gsOpen && items.length) { setGsOpen(false); return; } set(""); return; }
                    if (e.key === "ArrowDown" && items.length) { e.preventDefault(); setGsOpen(true); setGsActive((i) => Math.min(items.length - 1, i + 1)); }
                    else if (e.key === "ArrowUp" && items.length) { e.preventDefault(); setGsActive((i) => Math.max(0, i - 1)); }
                    else if (e.key === "Enter" && gsActive >= 0 && items[gsActive]) { e.preventDefault(); openHit(items[gsActive]); }
                  }}/>
                {gsOpen && String(val).trim().length >= 2 && <GlobalSearch groups={groups} active={gsActive} onOpen={openHit} onHover={setGsActive} loading={!gsData} />}
                {val && <button className="nb" data-testid="top-search-clear" aria-label="Clear search" onClick={() => set("")}
                  style={{ position:"absolute", right:2, top:"50%", transform:"translateY(-50%)", minWidth:44, minHeight:44, background:"transparent", color:"#64748b", fontSize:18, lineHeight:1 }}>×</button>}
              </>); })()}
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              {overdueFollowUps.length > 0 && (
                <button className="nb" onClick={() => setView("today")} style={{ background:"#fef2f2", color:"#ef4444", border:"1px solid #fecaca", borderRadius:8, padding:"6px 12px", fontSize:12, fontWeight:600 }}>
                  ⏰ {overdueFollowUps.length} overdue
                </button>
              )}
              {contractAlerts.length > 0 && (
                <button className="nb" onClick={() => setView("today")} style={{ background:"#fffbeb", color:"#92400e", border:"1px solid #fde68a", borderRadius:8, padding:"6px 12px", fontSize:12, fontWeight:600 }}>
                  🏁 {contractAlerts.length} ending soon
                </button>
              )}
            </div>
          </div>

          {/* VIEW CONTENT */}
          <div className="crm-content" style={{ flex:1, overflowY:"auto", padding:24 }}>
            {/* NIGHT-DIRECTSTAFF-2026-10-22 §6: anything written offline, waiting to go */}
            {/* offline with the lists already on screen: they ARE the last-loaded lists -- kept as they are (an open Text box
                stays open), under the banner; the saved copy is only for an app opened with no connection */}
            {offline && (allLeads.length > 0 || conversations.length > 0) && <OfflineBanner snap={(lsOrNull() && readSnapshot(lsOrNull())) || { savedAt: null }} />}
            <OutboxBar queued={outbox} sending={outboxSending} online={!offline} onCancel={cancelQueued} onSendNow={sendQueuedNow}
              whoOf={(q) => (q.kind === "text" ? ((allLeads.find((l) => l.id === q.payload.leadId) || {}).name || "") : (q.payload.to || ""))} />
            {/* §5 (NIGHT-DIRECTSTAFF-2026-10-17): pull down on a list to reload it (the full-screen app has no browser pull) */}
            <PullToRefresh onRefresh={refreshLive} enabled={["needs", "leads", "conversations"].includes(view) || (view === "candidates" && !!selected)} scrollEl={() => document.querySelector(".crm-content")} />
            {offline && !selected && ["today", "needs", "leads"].includes(view) && allLeads.length === 0 && conversations.length === 0 ? (
              <OfflineLists snap={lsOrNull() ? readSnapshot(lsOrNull()) : null} initialTab={view} />
            ) : selected ? (
              <CandidateDetail
                key={`${selected.id}:${detailTab || ''}`}
                initialTab={detailTab}
                timelineSources={{ allLeads, conversations, notifications: allNotifications, tick: `${liveTick}-${callRows.length}`, placements: placements.filter((p) => p.candidate_id === selected.id) }}
                placementsKit={{ ready: placementsReady, rows: placements.filter((p) => p.candidate_id === selected.id), api: placementsApi, reload: loadPlacements }}
                offersKit={{ api: offersApi, onChanged: () => { loadPlacements(); setLiveTick((n) => n + 1); }, sendApi, textConsent: textConsentOf(selected, allLeads),
                  draft: offerDraft && offerDraft.candidateId === selected.id ? offerDraft : null, onDraftUsed: () => setOfferDraft(null),
                  onMade: (offer, draft) => requestOfferApi.linked(offer, draft), onAccepted: (offer) => requestOfferApi.accepted(offer) }}
                saveEmail={saveCandidateEmail}
                availKit={availCols ? { api: availabilityApi, sendApi, base: onboardingCfg.baseUrl, textConsent: textConsentOf(selected, allLeads) } : null}
                profileKit={profileData ? { api: profileApi, sendApi, base: onboardingCfg.baseUrl, textConsent: textConsentOf(selected, allLeads), by: staffEmail, onChanged: loadProfiles,
                  row: profileByCand.get(String(selected.id)) || null, link: profileLinks.get(String(selected.id)) || null } : null}
                callKit={{ api: callsApi, onLogged: () => setCallTick((n) => n + 1) }}
                onbKit={{ overview: onbOverview, progressRows: onbProgress, healthRows: healthReqRows, documents: credDocs || [], expiring: expiringItems, exams: examAll }}
                testKit={candTestCols && owner ? { mark: async (cand, marked) => {
                  let by = ""; try { const { data } = await supabase.auth.getUser(); by = (data && data.user && data.user.email) || ""; } catch { by = ""; }
                  const patch = await markCandidateTest(supabase, cand.id, marked, by);
                  takeCandidates([...candidates, ...testCands, ...mergedCands].map((x) => (x.id === cand.id ? { ...x, ...patch } : x)));
                  setSelected((s0) => (s0 && s0.id === cand.id ? { ...s0, ...patch } : s0));
                } } : null}
                dupKit={{ candidates, canMerge: candMergeCols && owner, onOpen: (c) => { setDetailTab(null); setSelected(c); },
                  merge: async (keepId, otherId) => {
                    await mergeCandidates(supabase, keepId, otherId);
                    const [cands, acts] = await Promise.all([db.getCandidates(), db.getAllActivity()]);
                    takeCandidates(cands); setAllActivity(acts || []);
                    const kept = (cands || []).find((c) => c.id === keepId); if (kept) { setDetailTab(null); setSelected(kept); }
                    loadPlacements(); loadCredentials(); setLiveTick((n) => n + 1);
                  } }}
                credentialsKit={{ rows: credDocs === null ? null : credDocs.filter((d) => d.candidate_id === selected.id), extra: credExtra, api: credentialsApi, reload: loadCredentials }}
                jobsKit={JOBS_KIT}
                memoryKit={{ conversations, saveNote: saveMemoryNote }}
                workKit={{ row: profileData ? profileByCand.get(String(selected.id)) || null : null, leads: allLeads, conversations, notifications: allNotifications,
                  // 11-07 §3: from what is loaded (no read): the timeline's ten queries ran on every candidate opened
                  loadEvents: async () => buildTimeline(contactInputs({ candidate: selected, leads: allLeads, conversations, refs: convMsgRefs, activity: allActivity })),
                  jobsLoad: JOBS_KIT.load, onText: (l) => setTodayText(l), profileApi: profileData ? profileApi : null,
                  openPresent: presentFor !== null && String(presentFor) === String(selected.id), onPresentShown: () => setPresentFor(null),
                  // 11-05 §2: Present to a facility -- the open portal requests and website facility requests; the summary when ready; one line (ids only)
                  present: { api: facilityPortalApi, by: staffEmail, makeSummary: summaryKit ? summaryKit.make : async () => { throw new Error('The profile system is not on'); },
                    loadTargets: async () => { const d = await facilityPortalApi.searchData();
                      return presentTargets({ requests: d.requests, users: d.contacts, profiles: d.contacts.map((x) => ({ user_id: x.id, facility_name: x.facility_name })),
                        leads: allLeads, facilityFields, isFacility: isFacilityLead }); },
                    log: async (note) => { const act = await db.addActivity(selected.id, PRESENT_LOG_TYPE, note); setAllActivity((prev) => [act, ...prev]); } } }}
                onEmail={openEmailCandidate} emailOff={emailOptedOut({ email: selected.email })}
                candidate={selected}
                activity={allActivity.filter(a => a.candidate_id === selected.id)}
                emailSync={allEmailSync.filter(a => a.candidate_id === selected.id)}
                back={() => setSelected(null)}
                updateCandidate={updateCandidate}
                addActivity={addActivity}
                jobs={jobs}
                toast={showToast}
                allCandidates={candidates}
                gmailAuth={gmailAuth}
                setSendingEmail={setSendingEmail}
                templates={templates}
                deleteCandidate={deleteCandidate}
              />
            ) : view === "today" ? (
              <><InstallBanner />
              <Today today={todayShown} onDone={markDone} lastDone={lastDone} onUndoDone={undoDone} onCall={callLead} toast={showToast} sendApi={sendApi} consentOf={(c) => textConsentOf(c, allLeads)}
                week={weekStrip({ today: todayShown, interviewsBooked: ivBooked, reviewsWaiting, duplicates: notSameSet ? duplicateList(candidates, notSameSet).length : null,
                  // 11-03 §2: the YES hand-offs (lib/handoffGroups.js) and the profiles submission-ready (test candidates left out)
                  replied: handoffGroups({ alerts: alertsShown, needs: needsShown, decisions: leadDecisions, refs: convMsgRefs, conversations, leads: allLeads }).counts.replied,
                  profilesReady: profileData ? (() => { const tid = new Set([...testCands, ...candidates.filter((c) => Array.isArray(c.tags) && c.tags.some((x) => String(x).toLowerCase() === "test"))].map((c) => String(c.id)));
                    return (profileData.status || []).filter((r) => r.submission_ready && !tid.has(String(r.candidate_id))).length; })() : null })}
                onWeek={(id) => { if (id === "interviews") setView("facilities"); else if (id === "reviews") setView("reviews"); else if (id === "replied") setView("needs");
                  else if (id === "profiles") { const el = document.querySelector('[data-testid="today-profiles-watch"]'); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); } else if (id === "duplicates") { notSameApi.load().then(setNotSameSet).catch(() => {}); setView("duplicates"); }
                  else { const el = document.querySelector(`[data-testid="today-${id === "starts" ? (todayShown.starts && todayShown.starts.length ? "starts" : "startsSoon") : id}"]`); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); } }}
                profileWatchLine={profileData ? (() => { const w = profileWatch({ sessions: profileData.sessions, status: profileData.status, candidates: [...candidates, ...testCands, ...mergedCands] });
                  return { line: watchLine(w), alert: w.unseen || w.unknownMaker ? `${w.unseen} agent link${w.unseen === 1 ? '' : 's'} for a candidate the CRM does not have; ${w.unknownMaker} with an unknown maker. Tell the agents window.` : '' }; })() : null}
                profileResend={profileData ? { api: profileApi, make: async (x) => { try { return await makeProfileLink(profileApi, x.candidate.id, x.link, onboardingCfg.baseUrl); } catch (e) { throw new Error(profileError(e)); } finally { loadProfiles(); } },
                  message: profileReminder } : null}
                // DAY-DIRECTSTAFF-2026-11-08 §1/§3: "Ready to present" opens the candidate with Present open; the Profiles line's "Not opened"
                onPresent={(c) => { setPresentFor(c.id); setDetailTab(null); setSelected(c); setView("candidates"); }}
                // 11-09 §1: an applicant with no candidate -- the same Create candidate as on the lead (candidate_from_lead)
                onCreateCandidate={cflOn ? async (l) => { const before = new Set([...candidates, ...testCands].map((c) => String(c.id))); const id = await cflApi.create(l.id);
                  takeCandidates(await db.getCandidates()); return { id, existed: before.has(String(id)) }; } : null}
                onOpenLead={(l) => openLeadHere(l.id)}
                // 11-10 §1: an offer waiting -> the candidate's Offers (on the overview); a call-only candidate -> their page, where Log call is
                onOpenOffer={(c) => { setDetailTab(null); setSelected(c); setView("candidates"); setTimeout(() => { const el = document.querySelector('[data-testid="offers"]'); if (el) el.scrollIntoView({ block: 'start' }); }, 400); }}
                onLogCall={(c) => { setDetailTab(null); setSelected(c); setView("candidates"); }}
                notOpened={profileData ? { rows: notOpened({ sessions: profileData.sessions, status: profileData.status, candidates }),
                  resend: async (x) => { try { return await resendByEmail({ candidate: x.candidate, sendApi, api: profileApi, by: staffEmail,
                    make: () => makeProfileLink(profileApi, x.candidate.id, x.link, onboardingCfg.baseUrl) }); } catch (e) { throw new Error(e.code ? e.message : profileError(e)); } finally { loadProfiles(); } } } : null}
                dateText={new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                onOpenThread={(id) => { setConvDeepLinkId(id); setView("conversations"); }}
                onTextEnding={(x) => { if (x.conversationId) { setConvDeepLinkId(x.conversationId); setView("conversations"); } else if (x.lead) setTodayText(x.lead); }}
                endingBlock={(x) => (x.conversationId || (x.lead && textButtonState(x.lead).enabled) ? "" : "No textable number")}
                onOpenOnboarding={(r) => { const hit = candidates.find((c) => c.id === r.candidate_id); if (hit) { setDetailTab("onboarding"); setSelected(hit); setView("candidates"); } }}
                onOpenCandidate={(c) => { setSelected(c); setView("candidates"); }}
                findCandidate={(id) => resolveMerged(id, candidates, mergedCands)}
                onCalledBack={async (r) => { try { await markAlertRowHandled(r, "Called back"); showToast("Marked called back"); } catch (e) { showToast(e.message || "Not saved"); } }}
                onBookInterview={async (x, sl) => { try { await facilityPortalApi.bookInterview(x.id, sl.at); showToast(`Interview booked: ${slotText(sl)} (they see it on the card)`); }
                  catch (e) { showToast(e.message || "Not booked"); } finally { facilityPortalApi.interviewsWaiting().then(setInterviewsWaiting).catch(() => {}); } }}
                onOpenCard={async (x) => { const id = await facilityPortalApi.leadForAlert({ card: x.id }, allLeads).catch(() => null);
                  if (id) openLeadHere(id);
                  else if (x.contact && x.contact.email) setPortalContact({ email: x.contact.email, name: x.contact.display_name, userId: x.contact.id });   // 10-25: the portal's own record
                  else showToast("This portal contact has no website request in the CRM yet."); }}
                onCallBack={async (x, kind, at) => { try { const ref = refOfCall(x.call);
                    if (kind === 'done') { await callsApi.done(x.call, ref); showToast("Call back done"); } else { await callsApi.snooze(x.call, at, ref); showToast("Snoozed"); } }
                  catch (e) { showToast(e.message || "Not saved"); } finally { setCallTick((n) => n + 1); } }}
                onOpenContact={(c) => { const hit = allLeads.find((l) => String(l.email || "").trim().toLowerCase() === String(c.email || "").trim().toLowerCase());
                  if (hit) openLeadHere(hit.id); else setPortalContact(c); }}
                onStarted={async (x) => { try { await placementsApi.update(x.placement, { status: "started" }); showToast(`Started: ${x.candidate.name} at ${x.placement.facility || "the facility"}`); loadPlacements(); }
                  catch (e) { showToast(e.message || "Not saved"); } }}
                makeReminder={async (x) => { const r = await examsApi.send(x.candidate.id, x.exams, x.dueOn); return { link: todayExamLink(r.token, onboardingCfg.baseUrl), linkId: r.link_id }; }} /></>
            ) : view === "needs" ? (
              <>
              {needsRow}
              {/* DAY-DIRECTSTAFF-2026-10-29b §2: the hand-offs open 72 h+, closed together with one reason (owners; asks first; 50 at a time) */}
              {owner && <BulkClose72 alerts={alertsShown} needs={needsShown} toast={showToast}
                decisions={leadDecisions} refs={convMsgRefs} conversations={conversations} leads={allLeads}
                close={async (i, reason) => { if (i.kind === 'alert') { await markAlertRowHandled(i.row, `Closed: ${reason}`); return; }
                  if (i.row.leadId) { const r = await markLeadHandled(supabase, i.row.leadId, `Closed: ${reason}`, { noteColumns: handledCols }); setLeads((prev) => applyLeadHandled(prev, [i.row.leadId], r.patch)); }
                  else { await staff.handledOffline(i.row.conversationId); humanFollowedUp(i.row.conversationId); } }}
                log={async (n, reason) => { const act = await db.addActivity(null, 'bulk_action', bulkCloseNote(n, reason, staffEmail)); setAllActivity((prev) => [act, ...prev]); }} />}
              <NeedsHumanView topCount={alertsShown.length} top={<AlertRows rows={alertsShown} onCall={callLead} toast={showToast} onEmail={openEmail} emailOptedOut={emailOptedOut}
                  onSnooze={async (r, until) => { await setSnooze("ar", r, until); setLastSnooze({ kind: "ar", row: r, until, name: (r.lead && r.lead.name) || "The alert" }); }}
                  onText={(r) => setNeedsText({ leadId: r.leadId, conversationId: null, takeover: false })}
                  textBlock={(r) => (r.lead && textButtonState(r.lead).enabled ? '' : 'No textable number')} onMarkHandled={markAlertRowHandled}
                  onOpenPortal={(ref) => facilityPortalApi.leadForAlert(ref, allLeads)} />}
                rows={needsShown} topOverdue={overdueCount(alertsShown.map((r) => r.at))} loadedAt={agentLoadedAt} refreshing={agentRefreshing} onRefresh={refreshNeedsHuman}
                snoozedCount={(needsHuman.length - needsShown.length) + (needsAlertRows.length - alertsShown.length)} lastSnooze={lastSnooze}
                onSnooze={async (r, until) => { await setSnooze("nh", r, until); setLastSnooze({ kind: "nh", row: r, until, name: r.name || "This one" }); }}
                onUndoSnooze={async () => { if (!lastSnooze) return; try { await setSnooze(lastSnooze.kind, lastSnooze.row, null); setLastSnooze(null); showToast("Snooze undone"); } catch (e) { showToast(e.message || "Not undone"); } }}
                loadThread={(id) => db.getConversationMessages(id)} onHandBack={(row) => handBackToAgent(row.conversationId)}
                textReady={textReady} textBlock={needsTextBlock} onText={setNeedsText} onMarkHandled={markNeedsHumanHandled} toast={showToast}
                onEmail={(r) => openEmail(allLeads.find((l) => l.id === r.leadId) || { id: r.leadId, email: r.email, name: r.name })}
                emailOptedOut={(r) => emailOptedOut(allLeads.find((l) => l.id === r.leadId) || { email: r.email })} />
              {needsText && leads.some((l) => l.id === needsText.leadId) && (
                <TextCompose
                  lead={leads.find((l) => l.id === needsText.leadId)} waiting={waitingFor(needsText.leadId)} onCancelWaiting={cancelQueued}
                  loadThread={async () => db.getConversationMessages(needsText.conversationId)}
                  loadTemplates={async () => parseTemplates(await db.getSetting(SMS_TEMPLATES_KEY))}
                  saveTemplates={(list) => db.saveSettingChecked(SMS_TEMPLATES_KEY, serializeTemplates(list))}
                  sendText={textOrQueue}
                  onSent={(r) => { if (!(r && r.queued)) humanFollowedUp(needsText.conversationId); showToast(textToast(r)); }}
                  onClose={() => setNeedsText(null)}
                  conversationId={needsText.conversationId} takeover={!!needsText.takeover} onHandBack={handBackToAgent}
                />
              )}
              </>
            ) : view === "candidates" ? (
              <>
              {candidatesRows}
              {candTestCols && (testCands.length > 0 || showTests) && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                  <button className="nb" data-testid="cand-show-tests" aria-pressed={showTests} onClick={() => setShowTests(!showTests)}
                    style={{ background: showTests ? "#6d28d9" : "#f1f5f9", color: showTests ? "white" : "#475569", borderRadius: 8, padding: "0 14px", minHeight: 44, fontSize: 12, fontWeight: 700, border: 0 }}>
                    {showTests ? `Hide tests (${testCands.length})` : `Show tests (${testCands.length})`}</button>
                </div>)}
              <div role="group" aria-label="Show candidates as" data-testid="cand-mode" style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
                <span aria-hidden="true" style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>Show as</span>
                {[["list", "Rows"], ["pipeline", "Pipeline"]].map(([k, lab]) => (
                  <button key={k} className="nb" data-testid={`cand-mode-${k}`} aria-pressed={candMode === k} onClick={() => setCandMode(k)}
                    style={{ background: candMode === k ? "#0f2744" : "#f1f5f9", color: candMode === k ? "white" : "#334155", borderRadius: 20, padding: "0 16px", minHeight: 44, fontSize: 13, fontWeight: 700, border: 0 }}>{lab}</button>))}
              </div>
              {candMode === "pipeline" ? (
                <PipelineBoard candidates={filtered} offers={pipeOffers} placements={placements} stageTime={(c) => timeInStage(c, { activity: allActivity, offers: pipeOffers, placements })} onMove={movePipeline} onOpen={(c) => setSelected(c)} toast={showToast}
                  narrow={typeof window !== "undefined" && window.innerWidth <= 760} />
              ) : (
              <CandidatesTable candidates={filtered} setSelected={setSelected} updateCandidate={updateCandidate} candidateFilter={candidateFilter} setCandidateFilter={setCandidateFilter} showBulkEmail={showBulkEmail} setShowBulkEmail={setShowBulkEmail} addActivity={addActivity} toast={showToast} gmailAuth={gmailAuth} setSendingEmail={setSendingEmail} templates={templates} deleteCandidate={deleteCandidate}
                client={supabase} onNewCandidate={({ lead, candidate }) => { setCandidates(prev => [candidate, ...prev]); setLeads(prev => [lead, ...prev]); showToast("Candidate created"); }}
                onNewOnboardingLink={() => setShowOnbLink(true)} />
              )}
              </>
            ) : view === "leads" || view === "facilities" ? (
              <LeadsView key={view} kind={view === "facilities" ? "facilities" : "candidates"} onAlertsChanged={onLeadAlertsChanged} onFacilityTouched={closeFacilityReminder} onKind={leadKindView} waitingFor={waitingFor} cancelQueued={cancelQueued} placements={placements} openEmail={openEmail} emailOptedOut={emailOptedOut} emailCols={emailCols} setNoEmail={setNoEmail} facilityCols={facilityCols} saveFacility={saveFacility} callLead={callLead} liveTick={liveTick} setLeadQuery={setLeadQuery} mergeCols={mergeCols && owner} bulkOn={owner} notifications={allNotifications} leadQuery={leadQuery} onHandBack={handBackToAgent} callRows={callRows} onCallLogged={bumpCalls} assignRows={assignRows} onAssigned={bumpAssign} leads={leads} allLeads={allLeads} testCols={testCols && owner} showTests={showTests} setShowTests={setShowTests} setLeads={setLeads} spamCols={spamCols} handledCols={handledCols} candidates={candidates} conversations={conversations} convMsgRefs={convMsgRefs} leadDecisions={leadDecisions} addCandidate={addCandidate} toast={showToast} setView={setView} setConvDeepLink={setConvDeepLink} drafts={drafts} draftHandlers={draftHandlers} draftBusy={draftBusy} openDraft={openDraft}
                onMakeOffer={({ candidate, draft }) => { setOfferDraft({ ...draft, candidateId: candidate.id }); setDetailTab(null); setSelected(candidate); setView("candidates"); }}
                profileKit={profileData ? { api: profileApi, sendApi, base: onboardingCfg.baseUrl, onChanged: loadProfiles, rowOf: (id) => profileByCand.get(String(id)) || null, linkOf: (id) => profileLinks.get(String(id)) || null } : null}
                createCandidate={cflOn ? async (l) => { const before = new Set(candidates.map((c) => String(c.id))); const id = await cflApi.create(l.id);
                  takeCandidates(await db.getCandidates()); return { id, existed: before.has(String(id)) }; } : null} summaryKit={summaryKit}
                // DAY-DIRECTSTAFF-2026-11-06 §1: the facility's working view -- profile rows and licences for the matches, calls, Present (the 11-05 flow)
                facilityWorkKit={{ rowOf: (id) => (profileData ? profileByCand.get(String(id)) || null : null), credOf: (id) => (credDocs || []).filter((d) => d.candidate_id === id),
                  profileOf: (id) => (profileData ? profileApi.profile(id) : Promise.resolve(null)), callsApi, onCallLogged: () => setCallTick((n) => n + 1),
                  present: { api: facilityPortalApi, by: staffEmail, makeSummary: summaryKit ? summaryKit.make : async () => { throw new Error('The profile system is not on'); },
                    logFor: async (cid, note) => { const act = await db.addActivity(cid, PRESENT_LOG_TYPE, note); setAllActivity((prev) => [act, ...prev]); } } }} />
            ) : view === "drafts" ? (
              <>
              {needsRow}
              <DraftsView key={draftFocus || "drafts"} drafts={drafts} leadsById={leadsById} candidatesById={candidatesById} busyId={draftBusy} focusId={draftFocus} onRefresh={loadAgentTables} handlers={draftHandlers} />
              </>
            ) : view === "alerts" ? (
              <>
              {needsRow}
              <SystemAlerts key={alertFocus ? alertFocus.alertId : "alerts"} notifications={notifications} onMarkHandled={markSystemAlert} playAudio={voicemailAudio} focus={alertFocus} noteColumn={alertNoteColumn} />
              </>
            ) : view === "conversations" ? (
              <>
              {needsRow}
              <ConversationsView conversations={shownConversations} leads={leads} convMsgRefs={shownRefs} notifications={notifications} deepLinkPhone={convDeepLink} clearDeepLink={() => setConvDeepLink(null)} deepLinkId={convDeepLinkId} clearDeepLinkId={() => setConvDeepLinkId(null)} onRefresh={loadAgentTables} refreshing={agentRefreshing} loadedAt={agentLoadedAt} />
              </>
            ) : view === "reviews" ? (
              <>
              {needsRow}
              <Reviews api={reviewsApi} toast={showToast} onCount={setReviewsWaiting} />
              </>
            ) : view === "onboarding" ? (
              <>
              {candidatesRows}
              <OnboardingView overview={onbOverview} candidates={candidates} onReload={loadOnbOverview} onAdd={() => setShowOnbLink(true)}
                onOpenRecord={(id) => { const hit = candidates.find(x => x.id === id); if (hit) { setDetailTab("onboarding"); setSelected(hit); setView("candidates"); } }}
                onOpenExpirations={() => setView("expirations")} expirationsApi={expirationsApi}
                focusCandidateId={onbFocus} onFocusHandled={() => setOnbFocus(null)}
                tabProps={{ api: onboardingApi, toast: showToast, config: onboardingCfg, gmailSignedIn: !!(gmailAuth && gmailAuth.signedIn),
                  createDraft, staffEmail: gmailAuth && gmailAuth.email }} examsApi={examsApi} sendApi={sendApi}
                loadDocuments={loadBoardDocuments} loadHealth={loadBoardHealth} consentOf={(c) => textConsentOf(c, allLeads)} />
              </>
            ) : view === "duplicates" ? (
              <>
              {candidatesRows}
              {/* NIGHT-DIRECTSTAFF-2026-10-29 §1: every pair with the same phone or email; Merge (owners) / Not the same; M / N */}
              <DuplicatesView candidates={[...candidates, ...testCands]} notSameApi={notSameApi} toast={showToast} onOpen={(c) => { setSelected(c); setView("candidates"); }}
                leads={allLeads} onOpenLead={(l) => window.location.assign(leadHref(l.id))}
                mergeLeadGroup={mergeCols && owner ? async (g) => {   // DAY-DIRECTSTAFF-2026-10-29b §1: each newer lead into the oldest, one at a time
                  let by = ""; try { const { data } = await supabase.auth.getUser(); by = (data && data.user && data.user.email) || ""; } catch { by = ""; }
                  let keep = g.keep; let n = 0;
                  for (const o of g.others) { try { const d = await mergeLeads(supabase, keep, o, by); keep = { ...keep, ...d.fillPatch }; n += 1; } catch (e) { /* counted below */ } }
                  const fresh = await db.getLeads().catch(() => null); if (fresh) setLeads(fresh);
                  return n; } : null}
                merge={candMergeCols && owner ? async (keepId, otherId) => {
                  await mergeCandidates(supabase, keepId, otherId);
                  const [cands, acts] = await Promise.all([db.getCandidates(), db.getAllActivity()]);
                  takeCandidates(cands); setAllActivity(acts || []); loadPlacements(); loadCredentials(); setLiveTick((n) => n + 1);
                } : null} />
              </>
            ) : view === "expirations" ? (
              <>
              {candidatesRows}
              <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:12 }}>
                <NewOnboardingLinkButton onOpen={() => setShowOnbLink(true)} />
              </div>
              <ExpirationsView api={expirationsApi} candidates={candidates} staffEmail={gmailAuth && gmailAuth.email}
                onOpenCandidate={(id) => { const hit = candidates.find(x => x.id === id); if (hit) { setSelected(hit); setView("candidates"); } }} />
              </>
            ) : view === "reports" ? (
              <Reports load={loadReports} loadNumbers={loadNumbers} credCounts={credCounts} pipeline={pipeCounts}
                activity={<AuditView activity={allActivity} leads={allLeads} notifications={allNotifications} candidates={candidates} loadMore={loadAuditMore} />}
                credentials={<CredentialsReport documents={credDocs} candidates={candidates} decisions={leadDecisions} sendApi={sendApi} toast={showToast} activity={allActivity}
                  consentOf={(c) => textConsentOf(c, allLeads)} />}
                exportPanel={owner ? <ExportPanel leads={allLeads} candidates={candidates} by={staffEmail} toast={showToast}
                  placements={placementsReady ? placements : null} loadFacilityRequests={loadExportRequests}
                  loadBookkeeping={placementsReady ? async () => {   // NIGHT-DIRECTSTAFF-2026-10-29 §5: the stored bill and pay, joined here; nothing typed
                    const [o, r] = await Promise.all([supabase.from("offers").select("id,placement_id,facility_request_id,pay_hourly_taxable,pay_weekly_stipend,pay_other").eq("status", "accepted").limit(5000),
                      supabase.from("facility_requests").select("id,bill_rate,rate_basis").limit(5000)]);
                    return bookkeepingRows({ placements, offers: o.error ? [] : (o.data || []), requests: r.error ? [] : (r.data || []) }); } : null}
                  log={async (type, note) => { const act = await db.addActivity(null, type, note); setAllActivity((prev) => [act, ...prev]); }} /> : null} />
            ) : view === "activity" ? (
              <AuditView activity={allActivity} leads={allLeads} notifications={allNotifications} candidates={candidates} loadMore={loadAuditMore} />
            ) : view === "settings" ? (
              <>{owner && <StaffRoles api={staffApi} me={staffEmail} toast={showToast} />}
              <SettingsView templates={templates} saveTemplate={saveTemplate} delTemplate={delTemplate} /></>
            ) : null}
          </div>
        </div>
      </div>
      {showOnbLink && (
        <NewOnboardingLinkModal client={supabase} start={onboardingApi.start} config={onboardingCfg} createDraft={createDraft} sendApi={sendApi}
          gmailSignedIn={!!(gmailAuth && gmailAuth.signedIn)} toast={showToast} onClose={() => setShowOnbLink(false)}
          onCreated={(candidate) => { setCandidates(prev => [candidate, ...prev]); loadOnbOverview(); }}
          onOpenExisting={(cand) => {
            // on the Onboarding tab, the duplicate guard opens that candidate's panel right there
            if (view === "onboarding") { setShowOnbLink(false); setOnbFocus(cand.id); showToast("That email is already a candidate -- opened their onboarding"); return; }
            // the duplicate guard: that candidate's Onboarding tab, never a second row
            setShowOnbLink(false);
            const hit = candidates.find(x => x.id === cand.id) || cand;
            setDetailTab("onboarding"); setSelected(hit); setView("candidates");
            showToast("That email is already a candidate -- opened their Onboarding tab");
          }} />
      )}
      {portalContact && <PortalContact contact={portalContact} api={facilityPortalApi} sendApi={sendApi} toast={showToast} onClose={() => setPortalContact(null)}
        callsApi={callsApi} onCallLogged={() => setCallTick((n) => n + 1)} candidates={candidates} summaryKit={summaryKit} />}
      {/* NIGHT-DIRECTSTAFF-2026-10-22 §2: Text from "Ending soon" when there is no thread yet -- the same compose box as the lead rows */}
      {todayText && (
        <TextCompose lead={todayText} focusBody waiting={waitingFor(todayText.id)} onCancelWaiting={cancelQueued}
          loadThread={async () => []}
          loadTemplates={async () => parseTemplates(await db.getSetting(SMS_TEMPLATES_KEY))}
          saveTemplates={(list) => db.saveSettingChecked(SMS_TEMPLATES_KEY, serializeTemplates(list))}
          sendText={textOrQueue}
          onSent={(r) => showToast(textToast(r))}
          onClose={() => setTodayText(null)} conversationId={null} takeover={false} onHandBack={async (id) => { await staff.release(id); }} />
      )}
      {/* EMAIL-CRM-2026-10-22: the one Email box -- from Matt through crm-mailer, logged on the timeline */}
      {emailTo && (
        <EmailCompose {...emailTo} send={emailOrQueue} onClose={() => setEmailTo(null)}
          onSent={(r) => { showToast(r && r.queued ? "Saved: sending when online" : "Emailed"); setLiveTick((n) => n + 1); }}
          onBounced={() => { markAddressNoEmail(emailTo.to); setLiveTick((n) => n + 1); }} />
      )}
      {/* §14 (10-22): the keyboard list (?), and the row the keyboard is on */}
      <style>{`[data-kbd-on="true"]{outline:3px solid #1d4ed8;outline-offset:-3px}`}</style>
      {kbdHelp && (
        <div role="dialog" aria-label="Keyboard shortcuts" data-testid="kbd-help" style={{ position: "fixed", right: 16, bottom: 16, zIndex: 60, background: "white", border: "1px solid #cbd5e1", borderRadius: 12, padding: 16, boxShadow: "0 8px 24px #0f172a33" }}>
          <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 8px" }}>Keyboard (desktop)</h3>
          <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "4px 12px", margin: 0, fontSize: 13 }}>
            {KEYS.map(([k, w]) => <Fragment key={k}><dt><kbd style={{ border: "1px solid #cbd5e1", borderRadius: 4, padding: "0 6px" }}>{k}</kbd></dt><dd style={{ margin: 0 }}>{w}</dd></Fragment>)}
          </dl>
          <button className="nb" data-testid="kbd-help-close" onClick={() => setKbdHelp(false)} style={{ marginTop: 10, minHeight: 44, padding: "0 14px", borderRadius: 8, background: "#f1f5f9", fontWeight: 700 }}>Close</button>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── CANDIDATES TABLE ─────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
function CandidatesTable({ candidates, setSelected, updateCandidate, candidateFilter, setCandidateFilter, showBulkEmail, setShowBulkEmail, addActivity, toast, gmailAuth, setSendingEmail, templates, deleteCandidate, client = null, onNewCandidate = null, onNewOnboardingLink = null }) {
  const [showNewCandidate, setShowNewCandidate] = useState(false);   // + New candidate (hub, 2026-10-06)
  const [bulkSelected, setBulkSelected] = useState([]);
  const [bulkSubject, setBulkSubject] = useState("");
  const [bulkBody, setBulkBody] = useState("");
  const [sortBy, setSortBy] = useState("priority");

  const PRIORITY_ORDER = { "🔥 Hot":0, "Opportunity":1, "Warm":2, "Cold":3, "On Hold":4, "Placed":5 };
  const sorted = [...candidates].sort((a, b) => {
    switch(sortBy) {
      case "priority": return (PRIORITY_ORDER[a.priority]??9) - (PRIORITY_ORDER[b.priority]??9);
      case "priority_asc": return (PRIORITY_ORDER[b.priority]??9) - (PRIORITY_ORDER[a.priority]??9);
      case "last_contact_desc": return (b.last_contact||'').localeCompare(a.last_contact||'');
      case "last_contact_asc": return (a.last_contact||'').localeCompare(b.last_contact||'');
      case "name_asc": return (a.name||'').localeCompare(b.name||'');
      case "name_desc": return (b.name||'').localeCompare(a.name||'');
      case "stage": return STAGES.indexOf(a.stage) - STAGES.indexOf(b.stage);
      case "specialty": return (a.specialty||'').localeCompare(b.specialty||'');
      case "state": return (a.state||'').localeCompare(b.state||'');
      default: return 0;
    }
  });

  const filterLabels = { all:"All Candidates", hot:"🔥 Hot Leads", active:"Active Contracts", overdue:"Overdue Follow-ups", ending:"Ending Soon" };
  const toggleSelect = (id) => setBulkSelected(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  const toggleAll = () => setBulkSelected(prev => prev.length === candidates.length ? [] : candidates.map(c=>c.id));

  const sendBulkGmail = async () => {
    if (bulkSelected.length === 0 || !bulkBody.trim()) return;
    const sel = candidates.filter(c => bulkSelected.includes(c.id));
    if (isSignedIn()) {
      setSendingEmail(true);
      toast(`Sending to ${sel.length} candidates...`);
      const recipients = sel.filter(c => usableEmail(c.email)).map(c => ({ email: c.email, name: c.name, specialty: c.specialty, state: c.state, id: c.id }));   // 10-24 §4: never a placeholder
      const { sent, failed } = await sendBulkEmails(recipients, bulkSubject, bulkBody, (idx, total, name, success) => {
        toast(`${success ? '✓' : '✗'} ${name} (${idx}/${total})`);
        if (success) addActivity(recipients[idx-1]?.id, 'email', `Bulk email: ${bulkSubject}`);
      });
      setSendingEmail(false);
      toast(`Done — ${sent} sent${failed > 0 ? `, ${failed} failed` : ''}`);
    } else {
      sel.forEach((c, i) => {
        const body = bulkBody.replace(/{name}/g, c.name?.split(' ')[0]||'').replace(/{specialty}/g, c.specialty||'').replace(/{state}/g, c.state||'');
        const subject = bulkSubject.replace(/{name}/g, c.name?.split(' ')[0]||'').replace(/{specialty}/g, c.specialty||'');
        const url = `https://mail.google.com/mail/u/0/?view=cm&fs=1&to=${encodeURIComponent(c.email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        setTimeout(() => window.open(url, '_blank'), i * 500);
        addActivity(c.id, 'email', `Bulk email: ${subject}`);
      });
      toast(`Opened Gmail compose for ${sel.length} candidates`);
    }
    setShowBulkEmail(false);
    setBulkSelected([]);
    setBulkSubject("");
    setBulkBody("");
  };

  return (
    <div className="fade-in">
      {/* NAV-2026-10-22: Candidates is a primary nav item -- its header wraps and its table scrolls inside the card on a phone */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          <h2 style={{ fontSize:22, fontWeight:800, color:"#0f172a" }}>Candidates</h2>
          {candidateFilter !== "all" && (
            <div style={{ display:"flex", alignItems:"center", gap:6, background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:20, padding:"4px 12px" }}>
              <span style={{ fontSize:12, fontWeight:600, color:"#1d4ed8" }}>{filterLabels[candidateFilter]}</span>
              <button className="nb" onClick={() => setCandidateFilter("all")} style={{ color:"#3b82f6", fontSize:14, fontWeight:700, marginLeft:2 }}>×</button>
            </div>
          )}
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          {client && onNewCandidate && <NewCandidateButton onOpen={() => setShowNewCandidate(true)} />}
          {onNewOnboardingLink && <NewOnboardingLinkButton onOpen={onNewOnboardingLink} />}
          {bulkSelected.length > 0 && (
            <button className="nb" onClick={() => setShowBulkEmail(true)} style={{ background:"#1d4ed8", color:"white", borderRadius:8, padding:"7px 16px", fontSize:12, fontWeight:600 }}>
              ✉ Email {bulkSelected.length} Selected
            </button>
          )}
          <select aria-label="Sort candidates" value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ fontSize:12, padding:"5px 10px" }}>
            <option value="priority">Priority (High first)</option>
            <option value="priority_asc">Priority (Low first)</option>
            <option value="last_contact_desc">Last Contact (Newest)</option>
            <option value="last_contact_asc">Last Contact (Oldest)</option>
            <option value="name_asc">Name (A→Z)</option>
            <option value="name_desc">Name (Z→A)</option>
            <option value="stage">Stage</option>
            <option value="specialty">Specialty</option>
            <option value="state">State</option>
          </select>
          <span style={{ color:"#475569", fontSize:13 }}>{candidates.length} total</span>
        </div>
      </div>
      <div className="card" style={{ overflowX:"auto" }}>
        <table className="stack-table cand-table" style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"#f8fafc", borderBottom:"1px solid #e2e8f0" }}>
              <th style={{ padding:"10px 10px", width:36 }}>
                <input type="checkbox" aria-label="Select all candidates" checked={bulkSelected.length === candidates.length && candidates.length > 0} onChange={toggleAll} style={{ cursor:"pointer" }} />
              </th>
              {["Name","Specialty","Stage","Priority","State","Creds","Last Contact","Source","Delete"].map(h => (
                <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", textTransform:"uppercase", letterSpacing:".5px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(c => {
              const nf = calcNextFollowUp(c);
              const nfd = daysUntil(nf);
              const cc = credCount(c);
              const src = getSource(c);
              const isChecked = bulkSelected.includes(c.id);
              return (
                <tr key={c.id} style={{ borderBottom:"1px solid #f1f5f9", cursor:"pointer", transition:"background .1s", background:isChecked?"#eff6ff":"transparent" }} className="card-hover">
                  <td style={{ padding:"12px 10px" }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`Select ${c.name}`} checked={isChecked} onChange={() => toggleSelect(c.id)} style={{ cursor:"pointer" }} />
                  </td>
                  <td style={{ padding:"12px 14px" }} onClick={() => setSelected(c)}>
                    <div style={{ fontWeight:600, color:"#0f172a" }}>{c.name}{isCandTest(c) && <span data-testid={`cand-test-pill-${c.id}`} className="pill" style={{ background:"#ede9fe", color:"#5b21b6", fontSize:10, marginLeft:6 }}>Test</span>}</div>
                    <div style={{ fontSize:11, color:"#64748b" }}>{usableEmail(c.email) ? c.email : <span data-testid={`cand-noemail-${c.id}`} style={{ color:"#92400e" }}>No email on file</span>}</div>
                    {/* NIGHT-DIRECTSTAFF-2026-10-28 §7: their availability, one line (the detail has the rest and "Ask for availability") */}
                    {availabilityShort(c) && <div data-testid={`cand-avail-${c.id}`} style={{ fontSize:11.5, color:"#166534", fontWeight:600, overflowWrap:"anywhere" }}>{availabilityShort(c)}</div>}
                  </td>
                  <td style={{ padding:"12px 14px" }} onClick={() => setSelected(c)}>
                    <span className="pill" style={{ background:`${SPEC_COLOR[c.specialty]||"#94a3b8"}15`, color:"#1e293b", border:`1px solid ${SPEC_COLOR[c.specialty]||"#94a3b8"}66` }}>{c.specialty}{c.sub ? ` · ${c.sub}` : ''}</span>
                  </td>
                  <td style={{ padding:"12px 14px" }} onClick={() => setSelected(c)}>
                    <span className="pill" style={{ background:`${STAGE_COLOR[c.stage]||"#94a3b8"}15`, color:"#1e293b", border:`1px solid ${STAGE_COLOR[c.stage]||"#94a3b8"}66` }}>{c.stage}</span>
                    {nfd < 0 && c.stage !== "Inactive" && c.stage !== "Completed" && (
                      <span style={{ fontSize:10, color:"#b91c1c", fontWeight:600, marginLeft:6 }}>{Math.abs(nfd)}d overdue</span>
                    )}
                  </td>
                  <td style={{ padding:"12px 14px" }} onClick={() => setSelected(c)}>
                    <span className="pill" style={{ background:`${PRIORITY_COLORS[c.priority]||"#94a3b8"}15`, color:"#1e293b", border:`1px solid ${PRIORITY_COLORS[c.priority]||"#94a3b8"}66` }}>{c.priority}</span>
                  </td>
                  <td style={{ padding:"12px 14px", fontSize:12, color:"#475569" }} onClick={() => setSelected(c)}>{c.state || '—'}</td>
                  <td style={{ padding:"12px 14px" }} onClick={() => setSelected(c)}>
                    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                      <div style={{ width:40, height:4, borderRadius:2, background:"#e2e8f0", overflow:"hidden" }}>
                        <div style={{ width:`${(cc/5)*100}%`, height:"100%", background:cc>=4?"#22c55e":cc>=2?"#f59e0b":"#ef4444", borderRadius:2 }}/>
                      </div>
                      <span style={{ fontSize:11, color:"#64748b" }}>{cc}/5</span>
                    </div>
                  </td>
                  <td style={{ padding:"12px 14px", fontSize:12, color:"#475569" }} onClick={() => setSelected(c)}>{fmtDate(c.last_contact)}</td>
                  <td style={{ padding:"12px 14px", fontSize:11, color:"#64748b" }} onClick={() => setSelected(c)}>{src || '—'}</td>
                  <td style={{ padding:"8px 10px" }} onClick={e => e.stopPropagation()}>
                    <button className="nb" onClick={() => {
                      if (window.confirm(`Delete ${c.name}?`)) deleteCandidate(c.id, c.name);
                    }} style={{ color:"#ef4444", opacity:.5, fontSize:14 }} title="Delete">🗑</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNewCandidate && (
        <NewCandidateModal client={client} onClose={() => setShowNewCandidate(false)}
          onCreated={(out) => { setShowNewCandidate(false); onNewCandidate(out); }} />
      )}

      {/* Bulk Email Modal */}
      {showBulkEmail && (
        <div style={{ position:"fixed", inset:0, background:"#0008", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }} onClick={() => setShowBulkEmail(false)}>
          <div className="card" style={{ width:640, maxHeight:"80vh", overflow:"auto", padding:24 }} onClick={e => e.stopPropagation()}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ fontSize:16, fontWeight:700 }}>✉ Bulk Email — {bulkSelected.length} candidates</h3>
              <button className="nb" onClick={() => setShowBulkEmail(false)}><I n="x" s={18} c="#94a3b8"/></button>
            </div>

            <div style={{ marginBottom:12, background:"#f8fafc", borderRadius:8, padding:10, maxHeight:100, overflowY:"auto" }}>
              <div style={{ fontSize:11, color:"#64748b", fontWeight:600, marginBottom:6 }}>TO:</div>
              <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                {candidates.filter(c => bulkSelected.includes(c.id)).map(c => (
                  <span key={c.id} className="pill" style={{ background:"#eff6ff", color:"#1d4ed8", fontSize:10 }}>{c.name}</span>
                ))}
              </div>
            </div>

            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:11, color:"#64748b", fontWeight:600, marginBottom:4 }}>Quick Templates</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {(templates||[]).map(t => (
                  <button key={t.id} className="nb" onClick={() => { setBulkSubject(t.subject||''); setBulkBody(t.body); }} style={{ background:"#f1f5f9", color:"#475569", borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:600 }}>
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Subject</label>
              <input className="input" value={bulkSubject} onChange={e => setBulkSubject(e.target.value)} placeholder="Subject line... ({name}, {specialty}, {state} auto-fill)" style={{ marginTop:4 }} />
            </div>

            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Body</label>
              <textarea className="input" rows={10} value={bulkBody} onChange={e => setBulkBody(e.target.value)} placeholder="Compose your email... Use {name}, {specialty}, {state} for merge fields" style={{ marginTop:4, resize:"vertical", lineHeight:1.6 }} />
            </div>

            {gmailAuth.signedIn ? (
              <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:10, marginBottom:14, fontSize:12, color:"#166534" }}>
                ✓ Gmail connected — emails will send directly via {gmailAuth.email}
              </div>
            ) : (
              <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:10, marginBottom:14, fontSize:12, color:"#92400e" }}>
                ⚠ Gmail not connected — will open a compose tab per candidate. <button className="nb" onClick={signIn} style={{ color:"#1d4ed8", fontWeight:700, fontSize:12, textDecoration:"underline" }}>Connect Gmail</button> to send directly.
              </div>
            )}

            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button className="nb" onClick={() => setShowBulkEmail(false)} style={{ background:"#f1f5f9", color:"#64748b", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600 }}>Cancel</button>
              <button className="nb" onClick={sendBulkGmail} disabled={!bulkBody.trim() || bulkSelected.length===0} style={{ background:bulkBody.trim()?"#1d4ed8":"#e2e8f0", color:bulkBody.trim()?"white":"#94a3b8", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600 }}>
                {isSignedIn() ? `Send to ${bulkSelected.length} Candidate${bulkSelected.length!==1?"s":""}` : `Open Gmail for ${bulkSelected.length} Candidate${bulkSelected.length!==1?"s":""}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── EMAILS TAB ───────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
function EmailsTab({ candidate, activity = [], emailSync = [] }) {
  // Phase 2b: render email history from activity_log (not the Gmail API).
  //  type 'email'      -> sent/drafted from the CRM (note = description)
  //  type 'email_sync' -> synced Gmail message (note = JSON: subject/snippet/direction/from)
  const rows = (activity || [])
    .filter(a => a.type === 'email' || a.type === 'email_sync')
    .map(a => {
      let subject = a.note || '', snippet = '', direction = 'outbound', from = '';
      if (a.type === 'email_sync') {
        try { const d = JSON.parse(a.note); subject = d.subject || 'Email'; snippet = d.snippet || ''; direction = d.direction || 'inbound'; from = d.from || ''; }
        catch { subject = 'Email'; snippet = a.note || ''; direction = 'inbound'; }
      }
      return { id: a.id, date: a.created_at || a.date, type: a.type, subject, snippet, direction, from };
    })
    .sort((x, y) => new Date(y.date) - new Date(x.date));

  return (
    <div className="fade-in">
      <div className="card" style={{ overflow:"hidden" }}>
        {rows.length === 0 ? (
          <div style={{ padding:40, textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:10 }}>📭</div>
            <div style={{ fontWeight:600, color:"#64748b" }}>No email activity logged</div>
            <div style={{ fontSize:13, color:"#94a3b8", marginTop:6 }}>Emails sent or drafted from the CRM for {candidate.name} will appear here.</div>
          </div>
        ) : rows.map((email) => (
          <div key={email.id} className="email-row">
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"12px 16px", borderBottom:"1px solid #f1f5f9" }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:email.direction==="outbound"?"#3b82f6":"#0f172a", background:email.direction==="outbound"?"#eff6ff":"#f8fafc", padding:"2px 8px", borderRadius:20 }}>
                    {email.type === "email_sync" ? (email.direction === "outbound" ? "→ Sent" : "← Received") : "→ Logged"}
                  </span>
                  {email.from && <span style={{ fontSize:11, color:"#94a3b8" }}>{email.from.substring(0,50)}</span>}
                </div>
                <div style={{ fontSize:13, fontWeight:600, color:"#0f172a" }}>{email.subject}</div>
                {email.snippet && <div style={{ fontSize:12, color:"#64748b", marginTop:4, lineHeight:1.5 }}>{email.snippet.substring(0,160)}{email.snippet.length>160?"...":""}</div>}
              </div>
              <div style={{ fontSize:11, color:"#94a3b8", whiteSpace:"nowrap", marginLeft:12 }}>{fmtDate(email.date)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── CANDIDATE DETAIL ─────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
// NIGHT-DIRECTSTAFF-2026-10-19 §2 (axe): a colour-coded label's TEXT in its colour mixed 45% toward black, so it reads at
// 4.5:1 or better on its own pale tint (the stage, priority and specialty colours are bright on purpose for the dots/bars).
const inkOf = (hex) => { const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "")); if (!m) return "#334155";
  const n = parseInt(m[1], 16); const f = (v) => Math.round(v * 0.55).toString(16).padStart(2, "0");
  return `#${f(n >> 16)}${f((n >> 8) & 255)}${f(n & 255)}`; };
function CandidateDetail({ candidate:c, activity, emailSync, back, updateCandidate, addActivity, jobs, toast, allCandidates, gmailAuth, setSendingEmail, templates, deleteCandidate, initialTab = null, timelineSources = null, placementsKit = null, credentialsKit = null, memoryKit = null, onEmail = null, emailOff = false, jobsKit = null, offersKit = null, dupKit = null, testKit = null, saveEmail = null, onbKit = null, availKit = null, callKit = null, profileKit = null, workKit = null }) {
  const [tab, setTab] = useState(initialTab || "overview");
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [actInput, setActInput] = useState("");
  const [actType, setActType] = useState("call");
  const [tplModal, setTplModal] = useState(null);
  const [tplBody, setTplBody] = useState("");

  const credLabels = { resume:"Resume", bls:"BLS/CPR", refs:"References (2)", immunizations:"Immunizations", skills:"Skills Checklist" };
  const creds = typeof c.creds === 'string' ? JSON.parse(c.creds) : (c.creds || {});
  const cc = Object.values(creds).filter(Boolean).length;
  const nf = calcNextFollowUp(c);
  const nfd = daysUntil(nf);
  const avail = getAvailableDate(c);
  const src = getSource(c);
  const matchedJobs = jobs.filter(j => j.Discipline === c.specialty);

  // Parse email sync data for display
  const emails = emailSync.map(e => {
    try {
      const data = JSON.parse(e.note);
      return { ...data, id: e.id, date: e.date || data.date, activityId: e.id };
    } catch {
      return { subject: 'Email', snippet: e.note, date: e.date, direction: 'unknown', id: e.id };
    }
  });

  // Last email summary for overview
  const lastEmail = emails[0];

  const fillTemplate = (tpl, job = null) => {
    let body = tpl.body
      .replace(/{name}/g, c.name?.split(' ')[0] || '')
      .replace(/{specialty}/g, c.specialty || '')
      .replace(/{state}/g, c.state || '')
      .replace(/{email}/g, c.email || '');
    let subject = (tpl.subject || '')
      .replace(/{name}/g, c.name?.split(' ')[0] || '')
      .replace(/{specialty}/g, c.specialty || '')
      .replace(/{state}/g, c.state || '');
    if (job) {
      body = body.replace(/{city}/g, job.City||'').replace(/{setting}/g, job.Setting||'').replace(/{pay}/g, job.Pay||job['After Tax']||'').replace(/{start}/g, job['Start Date']||'');
      subject = subject.replace(/{state}/g, job.State||c.state||'');
    }
    setTplBody(body);
    setTplModal({ ...tpl, subject, filledBody: body });
  };

  const startEdit = () => {
    setEditData({
      name: c.name||'', email: c.email||'', phone: c.phone||'', specialty: c.specialty||'PT',
      sub: c.sub||'', state: c.state||'', stage: c.stage||'', priority: c.priority||'Warm',
      notes: c.notes||'', active_contract: c.active_contract||'', contract_end: c.contract_end||'',
    });
    setEditMode(true);
  };

  const saveEdit = () => {
    updateCandidate(c.id, editData);
    setEditMode(false);
  };

  const toggleCred = (key) => {
    const updated = { ...creds, [key]: !creds[key] };
    updateCandidate(c.id, { creds: updated });
    toast(`${credLabels[key]} ${updated[key] ? '✓' : '✗'}`);
  };

  const setFollowUpDate = (date) => {
    const tags = (c.tags || []).filter(t => typeof t !== 'string' || !t.startsWith('followup:'));
    tags.push(`followup:${date}`);
    updateCandidate(c.id, { tags });
  };

  const setAvailableDate = (date) => {
    const tags = (c.tags || []).filter(t => typeof t !== 'string' || !t.startsWith('available:'));
    if (date) tags.push(`available:${date}`);
    updateCandidate(c.id, { tags });
  };

  const logActivity = () => {
    if (!actInput.trim()) return;
    addActivity(c.id, actType, actInput.trim());
    setActInput("");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast("Copied to clipboard"));
  };

  // NIGHT-DIRECTSTAFF-2026-10-19 §2: "Timeline" -- every event for this person in one list. The other tabs stay (editing,
  // templates, jobs and the packet's actions are not events), so nothing becomes harder to reach.
  const tabs = timelineSources ? ["overview","timeline","emails","onboarding","edit","templates","jobs","activity"] : ["overview","emails","onboarding","edit","templates","jobs","activity"];

  return (
    <div className="fade-in">
      {/* Back + Header */}
      <button onClick={back} className="nb" style={{ color:"#475569", fontSize:13, marginBottom:12, display:"flex", alignItems:"center", gap:6 }}>
        <I n="back" s={16} c="#475569"/> Back to list
      </button>

      <div className="card" style={{ padding:20, marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div style={{ display:"flex", gap:14, alignItems:"center" }}>
            <div style={{ width:52, height:52, borderRadius:14, background:`${SPEC_COLOR[c.specialty]||"#3b82f6"}15`, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <span style={{ color:inkOf(SPEC_COLOR[c.specialty]||"#3b82f6"), fontWeight:800, fontSize:16 }}>{c.specialty}</span>
            </div>
            <div>
              <h2 style={{ fontSize:20, fontWeight:800, color:"#0f172a" }}>{c.name}</h2>
              <div style={{ display:"flex", gap:8, alignItems:"center", marginTop:4, flexWrap:"wrap" }}>
                <span className="pill" style={{ background:`${STAGE_COLOR[c.stage]||"#94a3b8"}15`, color:inkOf(STAGE_COLOR[c.stage]||"#94a3b8") }}>{c.stage}</span>
                <span className="pill" style={{ background:`${PRIORITY_COLORS[c.priority]||"#94a3b8"}15`, color:inkOf(PRIORITY_COLORS[c.priority]||"#94a3b8") }}>{c.priority}</span>
                {c.sub && <span style={{ fontSize:12, color:"#475569" }}>{c.sub}</span>}
                {c.state && <span style={{ fontSize:12, color:"#475569" }}>· {c.state}</span>}
              </div>
              {/* §1 (NIGHT-DIRECTSTAFF-2026-10-22): where they are working now */}
              {placementsKit && currentPlacement(placementsKit.rows) && (
                <div data-testid="cand-at" style={{ fontSize:13, fontWeight:700, color:"#166534", marginTop:6 }}>{headerLine(currentPlacement(placementsKit.rows))}</div>
              )}
              {/* §11 (10-22): what they've told us -- the agents window's memory, read-only, with "Wrong?" */}
              {memoryKit && <CandidateMemory candidate={c} conversations={memoryKit.conversations} saveNote={memoryKit.saveNote} toast={toast} />}
              {/* NIGHT-DIRECTSTAFF-2026-10-23b §2: another record with the same phone or email */}
              {dupKit && !isCandTest(c) && <CandidateDuplicates candidate={c} candidates={dupKit.candidates} canMerge={dupKit.canMerge} merge={dupKit.merge} onOpen={dupKit.onOpen} toast={toast} />}
              {testKit && <CandidateTestMark candidate={c} mark={testKit.mark} toast={toast} />}
            </div>
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {/* EMAIL-CRM-2026-10-22: Email from Matt through crm-mailer (it was a Gmail compose window) */}
            {onEmail ? <EmailButton testid="cand-email-btn" email={usableEmail(c.email)} optedOut={emailOff} onOpen={() => onEmail(c)} /> : null}
            {usableEmail(c.email) && (
            <a href={`https://mail.google.com/mail/u/0/#search/from%3A${encodeURIComponent(c.email)}+OR+to%3A${encodeURIComponent(c.email)}`} target="_blank" rel="noreferrer" style={{ background:"#f1f5f9", color:"#475569", borderRadius:8, padding:"7px 12px", fontSize:12, fontWeight:600, textDecoration:"none" }}>
              Gmail ↗
            </a>)}
          </div>
        </div>
      </div>

      {/* DAY-DIRECTSTAFF-2026-11-05 §1: the recruiter's working view, above the tabs (the same facts are further down; nothing removed) */}
      {workKit && <WorkView candidate={c} row={workKit.row} credentialRows={credentialsKit ? credentialsKit.rows || [] : []} leads={workKit.leads} conversations={workKit.conversations}
        notifications={workKit.notifications} loadEvents={workKit.loadEvents} jobsLoad={workKit.jobsLoad} onText={workKit.onText} profileApi={workKit.profileApi}
        present={workKit.present} toast={toast} openPresent={!!workKit.openPresent} onPresentShown={workKit.onPresentShown} />}

      {/* Tabs */}
      <div style={{ display:"flex", gap:0, borderBottom:"2px solid #e2e8f0", marginBottom:20, flexWrap:"wrap" }}>
        {tabs.map(t => (
          <button key={t} className={`tab-btn ${tab===t?"active":""}`} onClick={() => setTab(t)}>
            {t === "overview" ? "Overview" : t === "timeline" ? "Timeline" : t === "emails" ? "Emails" : t === "onboarding" ? "Onboarding" : t === "edit" ? "Edit" : t === "templates" ? "Templates" : t === "jobs" ? `Jobs (${matchedJobs.length})` : "Activity"}
          </button>
        ))}
      </div>

      {/* §1 (NIGHT-DIRECTSTAFF-2026-10-22): the placements, first on the overview */}
      {tab === "overview" && placementsKit && (
        <Placements candidate={c} rows={placementsKit.rows} ready={placementsKit.ready} api={placementsKit.api} onChanged={placementsKit.reload} toast={toast} />
      )}
      {/* NIGHT-DIRECTSTAFF-2026-10-23b §1: offers -- accepting one makes the placement above Confirmed */}
      {tab === "overview" && offersKit && <Offers key={offersKit.draft ? `draft-${offersKit.draft.card.id}` : 'offers'} candidate={c} api={offersKit.api} onChanged={offersKit.onChanged} toast={toast} sendApi={offersKit.sendApi || null} textConsent={offersKit.textConsent ?? null}
        draft={offersKit.draft || null} onDraftUsed={offersKit.onDraftUsed} onMade={offersKit.onMade || null} onAccepted={offersKit.onAccepted || null} />}
      {/* NIGHT-DIRECTSTAFF-2026-10-24 §1: what they told us about their availability, and the link to tell us again */}
      {tab === "overview" && availKit && <CandidateAvailability candidate={c} api={availKit.api} sendApi={availKit.sendApi} base={availKit.base} textConsent={availKit.textConsent} toast={toast} />}
      {/* DAY-DIRECTSTAFF-2026-10-30b §1: the profile -- 7 items, Submission-ready, the link, references on tap, the summary PDF */}
      {tab === "overview" && profileKit && <CandidateProfile candidate={c} row={profileKit.row} link={profileKit.link} kit={profileKit} toast={toast} />}
      {/* NIGHT-DIRECTSTAFF-2026-10-24 §4: the onboarding packet's progress, compact; Open packet -> the Onboarding tab */}
      {tab === "overview" && onbKit && <OnboardingProgress candidate={c} {...onbKit} onOpenPacket={() => setTab("onboarding")} />}
      {/* NIGHT-DIRECTSTAFF-2026-10-29 §4: the documents, on file / missing / expired, and one Request for the missing ones */}
      {tab === "overview" && onbKit && <OnboardingDocs candidate={c} progressRows={onbKit.progressRows} documents={onbKit.documents} healthRows={onbKit.healthRows}
        sendApi={availKit ? availKit.sendApi : null} textConsent={availKit ? availKit.textConsent : null} toast={toast} />}
      {/* NIGHT-DIRECTSTAFF-2026-10-25 §2: Log call (how it went, the next step; a call-back lands on Today at its time) */}
      {tab === "overview" && callKit && <div style={{ margin: "8px 0", display: "flex", flexWrap: "wrap", gap: 8 }}><LogCall testid="cand-logcall" target={{ kind: "candidate", id: c.id }} label={c.name} api={callKit.api} toast={toast} onLogged={callKit.onLogged} /></div>}
      {/* §8 (10-22): credentials with expiry dates, from the onboarding's own documents */}
      {tab === "overview" && credentialsKit && (
        <Credentials candidate={c} rows={credentialsKit.rows} extra={credentialsKit.extra} api={credentialsKit.api} onChanged={credentialsKit.reload} toast={toast} />
      )}
      {/* Matt 2026-10-23: live jobs for their discipline in their licensed states (job-distribution, ?source=crm); hidden on any error */}
      {tab === "overview" && jobsKit && <JobsMatch candidate={c} credentialRows={credentialsKit ? credentialsKit.rows : []} load={jobsKit.load} />}
      {/* TAB: OVERVIEW */}
      {tab === "overview" && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap:16 }}>
          {/* Contact Info */}
          <div className="card" style={{ padding:18 }}>
            <h4 style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:12 }}>Contact Info</h4>
            {[["Email", c.email],["Phone", telHref(c.phone) ? <a data-testid="cand-phone-link" href={telHref(c.phone)} style={{ display:"inline-flex", alignItems:"center", minHeight:44, color:"#1d4ed8", fontWeight:600 }}>{c.phone}</a> : (c.phone||"—")],["State", c.state||"—"],["Licenses", Array.isArray(c.licenses) ? c.licenses.join(", ") : c.licenses||"—"],["Recruiter", c.recruiter||"Matthew"]].map(([l,v]) => (
              <div key={l} style={{ display:"flex", justifyContent:"space-between", gap:8, padding:"5px 0", borderBottom:"1px solid #f8fafc", minWidth:0 }}>
                <span style={{ fontSize:12, color:"#475569" }}>{l}</span>
                {/* 10-24 §4: a placeholder (no "@") is never shown as an address -- "No email on file" and Add email instead */}
                {l === "Email" && saveEmail ? <CandidateEmail candidate={c} save={(email) => saveEmail(c.id, email)} toast={toast} />
                  : <span style={{ fontSize:12, color:"#0f172a", fontWeight:500 }}>{v}</span>}
              </div>
            ))}
            {src && (
              <div style={{ display:"flex", justifyContent:"space-between", padding:"5px 0" }}>
                <span style={{ fontSize:12, color:"#475569" }}>Source</span>
                <span style={{ fontSize:11, color:"#8b5cf6", fontWeight:600, background:"#8b5cf615", padding:"2px 8px", borderRadius:20 }}>{src}</span>
              </div>
            )}
          </div>

          {/* Status & Dates */}
          <div className="card" style={{ padding:18 }}>
            <h4 style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:12 }}>Status & Dates</h4>
            <div style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #f8fafc" }}>
              <span style={{ fontSize:12, color:"#475569" }}>Last Contact</span>
              <span style={{ fontSize:12, color:"#0f172a", fontWeight:500 }}>{fmtDateFull(c.last_contact)}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #f8fafc" }}>
              <span style={{ fontSize:12, color:"#475569" }}>Next Follow-up</span>
              <span style={{ fontSize:12, fontWeight:600, color:nfd<0?"#b91c1c":nfd===0?"#b45309":"#15803d" }}>
                {nfd < 0 ? `${Math.abs(nfd)}d overdue` : nfd === 0 ? "Today" : fmtDateFull(nf)}
              </span>
            </div>
            {avail && (
              <div style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #f8fafc" }}>
                <span style={{ fontSize:12, color:"#475569" }}>Available Date</span>
                <span style={{ fontSize:12, color:"#0f172a", fontWeight:500 }}>{fmtDateFull(avail)}</span>
              </div>
            )}
            {c.contract_end && (
              <div style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #f8fafc" }}>
                <span style={{ fontSize:12, color:"#475569" }}>Contract End</span>
                <span style={{ fontSize:12, fontWeight:600, color:daysUntil(c.contract_end)<=CONTRACT_ALERT_DAYS?"#b45309":"#0f172a" }}>{fmtDateFull(c.contract_end)}</span>
              </div>
            )}
            {c.active_contract && (
              <div style={{ display:"flex", justifyContent:"space-between", padding:"5px 0" }}>
                <span style={{ fontSize:12, color:"#475569" }}>Active Contract</span>
                <span style={{ fontSize:12, color:"#0f172a", fontWeight:500 }}>{c.active_contract}</span>
              </div>
            )}
            {/* Quick date setters */}
            <div style={{ marginTop:12, display:"flex", gap:6 }}>
              <div style={{ flex:1 }}>
                <label htmlFor="cand-set-followup" style={{ fontSize:10, color:"#475569", fontWeight:600 }}>Set Follow-up</label>
                <input id="cand-set-followup" type="date" className="input" style={{ fontSize:12, padding:"5px 8px", marginTop:3 }} defaultValue={nf} onChange={e => setFollowUpDate(e.target.value)} />
              </div>
              <div style={{ flex:1 }}>
                <label htmlFor="cand-set-available" style={{ fontSize:10, color:"#475569", fontWeight:600 }}>Set Available</label>
                <input id="cand-set-available" type="date" className="input" style={{ fontSize:12, padding:"5px 8px", marginTop:3 }} defaultValue={avail||''} onChange={e => setAvailableDate(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Credentials */}
          <div className="card" style={{ padding:18 }}>
            <h4 style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:12 }}>Credentials ({cc}/5)</h4>
            {Object.entries(credLabels).map(([key, label]) => (
              <div key={key} onClick={() => toggleCred(key)} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:"1px solid #f8fafc", cursor:"pointer" }}>
                <span style={{ fontSize:12, color:"#475569" }}>{label}</span>
                <div style={{ width:22, height:22, borderRadius:6, background:creds[key]?"#22c55e15":"#ef444415", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <I n={creds[key]?"check":"x"} s={14} c={creds[key]?"#15803d":"#b91c1c"}/>
                </div>
              </div>
            ))}
          </div>

          {/* Notes + Last Email */}
          <div className="card" style={{ padding:18 }}>
            <h4 style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:12 }}>Notes</h4>
            <div style={{ fontSize:13, color:"#475569", lineHeight:1.6, whiteSpace:"pre-wrap", maxHeight:160, overflowY:"auto" }}>
              {c.notes || <span style={{ color:"#475569", fontStyle:"italic" }}>No notes yet</span>}
            </div>
            {lastEmail && (
              <div style={{ marginTop:14, borderTop:"1px solid #e2e8f0", paddingTop:12 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#3b82f6", marginBottom:6 }}>📧 Last Email Sync</div>
                <div style={{ fontSize:12, fontWeight:600, color:"#0f172a" }}>{lastEmail.subject || "—"}</div>
                <div style={{ fontSize:11, color:"#64748b", marginTop:3 }}>{(lastEmail.snippet || lastEmail.body || "").substring(0, 120)}...</div>
                <div style={{ fontSize:10, color:"#475569", marginTop:4 }}>{fmtDate(lastEmail.date)} · {lastEmail.direction || "synced"}</div>
              </div>
            )}
          </div>

          {/* Tags */}
          {(c.tags && c.tags.length > 0) && (
            <div className="card" style={{ padding:18, gridColumn:"1 / -1" }}>
              <h4 style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:10 }}>Tags</h4>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {c.tags.map((t,i) => (
                  <span key={i} className="pill" style={{ background:"#f1f5f9", color:"#475569", fontSize:11 }}>{typeof t === 'string' ? t : JSON.stringify(t)}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: EMAILS */}
      {tab === "timeline" && timelineSources && (
        <Timeline idPrefix="cand" printable title={c.name} reloadKey={timelineSources.tick} load={() => loadTimeline({ supabase, candidate: c, lead: null, allLeads: timelineSources.allLeads, conversations: timelineSources.conversations, notifications: timelineSources.notifications, placements: timelineSources.placements || [] })} />
      )}
      {tab === "emails" && <EmailsTab candidate={c} activity={activity} emailSync={emailSync} />}

      {/* TAB: ONBOARDING */}
      {tab === "onboarding" && (
        <OnboardingTab candidate={c} api={onboardingApi} toast={toast} config={onboardingCfg}
          gmailSignedIn={!!(gmailAuth && gmailAuth.signedIn)} createDraft={createDraft} staffEmail={gmailAuth && gmailAuth.email} examsApi={examsApi} sendApi={sendApi} />
      )}
      {/* NIGHT-DIRECTSTAFF-2026-10-25d §5: the e-sign audit trail (the onboard portal's staff-only /api/esign-audit) */}
      {tab === "onboarding" && <EsignAudit candidate={c} baseUrl={onboardingCfg.baseUrl}
        token={async () => { const { data } = await supabase.auth.getSession(); return (data && data.session && data.session.access_token) || ""; }} />}

      {/* TAB: EDIT */}
      {tab === "edit" && (
        <div className="fade-in">
          {!editMode ? (
            <div className="card" style={{ padding:20 }}>
              <button className="nb" onClick={startEdit} style={{ background:"#1d4ed8", color:"white", borderRadius:9, padding:"10px 20px", fontSize:13, fontWeight:600 }}>
                <I n="edit" s={14} c="white"/> Edit Candidate
              </button>
              <div style={{ marginTop:16 }}>
                <h4 style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>Quick Stage Change</h4>
                <select value={c.stage} onChange={e => updateCandidate(c.id, { stage: e.target.value })} style={{ width:"100%", maxWidth:300 }}>
                  {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ marginTop:12 }}>
                <h4 style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>Quick Priority Change</h4>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {Object.keys(PRIORITY_COLORS).map(p => (
                    <button key={p} className="nb" onClick={() => updateCandidate(c.id, { priority: p })} style={{ background:c.priority===p?`${PRIORITY_COLORS[p]}20`:"#f1f5f9", color:c.priority===p?PRIORITY_COLORS[p]:"#64748b", border:`1.5px solid ${c.priority===p?PRIORITY_COLORS[p]:"#e2e8f0"}`, borderRadius:20, padding:"5px 14px", fontSize:12, fontWeight:600 }}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginTop:16, paddingTop:16, borderTop:"1px solid #fee2e2" }}>
                <button className="nb" onClick={() => {
                  if (window.confirm(`Delete ${c.name}? This cannot be undone.`)) {
                    deleteCandidate(c.id, c.name);
                  }
                }} style={{ background:"#fef2f2", color:"#ef4444", border:"1px solid #fecaca", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:600 }}>
                  🗑 Delete {c.name}
                </button>
              </div>
            </div>
          ) : (
            <div className="card" style={{ padding:20 }}>
              <h3 style={{ fontSize:15, fontWeight:700, marginBottom:16 }}>Edit {c.name}</h3>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {[["name","Name"],["email","Email"],["phone","Phone"],["sub","Sub-specialty"],["state","State"],["active_contract","Active Contract"]].map(([k,l]) => (
                  <div key={k}>
                    <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>{l}</label>
                    <input className="input" value={editData[k]||''} onChange={e => setEditData(d=>({...d,[k]:e.target.value}))} style={{ marginTop:4 }} />
                  </div>
                ))}
                <div>
                  <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Specialty</label>
                  <select value={editData.specialty||'PT'} onChange={e => setEditData(d=>({...d,specialty:e.target.value}))} style={{ marginTop:4, width:"100%" }}>
                    {DISCIPLINES.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Contract End</label>
                  <input type="date" className="input" value={editData.contract_end||''} onChange={e => setEditData(d=>({...d,contract_end:e.target.value}))} style={{ marginTop:4 }} />
                </div>
              </div>
              <div style={{ marginTop:12 }}>
                <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Notes</label>
                <textarea className="input" rows={4} value={editData.notes||''} onChange={e => setEditData(d=>({...d,notes:e.target.value}))} style={{ marginTop:4, resize:"vertical" }} />
              </div>
              <div style={{ display:"flex", gap:8, marginTop:16, justifyContent:"flex-end" }}>
                <button className="nb" onClick={() => setEditMode(false)} style={{ background:"#f1f5f9", color:"#64748b", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600 }}>Cancel</button>
                <button className="nb" onClick={saveEdit} style={{ background:"#1d4ed8", color:"white", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600 }}>Save Changes</button>
              </div>
              <div style={{ marginTop:16, paddingTop:16, borderTop:"1px solid #fee2e2" }}>
                <button className="nb" onClick={() => {
                  if (window.confirm(`Delete ${c.name}? This cannot be undone.`)) {
                    deleteCandidate(c.id, c.name);
                  }
                }} style={{ background:"#fef2f2", color:"#ef4444", border:"1px solid #fecaca", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:600 }}>
                  🗑 Delete {c.name}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: TEMPLATES */}
      {tab === "templates" && (
        <div className="fade-in">
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:12 }}>
            {(templates||[]).map(tpl => (
              <div key={tpl.id} className="card card-hover" style={{ padding:16, cursor:"pointer" }} onClick={() => fillTemplate(tpl)}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:18 }}>{tpl.icon}</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{tpl.label}</div>
                    <div style={{ fontSize:11, color:"#94a3b8" }}>Best for: {tpl.stage}</div>
                  </div>
                </div>
                <div style={{ fontSize:12, color:"#64748b", lineHeight:1.5, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical" }}>
                  {tpl.body.substring(0, 120)}...
                </div>
              </div>
            ))}
          </div>

          {/* Template Modal */}
          {tplModal && (
            <div style={{ position:"fixed", inset:0, background:"#0008", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }} onClick={() => setTplModal(null)}>
              <div className="card" style={{ width:600, maxHeight:"80vh", overflow:"auto", padding:24 }} onClick={e => e.stopPropagation()}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                  <h3 style={{ fontSize:16, fontWeight:700 }}>{tplModal.icon} {tplModal.label}</h3>
                  <button className="nb" onClick={() => setTplModal(null)}><I n="x" s={18} c="#94a3b8"/></button>
                </div>
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:11, color:"#94a3b8", fontWeight:600 }}>To: {c.email}</label>
                </div>
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:11, color:"#94a3b8", fontWeight:600 }}>Subject</label>
                  <input className="input" value={tplModal.subject||''} readOnly style={{ marginTop:4, background:"#f8fafc" }} />
                </div>
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:11, color:"#94a3b8", fontWeight:600 }}>Body</label>
                  <textarea className="input" rows={12} value={tplBody} onChange={e => setTplBody(e.target.value)} style={{ marginTop:4, resize:"vertical", lineHeight:1.6 }} />
                </div>
                <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                  <button className="nb" onClick={() => { copyToClipboard(tplBody); }} style={{ background:"#f1f5f9", color:"#475569", borderRadius:8, padding:"9px 16px", fontSize:13, fontWeight:600, display:"flex", alignItems:"center", gap:5 }}>
                    <I n="copy" s={13} c="#475569"/> Copy
                  </button>
                  <button className="nb" onClick={async () => {
                    if (isSignedIn()) {
                      setSendingEmail(true);
                      try {
                        await sendEmail(c.email, tplModal.subject||'', tplBody);
                        addActivity(c.id, 'email', `Sent template: ${tplModal.label}`);
                        toast(`✓ Sent to ${c.email}`);
                      } catch (e) {
                        toast(`✗ Send failed: ${e.message}`);
                      } finally {
                        setSendingEmail(false);
                      }
                    } else {
                      const gmailUrl = `https://mail.google.com/mail/u/0/?view=cm&fs=1&to=${encodeURIComponent(c.email)}&su=${encodeURIComponent(tplModal.subject||'')}&body=${encodeURIComponent(tplBody)}`;
                      window.open(gmailUrl, '_blank');
                      addActivity(c.id, 'email', `Sent template: ${tplModal.label}`);
                      toast("Opened in Gmail");
                    }
                    setTplModal(null);
                  }} style={{ background:"#1d4ed8", color:"white", borderRadius:8, padding:"9px 16px", fontSize:13, fontWeight:600, display:"flex", alignItems:"center", gap:5 }}>
                    <I n="mail" s={13} c="white"/> {isSignedIn() ? "Send" : "Open in Gmail"}
                  </button>
                  <button className="nb" onClick={async () => {
                    if (isSignedIn()) {
                      setSendingEmail(true);
                      try {
                        await createDraft(c.email, tplModal.subject||'', tplBody);
                        addActivity(c.id, 'email', `Saved Gmail draft: ${tplModal.label}`);
                        toast("✓ Draft saved to Gmail");
                      } catch (e) {
                        toast(`✗ Draft failed: ${e.message}`);
                      } finally {
                        setSendingEmail(false);
                      }
                    } else {
                      const gmailUrl = `https://mail.google.com/mail/u/0/?view=cm&fs=1&to=${encodeURIComponent(c.email)}&su=${encodeURIComponent(tplModal.subject||'')}&body=${encodeURIComponent(tplBody)}`;
                      window.open(gmailUrl, '_blank');
                      addActivity(c.id, 'email', `Saved Gmail draft: ${tplModal.label}`);
                      toast("Opened in Gmail — save as draft or send");
                    }
                    setTplModal(null);
                  }} style={{ background:"#ea4335", color:"white", borderRadius:8, padding:"9px 16px", fontSize:13, fontWeight:600, display:"flex", alignItems:"center", gap:5 }}>
                    <I n="draft" s={13} c="white"/> {isSignedIn() ? "Save Draft" : "Open in Gmail"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: JOBS */}
      {tab === "jobs" && (
        <div className="fade-in">
          <div className="card" style={{ padding:16, marginBottom:12, background:"#eff6ff", border:"1px solid #bfdbfe" }}>
            <div style={{ fontSize:13, color:"#1d4ed8" }}>
              <strong>{matchedJobs.length}</strong> {c.specialty} jobs available. Click "Pitch" to use the Job Pitch template for this candidate.
            </div>
          </div>
          <div className="card" style={{ overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#f8fafc", borderBottom:"1px solid #e2e8f0" }}>
                  {["State","City","Setting","Pay","Start","Action"].map(h => (
                    <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", textTransform:"uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matchedJobs.map((j, i) => (
                  <tr key={i} style={{ borderBottom:"1px solid #f1f5f9" }}>
                    <td style={{ padding:"10px 14px", fontWeight:600 }}>{j.State}</td>
                    <td style={{ padding:"10px 14px" }}>{j.City}</td>
                    <td style={{ padding:"10px 14px" }}>{j.Setting}</td>
                    <td style={{ padding:"10px 14px", fontWeight:700, color:"#22c55e" }}>{j.Pay || j['After Tax'] || '—'}</td>
                    <td style={{ padding:"10px 14px", fontSize:12 }}>{j['Start Date'] || '—'}</td>
                    <td style={{ padding:"10px 14px" }}>
                      <button className="nb" onClick={() => fillTemplate((templates||[]).find(t=>t.id==='jobpitch')||(templates||[])[0]||{body:'',subject:'',label:'Pitch',icon:'🎯'}, j)} style={{ background:"#eff6ff", color:"#3b82f6", border:"1px solid #bfdbfe", borderRadius:7, padding:"5px 12px", fontSize:11, fontWeight:600 }}>
                        Pitch
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB: ACTIVITY */}
      {tab === "activity" && (
        <div className="fade-in">
          {/* Log new activity */}
          <div className="card" style={{ padding:16, marginBottom:16 }}>
            <div style={{ display:"flex", gap:8 }}>
              <select value={actType} onChange={e => setActType(e.target.value)} style={{ width:120 }}>
                {["call","email","sms","note","contract","submission"].map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>
                ))}
              </select>
              <input className="input" placeholder="Log activity..." value={actInput} onChange={e => setActInput(e.target.value)} onKeyDown={e => e.key==='Enter' && logActivity()} style={{ flex:1 }} />
              <button className="nb" onClick={logActivity} disabled={!actInput.trim()} style={{ background:actInput.trim()?"#1d4ed8":"#e2e8f0", color:actInput.trim()?"white":"#94a3b8", borderRadius:8, padding:"0 16px", fontSize:13, fontWeight:600 }}>Log</button>
            </div>
          </div>

          {/* Activity list */}
          <div className="card" style={{ overflow:"hidden" }}>
            {activity.filter(a => a.type !== 'email_sync').length === 0 ? (
              <div style={{ padding:"32px", textAlign:"center", color:"#94a3b8" }}>No activity logged yet</div>
            ) : activity.filter(a => a.type !== 'email_sync').map((a, i) => {
              const icons = { call:"📞", email:"📧", sms:"💬", note:"📝", contract:"📄", submission:"🚀", stage_change:"🔄" };
              return (
                <div key={a.id||i} style={{ padding:"12px 16px", borderBottom:"1px solid #f1f5f9" }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}>
                    <div style={{ fontSize:13, color:"#0f172a" }}>
                      <span>{icons[a.type]||"📌"}</span> {a.note}
                    </div>
                    <span style={{ fontSize:11, color:"#94a3b8", whiteSpace:"nowrap" }}>{fmtDate(a.date)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// ─── ADD CANDIDATE MODAL ──────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
function AddCandidateModal({ prefill = {}, onSave, onClose }) {
  const [data, setData] = useState({
    name: prefill.name || '',
    email: prefill.email || '',
    phone: prefill.phone || '',
    specialty: prefill.specialty || 'PT',
    stage: prefill.stage || 'New Lead',
    priority: prefill.priority || 'Warm',
    state: prefill.state || '',
    notes: prefill.notes || '',
    tags: prefill.tags || [],
  });
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  return (
    <div style={{ position:"fixed", inset:0, background:"#0008", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1001 }} onClick={onClose}>
      <div className="card" style={{ width:500, maxHeight:"80vh", overflow:"auto", padding:24 }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <h3 style={{ fontSize:16, fontWeight:700 }}>Add Candidate</h3>
          <button className="nb" onClick={onClose}><I n="x" s={18} c="#94a3b8"/></button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          {[["name","Name"],["email","Email"],["phone","Phone"],["state","State"]].map(([k,l]) => (
            <div key={k}>
              <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>{l}</label>
              <input className="input" value={data[k]} onChange={e => set(k, e.target.value)} style={{ marginTop:4 }} />
            </div>
          ))}
          <div>
            <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Specialty</label>
            <select value={data.specialty} onChange={e => set('specialty', e.target.value)} style={{ marginTop:4, width:"100%" }}>
              {["PT","OT","SLP","PTA","COTA","SLPA"].map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Stage</label>
            <select value={data.stage} onChange={e => set('stage', e.target.value)} style={{ marginTop:4, width:"100%" }}>
              {STAGES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Priority</label>
            <select value={data.priority} onChange={e => set('priority', e.target.value)} style={{ marginTop:4, width:"100%" }}>
              {Object.keys(PRIORITY_COLORS).map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginTop:12 }}>
          <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Notes</label>
          <textarea className="input" rows={3} value={data.notes} onChange={e => set('notes', e.target.value)} style={{ marginTop:4, resize:"vertical" }} />
        </div>
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
          <button className="nb" onClick={onClose} style={{ background:"#f1f5f9", color:"#64748b", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600 }}>Cancel</button>
          <button className="nb" onClick={() => onSave(data)} disabled={!data.name||!data.email} style={{ background:data.name&&data.email?"#1d4ed8":"#e2e8f0", color:data.name&&data.email?"white":"#94a3b8", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600 }}>Add Candidate</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── TEMPLATE EDITOR FORM ─────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
function TemplateEditorForm({ tpl, onSave, onClose }) {
  const [data, setData] = useState({
    id: tpl.id || null,
    label: tpl.label || '',
    icon: tpl.icon || '📧',
    stage: tpl.stage || '',
    subject: tpl.subject || '',
    body: tpl.body || '',
    sort_order: tpl.sort_order || 0,
  });
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
        <div>
          <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Label</label>
          <input className="input" value={data.label} onChange={e => set('label', e.target.value)} style={{ marginTop:4 }} placeholder="Template name" />
        </div>
        <div>
          <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Icon</label>
          <input className="input" value={data.icon} onChange={e => set('icon', e.target.value)} style={{ marginTop:4 }} placeholder="📧" />
        </div>
        <div style={{ gridColumn:"1 / -1" }}>
          <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Stage (optional)</label>
          <select value={data.stage} onChange={e => set('stage', e.target.value)} style={{ marginTop:4, width:"100%" }}>
            <option value="">— Any stage —</option>
            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom:12 }}>
        <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Subject</label>
        <input className="input" value={data.subject} onChange={e => set('subject', e.target.value)} style={{ marginTop:4 }} placeholder="Subject line... ({name}, {specialty}, {state})" />
        <div style={{ fontSize:10, color:"#94a3b8", marginTop:4 }}>Use {'{name}'}, {'{specialty}'}, {'{state}'} as merge fields</div>
      </div>
      <div style={{ marginBottom:16 }}>
        <label style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>Body</label>
        <textarea className="input" rows={12} value={data.body} onChange={e => set('body', e.target.value)} style={{ marginTop:4, resize:"vertical", lineHeight:1.6 }} placeholder="Email body..." />
      </div>
      <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
        <button className="nb" onClick={onClose} style={{ background:"#f1f5f9", color:"#64748b", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600 }}>Cancel</button>
        <button className="nb" onClick={() => onSave(data)} disabled={!data.label} style={{ background:data.label?"#1d4ed8":"#e2e8f0", color:data.label?"white":"#94a3b8", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600 }}>Save Template</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
function SettingsView({ templates, saveTemplate, delTemplate }) {
  const [editingTpl, setEditingTpl] = useState(null);

  return (
    <div className="fade-in">
      <h2 style={{ fontSize:22, fontWeight:800, color:"#0f172a", marginBottom:20 }}>Settings</h2>
      {/* CRM-LEADS-2026-10-16 §4: install the CRM as an app on this device */}
      <AppSettings><PushSettings api={pushApi} /></AppSettings>

      {/* Email Templates Management */}
      <div className="card" style={{ padding:20, marginBottom:20 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <h4 style={{ fontSize:14, fontWeight:700, color:"#0f172a" }}>📧 Email Templates</h4>
          <button className="nb" onClick={() => setEditingTpl({})} style={{ background:"#1d4ed8", color:"white", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:600 }}>＋ New Template</button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:10 }}>
          {(templates||[]).map(tpl => (
            <div key={tpl.id} style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:10, padding:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:18 }}>{tpl.icon||'📧'}</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{tpl.label}</div>
                    {tpl.stage && <div style={{ fontSize:11, color:"#94a3b8" }}>Stage: {tpl.stage}</div>}
                  </div>
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  <button className="nb" onClick={() => setEditingTpl(tpl)} style={{ background:"#eff6ff", color:"#3b82f6", border:"1px solid #bfdbfe", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:600 }}>Edit</button>
                  <button className="nb" onClick={() => { if (window.confirm(`Delete "${tpl.label}"? This cannot be undone.`)) delTemplate(tpl.id); }} style={{ background:"#fef2f2", color:"#ef4444", border:"1px solid #fecaca", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:600 }}>Delete</button>
                </div>
              </div>
              {tpl.subject && <div style={{ fontSize:11, color:"#64748b", fontStyle:"italic" }}>Subject: {tpl.subject.substring(0,60)}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Template editor modal */}
      {editingTpl !== null && (
        <div style={{ position:"fixed", inset:0, background:"#0008", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1001 }} onClick={() => setEditingTpl(null)}>
          <div className="card" style={{ width:580, maxHeight:"85vh", overflow:"auto", padding:24 }} onClick={e => e.stopPropagation()}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ fontSize:16, fontWeight:700 }}>{editingTpl.id ? 'Edit Template' : 'New Template'}</h3>
              <button className="nb" onClick={() => setEditingTpl(null)}><I n="x" s={18} c="#94a3b8"/></button>
            </div>
            <TemplateEditorForm tpl={editingTpl} onSave={async (data) => { await saveTemplate(data); setEditingTpl(null); }} onClose={() => setEditingTpl(null)} />
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap:16 }}>
        <div className="card" style={{ padding:20 }}>
          <h4 style={{ fontSize:14, fontWeight:700, color:"#0f172a", marginBottom:12 }}>📧 Email Integration</h4>
          <div style={{ fontSize:13, color:"#475569", lineHeight:1.6 }}>
            Gmail is connected via Claude chat sessions as <strong>mmichuda@protherapystaffing.com</strong>. Email sync, draft creation, and Formspree imports run through Claude conversations.
          </div>
          <div style={{ marginTop:12, background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:10 }}>
            <span style={{ fontSize:12, color:"#166534" }}>✓ Gmail connected · Sync via Claude chat</span>
          </div>
        </div>

        <div className="card" style={{ padding:20 }}>
          <h4 style={{ fontSize:14, fontWeight:700, color:"#0f172a", marginBottom:12 }}>📱 Twilio SMS</h4>
          {/* NAV-2026-10-22: was a "connect Twilio" placeholder -- texts have gone through the business line since 10-16 */}
          <div data-testid="settings-sms" style={{ fontSize:13, color:"#475569", lineHeight:1.6 }}>
            Texts go out on the business line: Text on a lead, a candidate or a Needs a human row. Every text, both ways, is on that person's timeline.
          </div>
          <div style={{ marginTop:12, background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:10 }}>
            <span style={{ fontSize:12, color:"#166534" }}>✓ Connected</span>
          </div>
        </div>

        <div className="card" style={{ padding:20 }}>
          <h4 style={{ fontSize:14, fontWeight:700, color:"#0f172a", marginBottom:12 }}>📊 Job Feed</h4>
          <div style={{ fontSize:13, color:"#475569", lineHeight:1.6 }}>
            Jobs auto-load from the published Google Sheet CSV on startup. The sheet is maintained by Marianne with daily updates.
          </div>
          <div style={{ marginTop:8, fontSize:11, color:"#94a3b8", wordBreak:"break-all" }}>
            Feed: Published CSV from staff sheet
          </div>
          <div style={{ marginTop:8, background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:10 }}>
            <span style={{ fontSize:12, color:"#166534" }}>✓ Auto-loading on startup</span>
          </div>
        </div>

        <div className="card" style={{ padding:20 }}>
          <h4 style={{ fontSize:14, fontWeight:700, color:"#0f172a", marginBottom:12 }}>🗄️ Database</h4>
          <div style={{ fontSize:13, color:"#475569", lineHeight:1.6 }}>
            Supabase PostgreSQL — all candidates, activity logs, messages, and settings persist in real-time.
          </div>
          <div style={{ marginTop:12, background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:10 }}>
            <span style={{ fontSize:12, color:"#166534" }}>✓ Connected · {new Date().toLocaleDateString()}</span>
          </div>
        </div>

        <div className="card" style={{ padding:20, gridColumn:"1 / -1" }}>
          <h4 style={{ fontSize:14, fontWeight:700, color:"#0f172a", marginBottom:12 }}>📋 Version Info</h4>
          <div style={{ fontSize:13, color:"#475569", lineHeight:1.8 }}>
            <strong>TherapyLink CRM v5.1</strong> · Pro Therapy Staffing<br/>
            Live at: crm.protherapystaffing.com<br/>
            Deploy: Netlify auto-deploy from GitHub main branch<br/>
            Supabase project: loqnsjyyctbqcaooomce
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ─── LEADS VIEW (public.leads — site forms + agent contact captures) ──────────
// ═══════════════════════════════════════════════════════════════════════════════
function fmtWhen(x) {
  if (!x) return "—";
  try { const d = new Date(x); return d.toLocaleString(undefined, { month:"short", day:"numeric", year:"numeric", hour:"numeric", minute:"2-digit" }); } catch { return String(x); }
}

// Tone -> pill colours for the decision log. Keyed off src/lib/leadDecisions.js
// so the vocabulary lives in one place and this is only its appearance.
const DECISION_TONES = {
  good:      { background:"#dcfce7", color:"#166534" },
  attention: { background:"#fef3c7", color:"#b45309" },
  bad:       { background:"#fee2e2", color:"#b91c1c" },
  neutral:   { background:"#f1f5f9", color:"#475569" },
};

function LeadsView({ kind: kindProp = null, onKind = () => {}, waitingFor = () => [], cancelQueued = () => {}, placements = [], openEmail = () => {}, emailOptedOut = () => false, emailCols = false, setNoEmail = async () => {}, facilityCols = false, saveFacility = async () => {}, callLead = () => '', liveTick = 0, setLeadQuery = () => {}, mergeCols = false, onAlertsChanged = () => {}, notifications = [], leadQuery = "", onHandBack = null, leads, allLeads = leads, testCols = false, showTests = false, setShowTests = () => {}, setLeads, spamCols, candidates, conversations, convMsgRefs, leadDecisions, addCandidate, toast, setView, setConvDeepLink, handledCols, drafts = [], draftHandlers = null, draftBusy = null, openDraft = () => {}, onMakeOffer = null, callRows = [], onCallLogged = () => {}, onFacilityTouched = async () => 0, assignRows = [], onAssigned = () => {}, bulkOn = true, profileKit = null, createCandidate = null, summaryKit = null, facilityWorkKit = null }) {
  const assignMap = useMemo(() => assigneesOf(assignRows), [assignRows]);
  const assigneeOf = (l) => ((assignMap.get(String(l.id).toLowerCase()) || {}).to || '');
  // Second touch (SECOND-TOUCH-2026-10-02): a lead with a draft shows it on its row; the
  // button opens the draft inline under the row, and "Open in Drafts" goes to the tab.
  // The newest row per lead: one LIVE draft per lead, but history rows may sit beside it (ruling 1b).
  const draftByLead = latestByLead(drafts);
  const [draftOpen, setDraftOpen] = useState(null);
  const [formType, setFormType] = useState("All");
  const [source, setSource] = useState("All");
  // ── Mark handled (leads.processed) — AFTERNOON-2026-10-01 B4 ──
  const [handledFilter, setHandledFilter] = useState(HANDLED_FILTERS[0]);
  // A6 (OVERNIGHT-MORE-2026-10-03): search by discipline, state, consent and draft status, and the ?lead=<id> deep
  // link the morning brief prints for every lead -- it focuses that one lead, past every other filter.
  const [search, setSearch] = useState(SEARCH_DEFAULTS);
  const [leadFocus, setLeadFocus] = useState(() => parseLeadParam(typeof window !== "undefined" ? window.location.search : ""));
  const setQ = (k) => (e) => setSearch((q) => ({ ...q, [k]: e.target.value }));
  const [selectedIds, setSelectedIds] = useState([]);   // ticked rows + pasted call-list ids
  const [handleOpen, setHandleOpen] = useState(null);   // the row whose "Mark handled" form is open
  const [handledBusy, setHandledBusy] = useState(false);
  const [handledUndo, setHandledUndo] = useState(null); // { ids, message } -- the last mark, undoable
  const handledApi = { mark: (ids, note) => db.markLeadsHandled(ids, note), unmark: (ids) => db.unmarkLeadsHandled(ids) };
  const doHandled = async (ids, handled, note) => {
    setHandledBusy(true);
    try {
      const res = await performHandled({ api: handledApi, rows: leads, ids, handled, note,
        setRows: setLeads, setUndo: setHandledUndo, notify: toast });
      if (res.ok) setHandleOpen(null);
      return res;
    } finally {
      setHandledBusy(false);
    }
  };
  const toggleSelected = (id) => setSelectedIds(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  const [onlyNeedsAttention, setOnlyNeedsAttention] = useState(true); // default view
  // FIX-AGENTS-2026-10-12 §4: "Spam (N)" lists ONLY the tagged rows (spam or duplicate); otherwise they stay out of the
  // working list. Nothing is deleted -- "Not spam" puts a row back.
  const [spamOnly, setSpamOnly] = useState(false);
  const [promote, setPromote] = useState(null);
  const [creating, setCreating] = useState(null);       // NIGHT-DIRECTSTAFF-2026-10-29 addendum: the lead id being made a candidate
  const doCreate = async (l) => {
    setCreating(l.id);
    try { const r = await createCandidate(l); toast(r.existed ? `Already a candidate: #${r.id} (same phone or email). The lead is linked to them.` : `Candidate #${r.id} created from this lead.`); }
    catch (e) { toast(e.message || "The candidate could not be made"); } finally { setCreating(null); }
  };
  // Which lead's decision log is open. One at a time: this is a detail read, not
  // a column, and a table with an expandable block on every row stops being one.
  const [openDecisions, setOpenDecisions] = useState(null);
  // The Text button (CRM-TEXT-2026-10-16): the lead whose compose box is open.
  const [textLead, setTextLead] = useState(null);
  // CRM-LEADS-2026-10-16 §6: Candidates / Facilities, discipline (candidates only), source, date range -- kept per device
  const filterStore = () => { try { return window.localStorage; } catch { return null; } };
  const [filtersRaw, setFiltersState] = useState(() => readLeadFilters(filterStore()));
  // NAV-2026-10-22: Leads and Facilities are nav items of their own -- the nav decides the kind; the other filters stay per device
  const filters = kindProp ? { ...filtersRaw, kind: kindProp, ...(kindProp === 'facilities' ? { discipline: F_ANY } : {}) } : filtersRaw;
  const setFilter = (k, v) => setFiltersState((f) => { const n = { ...f, [k]: v, ...(k === 'kind' && v === 'facilities' ? { discipline: F_ANY } : {}) }; writeLeadFilters(filterStore(), n); return n; });
  const resetFilters = () => { const n = { ...FILTER_DEFAULTS, kind: filters.kind }; writeLeadFilters(filterStore(), n); setFiltersState(n); };
  const kinds = kindCounts(leads.filter((l) => !isSpamTagged(l)));
  // CRM-LEADS-2026-10-16 §5: a push notification opens ?lead=<id>&compose=1 -> that lead's compose box, ready to type. Once.
  const composeRef = useRef(typeof window !== "undefined" && new URLSearchParams(window.location.search).get("compose") === "1");
  useEffect(() => {
    if (!composeRef.current || !leadFocus) return;
    const hit = allLeads.find((l) => String(l.id) === String(leadFocus));
    if (!hit) return;
    composeRef.current = false;
    setTextLead(hit);
  }, [allLeads, leadFocus]);

  // public.agent_lead_decisions, grouped by lead. READ-ONLY — the CRM never
  // writes to the worker's account of its own behaviour.
  const decisionIndex = decisionsByLead(leadDecisions);

  const formTypes = ["All", ...Array.from(new Set(leads.map(l => l.form_type).filter(Boolean))).sort()];
  const sources = ["All", ...Array.from(new Set(leads.map(l => l.source_site).filter(Boolean))).sort()];

  // §4: the one-lead view finds a test lead too (so "Not a test" is reachable); every list leaves tests out
  // §4 (10-19): ?lead=<a merged lead> opens the lead it was merged into -- the redirect -- with a line saying so
  const focusHit = leadFocus ? allLeads.find((l) => String(l.id).toLowerCase() === leadFocus) : null;
  const mergedFrom = focusHit && isMerged(focusHit) ? focusHit : null;
  const focusId = mergedFrom ? String(mergedFrom.merged_into).toLowerCase() : leadFocus;
  const dups = useMemo(() => dupIds(allLeads), [allLeads]);
  const [merges, setMerges] = useState([]);   // this session's merges, newest first -- what Undo reverses
  const doMerge = async (a, b) => {
    let by = "";
    try { const { data } = await supabase.auth.getUser(); by = (data && data.user && data.user.email) || ""; } catch { by = ""; }
    const done = await mergeLeads(supabase, a, b, by);
    setLeads((rs) => rs.map((r) => (r.id === done.older.id ? { ...r, ...done.fillPatch } : r.id === done.newer.id ? { ...r, ...done.mergePatch } : r)));
    setMerges((m) => [done, ...m]);
    return done;
  };
  const doUndo = async (done) => {
    await undoMerge(supabase, done);
    const blank = Object.fromEntries(done.filled.map((f) => [f, null]));
    setLeads((rs) => rs.map((r) => (r.id === done.newer.id ? { ...r, merged_into: null, merged_at: null, merged_by: null } : r.id === done.older.id ? { ...r, ...blank } : r)));
    setMerges((m) => m.filter((x) => x !== done));
  };
  // NIGHT-DIRECTSTAFF-2026-10-19 §5: the sort (part of a saved view) and the views themselves, per staff account
  const [sort, setSort] = useState("newest");
  const [uid, setUid] = useState(null);
  useEffect(() => { let live = true; supabase.auth.getUser().then(({ data }) => { if (live) setUid((data && data.user && data.user.id) || null); }).catch(() => {}); return () => { live = false; }; }, []);
  const viewsApi = useMemo(() => (uid ? makeViewsApi({ getSetting: (k) => db.getSetting(k), saveSettingChecked: (k, v) => db.saveSettingChecked(k, v), userId: uid }) : null), [uid]);
  // §12 (NIGHT-DIRECTSTAFF-2026-10-22): "Add to view" -- a saved view can be a fixed set of leads (its ids); applying it shows those
  const [pinnedIds, setPinnedIds] = useState(null);
  const [viewsKey, setViewsKey] = useState(0);
  const currentView = { ...snapshot({ filters, search, formType, source, handledFilter, onlyNeedsAttention, spamOnly, query: leadQuery, sort }), ...(pinnedIds ? { ids: pinnedIds } : {}) };
  const applyView = (state) => {
    setPinnedIds(Array.isArray(state.ids) && state.ids.length ? state.ids : null);
    const a = applied(state);
    writeLeadFilters(filterStore(), a.filters); setFiltersState(a.filters);
    if (kindProp && a.filters.kind && a.filters.kind !== kindProp) onKind(a.filters.kind);   // a saved view of the other kind opens under its nav item
    setSearch(a.search); setFormType(a.formType); setSource(a.source); setHandledFilter(a.handledFilter);
    setOnlyNeedsAttention(a.onlyNeedsAttention); setSpamOnly(a.spamOnly); setLeadQuery(a.query); setSort(a.sort); setSelectedIds([]);
  };
  // §6 (NIGHT-DIRECTSTAFF-2026-10-19): a ?lead= link inside the list opens the lead in place -- pushState, no reload -- so
  // Back (the phone's button, the browser's) returns to the same list: the same filters, search, sort and scroll position.
  const listScroll = useRef({ page: 0, box: 0 });   // at 360px the PAGE scrolls; on desktop .crm-content does
  const scroller = () => (typeof document !== "undefined" ? document.querySelector(".crm-content") : null);
  const openLead = (id) => {
    const el = scroller(); listScroll.current = { page: window.scrollY || 0, box: el ? el.scrollTop : 0 };
    window.history.pushState({ lead: id }, "", leadHref(id));
    setLeadFocus(String(id).toLowerCase());
    if (el) el.scrollTop = 0;
    window.scrollTo(0, 0);
  };
  useEffect(() => {
    const onPop = () => {
      const id = parseLeadParam(window.location.search);
      setLeadFocus(id);
      if (!id) requestAnimationFrame(() => requestAnimationFrame(() => { const el = scroller(); if (el) el.scrollTop = listScroll.current.box; window.scrollTo(0, listScroll.current.page); }));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const onLeadLinkClick = (e) => {
    const a = e.target && e.target.closest ? e.target.closest('a[href^="?lead="]') : null;
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    const id = parseLeadParam(a.getAttribute("href"));
    if (!id) return;
    e.preventDefault();
    openLead(id);
  };
  const rowsUnsorted = (leadFocus ? allLeads : leads).filter(l => {
    if (leadFocus) return String(l.id).toLowerCase() === focusId;
    if (!matchesSearch(l, search, draftByLead)) return false;
    if (!passesLeadFilters(l, filters)) return false;   // §6: type, discipline, source, dates
    if (!leadMatches(l, leadQuery)) return false;        // NIGHT-DIRECTSTAFF-2026-10-19 §1: the top search box, within the filters
    if (formType !== "All" && l.form_type !== formType) return false;
    if (source !== "All" && l.source_site !== source) return false;
    if (spamOnly !== isSpamTagged(l)) return false;
    // Same predicate as the nav badge above, and for the same reason: this toggle
    // defaults ON, so a row admitted here that nobody needs to action sits in the
    // default view forever with a Promote button beside it — and a row wrongly
    // excluded here is invisible in the view recruiters actually work from.
    // A handled lead needs nobody: needsAttention() excludes processed = true.
    if (onlyNeedsAttention && !needsAttention(l, !!findCandidateForLead(l, candidates, allLeads), isNeedsHuman)) return false;
    if (!passesHandledFilter(l, handledFilter)) return false;
    if (pinnedIds && !pinnedIds.includes(l.id)) return false;   // §12 (10-22): a saved selection (Add to view)
    return true;
  });
  const rows = sortRows(rowsUnsorted, sort);

  // public.leads is the table this view renders, so that is the table the mark is
  // written to. The data layer takes the table by NAME because the same column
  // exists on public.chat_leads and a chat-leads view would use the same call --
  // a mark on one table says nothing about the other, so they never share a call
  // that guesses which one it is on.
  const SPAM_TABLE = 'leads';
  const canMarkSpam = spamCols && spamCols[SPAM_TABLE] === true;
  const [spamBusy, setSpamBusy] = useState(null);

  // ONE list of columns, so the header, the cells and the expanded row's colSpan
  // cannot disagree.
  //
  // The Spam column comes and goes with the migration -- hidden entirely until the
  // column exists, rather than rendered as a row of em-dashes. A column of dashes
  // reads as "we have this data and it is empty for everyone", which is a claim; an
  // absent column claims nothing, which is the truth while the migration is unapplied.
  //
  // AND THE colSpAN IS DERIVED, not written down. It was a literal 9 while the Spam
  // column made the table 10 wide, so the decisions panel under-spanned by one from
  // the moment that column was added. A count kept in a second place is a count that
  // goes stale the first time the first place changes.
  // + a Select column first and a Handled column before Actions (withHandledColumns).
  const COLS = withHandledColumns(leadColumns(canMarkSpam));
  const visibleIds = rows.map(l => l.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));

  // §4: Mark as test / Not a test -- one lead, the write confirmed (zero rows updated is an error), folded into the list
  const [testBusy, setTestBusy] = useState(null);
  // DAY-DIRECTSTAFF-2026-10-28: a test mark closes the lead's open alerts (Not a test reopens them) -> how many; null when that write failed
  const testAlerts = async (lead, next) => {
    try {
      const r = await testAlertsWrite(supabase, testAlertIds(lead, { notifications, conversations }, next), next);
      if (r.ids.length) onAlertsChanged(r.ids, r.patch);
      return r.ids.length;
    } catch (e) { console.error("Test-mark alerts:", e); return null; }
  };
  const doTest = async (lead, next) => {
    setTestBusy(lead.id);
    try {
      let by = "";
      try { const { data } = await supabase.auth.getUser(); by = (data && data.user && data.user.email) || ""; } catch { by = ""; }
      const patch = await markLeadTest(supabase, lead.id, next, by);
      setLeads(rs => rs.map(r => (r.id === lead.id ? { ...r, ...patch } : r)));
      const closed = await testAlerts(lead, next);                                    // DAY-DIRECTSTAFF-2026-10-28: its open alerts too
      toast((next ? "Marked as a test: hidden from the lists and counts (Show tests brings it back)" : "Not a test: back in the lists")
        + (closed === null ? " (its alerts were not changed: try again)" : closed ? (next ? ` · ${closed} open alert${closed === 1 ? "" : "s"} closed` : ` · ${closed} alert${closed === 1 ? "" : "s"} reopened`) : ""));
    } catch (e) {
      console.error("Test mark error:", e);
      toast(e.message || "Could not update the test mark");
    } finally { setTestBusy(null); }
  };
  const doSpam = async (lead, next) => {
    setSpamBusy(lead.id);
    try {
      const patch = await db.setLeadSpam(SPAM_TABLE, lead.id, next, lead);
      // Fold the write into the list rather than refetching: the row is already on
      // screen, and a refetch would reorder and re-filter it out from under the
      // recruiter's cursor. applySpamMark returns a new array and mutates nothing.
      setLeads(rows => applySpamMark(rows, lead.id, patch));
      toast(next ? 'Marked as spam' : 'Not spam: back in the lead list');
    } catch (e) {
      console.error('Spam mark error:', e);
      // Say what failed. The one outcome a recruiter must never be left guessing
      // about is "I clicked it and nothing happened".
      toast(e.message || 'Could not update the spam mark');
    } finally {
      setSpamBusy(null);
    }
  };

  // §12 (10-22): the bulk marks, one lead at a time through the same writes as the single buttons; -> { done, failed }
  const bulkMark = async (kind, ids, next) => {
    let by = ""; try { const { data } = await supabase.auth.getUser(); by = (data && data.user && data.user.email) || ""; } catch { by = ""; }
    const done = [], failed = [];
    for (const id of ids) {
      const lead = allLeads.find((l) => l.id === id) || { id };
      try {
        if (kind === "test") { const patch = await markLeadTest(supabase, id, next, by); setLeads((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r))); await testAlerts(lead, next); }
        else { const patch = await db.setLeadSpam(SPAM_TABLE, id, next, lead); setLeads((rs) => applySpamMark(rs, id, patch)); }
        done.push(id);
      } catch (e) { failed.push({ id, error: e.message || "not saved" }); }
    }
    return { done, failed };
  };
  const addToView = async (name, ids) => {
    const list = await viewsApi.load();
    await viewsApi.save([...list, { id: newViewId(), name, state: { ...currentView, ids } }]);
    setViewsKey((k) => k + 1);
  };
  // §12 (10-22): Facilities -- select requests and change their status together (once the pipeline columns exist)
  const [facSel, setFacSel] = useState([]);
  const [facStatus, setFacStatus] = useState("called");
  const [facUndo, setFacUndo] = useState(null);   // [{ id, status }] -- what each was before
  const bulkFacility = async () => {
    const pick = rows.filter((l) => facSel.includes(l.id));
    if (!pick.length || !window.confirm(`Set ${pick.length} facility request${pick.length === 1 ? "" : "s"} to ${FACILITY_STATUSES[facStatus]}?`)) return;
    const before = pick.map((l) => ({ id: l.id, status: l.facility_status || "new" }));
    let ok = 0;
    for (const l of pick) { try { await saveFacility(l.id, { status: facStatus }); ok += 1; } catch (e) { /* said below */ } }
    setFacUndo(before); setFacSel([]);
    toast(ok === pick.length ? `${ok} set to ${FACILITY_STATUSES[facStatus]}` : `${ok} of ${pick.length} set; the rest were not saved`);
  };
  const undoFacility = async () => {
    if (!facUndo) return;
    for (const b of facUndo) { try { await saveFacility(b.id, { status: b.status }); } catch (e) { /* best effort, said */ } }
    setFacUndo(null); toast("Statuses put back");
  };

  const doPromote = async (data) => {
    const tags = [...(data.tags || []), "source:lead"];
    const link = "Promoted from lead" + (promote.form_type ? " (" + promote.form_type + ")" : "") + (promote.source_site ? " via " + promote.source_site : "") + " · lead_id " + promote.id;
    await addCandidate({ ...data, tags, notes: data.notes ? data.notes + "\n" + link : link });
    setPromote(null);
    toast("Promoted to candidate");
  };

  const th = { textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:.3, borderBottom:"1px solid #e2e8f0", whiteSpace:"nowrap" };
  const td = { padding:"10px 12px", fontSize:13, color:"#1e293b", borderBottom:"1px solid #f1f5f9", verticalAlign:"middle" };

  return (
    <div className="fade-in phone-tap" data-testid="leads-view" onClickCapture={onLeadLinkClick}>
      {/* DAY-DIRECTSTAFF-2026-11-06 §1: on one facility's page the list's title and filters step aside (they do not apply to one lead);
          "Show all leads" brings them back */}
      <div hidden={!!(leadFocus && rows[0] && isFacilityLead(rows[0]))} data-testid="leads-head" style={{ display: leadFocus && rows[0] && isFacilityLead(rows[0]) ? "none" : "flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:16 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:800, color:"#0f172a" }}>Leads</h2>
          <p style={{ fontSize:13, color:"#475569", marginTop:2 }}>public.leads — site forms &amp; agent contact captures. {rows.length} shown{onlyNeedsAttention ? " · needs attention" : ""}.</p>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          <button className="nb" data-testid="spam-filter" onClick={() => { const on = !spamOnly; setSpamOnly(on); if (on) setOnlyNeedsAttention(false); }} title="Rows the spam filter (or a person) tagged: spam or an identical repeat. Nothing is deleted."
            style={{ background:spamOnly?"#b91c1c":"#f1f5f9", color:spamOnly?"white":"#475569", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>
            Spam ({spamTaggedCount(leads)})
          </button>
          {testCols && (
            <button className="nb" data-testid="show-tests" aria-pressed={showTests} onClick={() => setShowTests(!showTests)}
              title="Leads marked as a test are left out of every list and count. Nothing is deleted."
              style={{ background:showTests?"#6d28d9":"#f1f5f9", color:showTests?"white":"#475569", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>
              {showTests ? "Hide tests" : "Show tests"} ({testCount(allLeads)})
            </button>
          )}
          <button className="nb" onClick={() => setOnlyNeedsAttention(v => !v)} style={{ background:onlyNeedsAttention?"#b45309":"#f1f5f9", color:onlyNeedsAttention?"white":"#475569", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>
            {onlyNeedsAttention ? "Needs attention" : "All leads"}
          </button>
          <select aria-label="Form type" value={formType} onChange={e => setFormType(e.target.value)}>{formTypes.map(f => <option key={f}>{f}</option>)}</select>
          <select aria-label="Source" value={source} onChange={e => setSource(e.target.value)}>{sources.map(s => <option key={s}>{s}</option>)}</select>
          {/* "Needs attention" already hides handled leads, so asking for Handled
              switches it off -- otherwise the answer would always be an empty table. */}
          <select aria-label="Handled" value={handledFilter} title="Filter by leads.processed (Mark handled)"
            onChange={e => { setHandledFilter(e.target.value); if (e.target.value === "Handled") setOnlyNeedsAttention(false); }}>
            {HANDLED_FILTERS.map(f => <option key={f}>{f}</option>)}
          </select>
          {/* §6: discipline is for candidates only -- a facility is never "PT" */}
          {filters.kind === "candidates" && (
            <select data-testid="search-discipline" aria-label="Discipline" value={filters.discipline} onChange={(e) => setFilter("discipline", e.target.value)} title="Discipline (candidates only)">
              <option value={F_ANY}>Any discipline</option>{LEAD_DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}</select>
          )}
          <select data-testid="lead-source-kind" aria-label="Source" value={filters.source} onChange={(e) => setFilter("source", e.target.value)} title="How they came in">
            <option value={F_ANY}>Any source</option>{Object.entries(SOURCES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          <label style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:12, color:"#475569" }}>From
            <input type="date" data-testid="lead-from" aria-label="Came in from" value={filters.from} max={filters.to || undefined} onChange={(e) => setFilter("from", e.target.value)} style={{ border:"1.5px solid #e2e8f0", borderRadius:9, padding:"6px 8px", fontSize:13 }} /></label>
          <label style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:12, color:"#475569" }}>To
            <input type="date" data-testid="lead-to" aria-label="Came in to" value={filters.to} min={filters.from || undefined} onChange={(e) => setFilter("to", e.target.value)} style={{ border:"1.5px solid #e2e8f0", borderRadius:9, padding:"6px 8px", fontSize:13 }} /></label>
          <select data-testid="lead-sort" aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value)} title="Sort the list (saved with a view)">
            {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          {filtersActive(filters) && (
            <button className="nb" data-testid="lead-filters-reset" onClick={resetFilters} style={{ fontSize:12, color:"#1d4ed8", background:"transparent" }}>Reset filters</button>
          )}
          <select data-testid="search-state" value={search.state} onChange={setQ("state")} title="State (Unknown = none on file)">{STATE_OPTIONS.map(o => <option key={o} value={o}>{o === "Any" ? "Any state" : o}</option>)}</select>
          <select data-testid="search-consent" value={search.consent} onChange={setQ("consent")} title="SMS consent">{CONSENT_OPTIONS.map(o => <option key={o} value={o}>{o === "Any" ? "Any consent" : o}</option>)}</select>
          <select data-testid="search-draft" value={search.draft} onChange={setQ("draft")} title="Second-touch draft status">{DRAFT_OPTIONS.map(o => <option key={o} value={o}>{o === "Any" ? "Any draft status" : o}</option>)}</select>
          {searchActive(search) && (
            <button className="nb" data-testid="search-clear" onClick={() => setSearch(SEARCH_DEFAULTS)} style={{ fontSize:12, color:"#1d4ed8", background:"transparent" }}>Clear search</button>
          )}
        </div>
      </div>
      {/* CRM-LEADS-2026-10-16 §6: Candidates / Facilities first */}
      {/* §5 (NIGHT-DIRECTSTAFF-2026-10-19): saved views -- chips; tap applies, … renames or deletes */}
      {/* kept mounted (hidden) while a lead is open, so coming Back does not reload it and shift the list */}
      <div hidden={!!leadFocus}><SavedViews key={viewsKey} current={currentView} onApply={applyView} api={viewsApi} toast={toast} sourceLabels={SOURCES} /></div>
      {pinnedIds && !leadFocus && (
        <div data-testid="pinned-view" className="card" style={{ padding: "8px 14px", marginBottom: 12, fontSize: 13, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span>Showing a saved selection: {pinnedIds.length} lead{pinnedIds.length === 1 ? "" : "s"}.</span>
          <button className="nb" data-testid="pinned-view-clear" onClick={() => setPinnedIds(null)} style={{ color: "#1d4ed8", background: "transparent", fontWeight: 700, minHeight: 44 }}>Show all</button>
        </div>
      )}
      {!kindProp && <div role="group" aria-label="Lead type" data-testid="lead-kind" style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
        {Object.entries(KINDS).map(([k, label]) => (
          <button key={k} className="nb" data-testid={`lead-kind-${k}`} aria-pressed={filters.kind === k} onClick={() => setFilter("kind", k)}
            style={{ borderRadius:20, padding:"8px 16px", fontSize:13, fontWeight:700, background: filters.kind === k ? "#0f2744" : "#f1f5f9", color: filters.kind === k ? "white" : "#334155" }}>
            {label} <span style={{ opacity:.8 }}>{kinds[k]}</span></button>
        ))}
      </div>}
      {/* DAY-DIRECTSTAFF-2026-11-06 §1: a facility's page opens on the facility (its working view first), the lead line under it */}
      {/* NIGHT-DIRECTSTAFF-2026-10-21 §1: a facility lead's detail -- everything it sent, status, owner, notes */}
      {leadFocus && rows[0] && isFacilityLead(rows[0]) && (
        <FacilityCard key={`fac-${rows[0].id}`} lead={rows[0]} full pipelineReady={facilityCols} onSave={saveFacility} onCall={callLead} toast={toast}
          interviewAlert={(cardId) => interviewAlertId(notifications, cardId)}
          onEmail={openEmail} optedOut={emailOptedOut(rows[0])}
          loadThread={async () => { const fc = findConversationForPhone(rows[0].phone, conversations); return fc ? db.getConversationMessages(fc.id) : null; }}
          onReply={textButtonState(rows[0]).enabled ? () => setTextLead(rows[0]) : null} threadKey={liveTick}
          candidates={candidates} matchApi={matchApi} portalApi={facilityPortalApi} sendApi={sendApi} onMakeOffer={onMakeOffer} summaryKit={summaryKit} workKit={facilityWorkKit} />
      )}
      {leadFocus && (
        <div data-testid="lead-focus" className="card" style={{ padding:"8px 14px", marginBottom:12, fontSize:13, display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          <span>Showing one lead: <code>{(focusId || leadFocus).slice(0, 8)}</code>{rows.length ? "" : " (not loaded here)"}</span>
          {mergedFrom && <span data-testid="lead-merged-from" style={{ flexBasis:"100%", color:"#713f12", fontWeight:600 }}>Lead <code>{String(mergedFrom.id).slice(0, 8)}</code> was merged into this one.</span>}
          <button className="nb" data-testid="lead-focus-clear" onClick={() => { if (window.history.state && window.history.state.lead) { window.history.back(); return; } setLeadFocus(null); if (typeof window !== "undefined" && window.history && window.history.replaceState) window.history.replaceState(null, "", window.location.pathname); }}
            style={{ fontSize:12, color:"#1d4ed8", background:"transparent" }}>Show all leads</button>
          {/* The detail: what they signed up for, and whether texting is allowed (NIGHT-DIRECTSTAFF-2026-10-17 §2). */}
          {rows[0] && (() => { const su = signedUpFor(rows[0]); const c = consentLine(rows[0]); return (
            <div data-testid="lead-detail-signup" style={{ flexBasis:"100%", display:"grid", gap:4, fontSize:14 }}>
              <div><strong>Signed up for:</strong> <span data-testid="lead-detail-signedup" title={su.title} style={{ overflowWrap:"anywhere" }}>{su.label}</span></div>
              <div data-testid="lead-detail-consent" style={{ fontWeight:600, color: c.ok ? "#166534" : "#b91c1c" }}>{c.ok ? "✓" : "✕"} {c.text}</div>
              {/* DAY-DIRECTSTAFF-2026-10-31 §1: the posting a job-page lead (chat or apply) came from -- its id, linked to the job page */}
              {(() => { const jb = jobOf(rows[0]); return jb ? (
                <div data-testid="lead-detail-job"><strong>Job:</strong> <a data-testid="lead-detail-job-link" href={jb.url} target="_blank" rel="noreferrer" style={{ color:"#1d4ed8", fontWeight:600, display:"inline-flex", alignItems:"center", minHeight:44 }}>{jb.id} ↗</a>{jb.title ? <span style={{ overflowWrap:"anywhere" }}> · {jb.title}</span> : null}</div>) : null; })()}
              {/* NIGHT-DIRECTSTAFF-2026-10-25 §2: a voicemail / missed call (the agents' path) not yet called back -- until a call is logged */}
              {(() => { const vr = voiceRows({ notifications, leads: allLeads }).find((r) => String(r.leadId) === String(rows[0].id)); return vr && !calledSince(callRows, rows[0].id, vr.at) ? (
                <div data-testid="lead-detail-calledus" style={{ color:"#7c2d12", fontWeight:700 }}>Called us {new Date(vr.at).toLocaleString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" })} — not yet called back</div>) : null; })()}
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}><LogCall testid="lead-logcall" target={{ kind: "lead", id: rows[0].id }} label={rows[0].name || ""} api={callsApi} toast={toast}
                quick={isFacilityLead(rows[0]) ? ['reached', 'voicemail'] : null}
                onLogged={(row) => { onCallLogged(row); if (isFacilityLead(rows[0])) onFacilityTouched(rows[0].id, 'Call logged in the CRM'); }} /></div>
              {/* EMAIL-CRM-2026-10-22: Email from Matt; "No email" said here, and Matt can set it (once the hub's column exists) */}
              {rows[0].email && (
                <div data-testid="lead-detail-email" style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                  {emailOptedOut(rows[0]) && <NoEmailNote testid="lead-detail-no-email" />}
                  <EmailButton testid="lead-detail-email-btn" email={rows[0].email} optedOut={emailOptedOut(rows[0])} onOpen={() => openEmail(rows[0])} />
                  {emailCols && (
                    <button className="nb" data-testid="lead-detail-no-email-toggle" onClick={() => setNoEmail(rows[0], !isOptedOut(rows[0]))}
                      style={{ background:"#fef2f2", color:"#991b1b", borderRadius:8, padding:"0 14px", minHeight:44, fontSize:13, fontWeight:700 }}>
                      {isOptedOut(rows[0]) ? "Allow email again" : "Mark No email"}</button>
                  )}
                </div>
              )}
              {/* §5: the agent stepped back on this lead's conversation -> say so, with the way back */}
              {onHandBack && (() => { const fc = findConversationForPhone(rows[0].phone, conversations); return fc && fc.human_takeover ? (
                <div data-testid="lead-detail-handback" style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:8, padding:"6px 10px" }}>
                  <span style={{ color:"#92400e", fontWeight:700 }}>You have this one.</span>
                  <button className="nb" data-testid="lead-detail-handback-btn" onClick={async () => { try { await onHandBack(fc.id); toast("Handed back to the agent"); } catch (e) { toast(e.message || "Could not hand back to the agent"); } }}
                    style={{ background:"#92400e", color:"white", borderRadius:8, padding:"0 14px", minHeight:44, fontSize:13, fontWeight:700 }}>Hand back to agent</button>
                </div>
              ) : null; })()}
              {testCols && (
                <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                  {/* 11-09 §1: test data by its address (lib/testMark.js testAddress) -- hidden without a mark, so no button to undo it */}
                  {rows[0].test_auto ? <span data-testid="lead-detail-test" style={{ color:"#5b21b6", fontWeight:600 }}>Test data: {rows[0].test_auto}, so hidden from the lists and counts automatically.</span>
                  : isTestMarked(rows[0]) && <span data-testid="lead-detail-test" style={{ color:"#5b21b6", fontWeight:600 }}>Marked as a test ({testLabel(rows[0]).replace("test · ", "")}): hidden from the lists and counts.</span>}
                  {!rows[0].test_auto && <button className="nb" data-testid="lead-detail-mark-test" disabled={testBusy === rows[0].id} onClick={() => doTest(rows[0], !isTestMarked(rows[0]))}
                    style={{ background:"#ede9fe", color:"#5b21b6", borderRadius:8, padding:"0 14px", minHeight:44, fontSize:13, fontWeight:700, opacity: testBusy === rows[0].id ? 0.5 : 1 }}>
                    {isTestMarked(rows[0]) ? "Not a test" : "Mark as test"}
                  </button>}
                </div>
              )}
            </div>
          ); })()}
        </div>
      )}
      {/* §4 (NIGHT-DIRECTSTAFF-2026-10-19): the other leads that look like the same person, and the merge */}
      {leadFocus && rows[0] && (
        <DuplicateStrip key={`dup-${rows[0].id}`} lead={rows[0]} allLeads={allLeads} canMerge={mergeCols} merge={doMerge} undo={doUndo} toast={toast}
          candidate={candidateMatch(rows[0], cachedIndex(candidates, allLeads))}
          undoable={merges.filter((d) => d.older.id === rows[0].id || d.newer.id === rows[0].id)} />
      )}
      {/* NIGHT-DIRECTSTAFF-2026-10-25d §3: the whole thread, with the CRM's lines inline; Take over (the Text box) / Hand back */}
      {leadFocus && rows[0] && (() => { const fc = findConversationForPhone(rows[0].phone, conversations); const lid = rows[0].id; return (
        <ThreadView key={`th-${lid}`} testid="lead-thread" conversation={fc || null} load={(id) => db.getConversationMessages(id)}
          decisions={(leadDecisions || []).filter((d) => String(d.lead_id) === String(lid))}
          loadExtra={async () => { const { data, error } = await supabase.from("activity_log").select("id,type,note,date,created_at")
            .in("type", ["reengage", "voicemail", "missed_call", "summary", "call_log", "call_followup"]).like("note", `%lead_id=${lid}%`).limit(500); return error ? [] : (data || []); }}
          onTakeOver={textButtonState(rows[0]).enabled ? () => setTextLead(rows[0]) : null} onHandBack={onHandBack} toast={toast}
          suggestion={profileKit && fc && interestedHandoff(notifications, fc.id) ? (() => { const pc = candidateForLead(rows[0], candidates); return (
            <ProfileSuggestion key={`ps-${lid}`} candidate={pc} row={pc ? profileKit.rowOf(pc.id) : null} link={pc ? profileKit.linkOf(pc.id) : null}
              kit={{ ...profileKit, textConsent: textConsentOf(pc || {}, [rows[0]]) }} toast={toast} />); })() : null} />); })()}
      {/* §2 (NIGHT-DIRECTSTAFF-2026-10-19): the person's whole timeline under the one-lead card */}
      {leadFocus && rows[0] && (
        <Timeline key={`tl-${rows[0].id}`} idPrefix="lead" reloadKey={`${liveTick}-${callRows.length}`} load={() => { const cand = findCandidateForLead(rows[0], candidates, allLeads); return loadTimeline({ supabase, lead: rows[0], candidate: cand, allLeads, conversations, notifications, candidates, placements: cand ? placements.filter((p) => p.candidate_id === cand.id) : [] }); }} />
      )}

      <HandledUndoBanner
        action={handledUndo}
        busy={handledBusy}
        onUndo={async (ids) => { const res = await doHandled(ids, false); if (res.ok) setHandledUndo(null); }}
        onDismiss={() => setHandledUndo(null)}
      />
      {/* §12 (NIGHT-DIRECTSTAFF-2026-10-22): Mark as test / Mark as spam / Add to view, with a confirm naming the count and Undo */}
      {/* NIGHT-DIRECTSTAFF-2026-10-25d §2: the bulk actions are owner-only (a recruiter selects and texts one at a time) */}
      {bulkOn && <BulkMarks selected={rows.filter((l) => selectedIds.includes(l.id))} testOn={!!testCols} spamOn={!!spamCols} isTest={isTestMarked} isSpam={isSpamMarked}
        mark={bulkMark} addToView={viewsApi ? addToView : null} toast={toast}
        assign={async (ids, to) => { const r = await assignApi.assign(ids, to); onAssigned(); return r; }} assigneeOf={assigneeOf}
        names={[...new Set([...assignMap.values()].map((a) => a.to))]} logBulk={(what, ids) => assignApi.logBulk(what, ids)} />}
      {bulkOn && <HandledBulkBar
        leads={leads}
        selectedIds={selectedIds}
        onSelectedChange={setSelectedIds}
        noteEnabled={handledCols === true}
        busy={handledBusy}
        onMark={async (ids, note) => { const r = await doHandled(ids, true, note); if (r && r.ok) await assignApi.logBulk(note ? "marked handled with a note" : "marked handled", ids); return r; }}
      />}
      {/* CRM-LEADS-2026-10-16 §7: the selected leads on the filtered list -- copy, CSV, or one text each (25 at a time) */}
      {bulkOn && <GatherBar selected={rows.filter((l) => selectedIds.includes(l.id))} send={(id, body, anyway) => staff.textLead(id, body, anyway)} toast={toast}
        onDeselect={(ids) => setSelectedIds((s) => s.filter((x) => !ids.includes(x)))} />}

      {/* NIGHT-DIRECTSTAFF-2026-10-25c §4: the portal's problem reports, above the facility requests */}
      {filters.kind === "facilities" && !leadFocus && <FacilityProblems rows={problemReports(notifications)} />}
      {/* NIGHT-DIRECTSTAFF-2026-10-21 §1: Facilities is a real list -- one card per request, everything it sent */}
      {filters.kind === "facilities" && !leadFocus && (
        <section data-testid="facility-list">
          {rows.length === 0 && <div className="card" style={{ padding:24, textAlign:"center", color:"#475569" }}>No facility requests match.</div>}
          {facilityCols && (facSel.length > 0 || facUndo) && (
            <div data-testid="fac-bulk" className="card" style={{ padding: "8px 14px", marginBottom: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
              {facSel.length > 0 && <>
                <span>{facSel.length} selected: set status to</span>
                <select data-testid="fac-bulk-status" aria-label="New status" value={facStatus} onChange={(e) => setFacStatus(e.target.value)} style={{ minHeight: 44 }}>
                  {Object.entries(FACILITY_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <button className="nb" data-testid="fac-bulk-apply" onClick={bulkFacility} style={{ background: "#1d4ed8", color: "white", borderRadius: 8, padding: "0 14px", minHeight: 44, fontWeight: 700 }}>Apply</button>
              </>}
              {facUndo && <button className="nb" data-testid="fac-bulk-undo" onClick={undoFacility} style={{ background: "#0f172a", color: "white", borderRadius: 8, padding: "0 14px", minHeight: 44, fontWeight: 700 }}>Undo the last status change</button>}
            </div>
          )}
          {[...rows].sort(facilityNewFirst).map((l) => <FacilityCard key={`fac-${l.id}`} lead={l} pipelineReady={facilityCols}
            selectable={facilityCols} selected={facSel.includes(l.id)} onToggle={() => setFacSel((s) => (s.includes(l.id) ? s.filter((x) => x !== l.id) : [...s, l.id]))} onSave={saveFacility} onCall={callLead} toast={toast}
            onEmail={openEmail} optedOut={emailOptedOut(l)} />)}
        </section>
      )}
      {filters.kind === "facilities" && !leadFocus ? null : leads.length === 0 ? (
        <div className="card" style={{ padding:32, textAlign:"center", color:"#94a3b8", fontSize:14 }}>
          No leads visible. If leads exist but this is empty, <b>public.leads</b> likely needs an <code>authenticated</code> SELECT RLS policy (see deploy notes).
        </div>
      ) : (
        <div className="card" style={{ overflow:"auto" }}>
          <table className="lead-table" style={{ width:"100%", borderCollapse:"collapse", minWidth:900 }}>
            <thead><tr>
              {COLS.map(h => h === "Select" ? (
                <th key={h} style={{ ...th, width:28 }}>
                  <input type="checkbox" aria-label="Select all shown leads" checked={allVisibleSelected}
                    onChange={() => setSelectedIds(s => allVisibleSelected
                      ? s.filter(id => !visibleIds.includes(id))
                      : [...new Set([...s, ...visibleIds])])} />
                </th>
              ) : <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map(l => {
                const match = findCandidateForLead(l, candidates, allLeads);
                const conv = findConversationForPhone(l.phone, conversations);
                const decs = decisionsFor(decisionIndex, l.id);
                const isOpen = openDecisions === l.id;
                return (
                  <Fragment key={l.id}>
                  <tr className="card-hover">
                    <td style={td}>
                      <input type="checkbox" aria-label="Select lead" checked={selectedIds.includes(l.id)} onChange={() => toggleSelected(l.id)} />
                    </td>
                    <td data-label="Created" style={{ ...td, whiteSpace:"nowrap", color:"#475569" }}>{fmtWhen(l.created_at)} <a data-testid="lead-link" className="tap-link" href={leadHref(l.id)} title="Link to this lead" aria-label="Link to this lead" style={{ color:"#1d4ed8", textDecoration:"underline" }}>↗</a></td>
                    <td data-label="Name" style={{ ...td, fontWeight:600 }}>{assigneeOf(l) && <span data-testid={`lead-assignee-${String(l.id).slice(0, 8)}`} style={{ display:"inline-block", marginRight:6, padding:"1px 8px", borderRadius:10, background:"#ecfeff", color:"#0e7490", fontSize:11.5, fontWeight:700 }}>{assigneeOf(l)}</span>}{l.name
                      ? l.name
                      : l.email
                        // Facility (and pre-fix) leads can land with a blank name; fall back to
                        // the email so the row is identifiable instead of a bare dash.
                        ? <span style={{ fontWeight:500, fontStyle:"italic", color:"#475569" }} title="No name provided — showing email">{l.email}</span>
                        : "—"}{dups.has(l.id) && <a className="pill" data-testid={"lead-dup-" + String(l.id).slice(0, 8)} href={leadHref(l.id)} title="Another lead has the same phone or email: open it to compare or merge" style={{ marginLeft:6, background:"#fef9c3", color:"#713f12", textDecoration:"none" }}>same person?</a>}{isTestMarked(l) && <span className="pill" data-testid={"lead-test-" + String(l.id).slice(0, 8)} title={"Marked as a test" + (l.test_marked_by ? " by " + l.test_marked_by : "")} style={{ marginLeft:6, background:"#ede9fe", color:"#5b21b6" }}>{testLabel(l)}</span>}</td>
                    <td data-label="Email" style={td}>{l.email || "—"}</td>
                    <td data-label="Phone" style={{ ...td, whiteSpace:"nowrap" }}>{telHref(l.phone) ? <a data-testid="lead-phone-link" href={telHref(l.phone)} style={{ color:"#1d4ed8", display:"inline-flex", alignItems:"center", minHeight:44 }}>{l.phone}</a> : (l.phone || "—")}</td>
                    <td data-label="Form" style={td}>{(() => { const pill = formTypePillFor(l); return (
                      <span className="pill" style={pill.style} title={pill.title}>{pill.label}</span>
                    ); })()}</td>
                    {/* What they signed up for, in words (NIGHT-DIRECTSTAFF-2026-10-17 §2). */}
                    <td data-label="Source" style={{ ...td, color:"#475569" }}>{(() => { const su = signedUpFor(l); return <span data-testid={"lead-signup-" + String(l.id).slice(0, 8)} title={su.title}>{su.label}</span>; })()}</td>
                    <td data-label="SMS consent" style={td}>{l.sms_consent
                      ? <span className="pill" style={{ background:"#dcfce7", color:"#166534" }}>✓ consent</span>
                      : <span className="pill" style={{ background:"#f1f5f9", color:"#475569" }}>none</span>}</td>
                    <td data-label="Opt-out" style={td}>{l.sms_opt_out ? <span className="pill" style={{ background:"#fee2e2", color:"#b91c1c" }}>opted out</span> : "—"}</td>
                    {/* Gated with the header above, never independently -- a cell without
                        its header (or the reverse) shifts every column after it.
                        The TIME, not a tick. What the welcome job's 24-hour delay does
                        with this row depends on when the mark landed relative to the
                        append, so the moment is the useful fact. */}
                    {canMarkSpam && (
                      <td data-label="Spam" style={td}>{isSpamMarked(l)
                        ? <span className="pill" style={{ background:"#fee2e2", color:"#b91c1c" }} title={"Marked as spam " + fmtWhen(l.spam_marked_at)}>spam · {spamTagLabel(l)} · {fmtWhen(l.spam_marked_at)}</span>
                        : isSpamTagged(l)
                          ? <span className="pill" style={{ background:"#fef3c7", color:"#92400e" }} title="An identical repeat of an earlier lead (the first copy reached staff)">duplicate</span>
                          : "—"}</td>
                    )}
                    <td data-label="Handled" style={{ ...td, whiteSpace:"nowrap" }}><HandledCell lead={l} /></td>
                    <td className="lead-actions" style={{ ...td, whiteSpace:"nowrap" }}>
                      {match ? (<>
                        <span className="pill" style={{ background:"#dcfce7", color:"#166534" }} title="Already a candidate">✓ {match.name || "Candidate"}</span>
                        {/* DAY-DIRECTSTAFF-2026-11-01 §1: the same phone or email, not yet linked -> one tap writes the lead_id line (candidate_from_lead) */}
                        {createCandidate && canCreateFromLead(l) && !leadLinked(match, l.id) && <button className="nb" data-testid={`lead-link-cand-${String(l.id).slice(0, 8)}`} disabled={creating === l.id} onClick={() => doCreate(l)}
                          title="Link this lead to that candidate (the same phone or email): one line on them, never a second candidate"
                          style={{ background:"#f1f5f9", color:"#1d4ed8", borderRadius:7, padding:"6px 11px", fontSize:12, fontWeight:600, marginLeft:4, opacity: creating === l.id ? 0.6 : 1 }}>{creating === l.id ? "Linking…" : "Link"}</button>}
                      </>) : (
                        createCandidate && canCreateFromLead(l)
                          ? <button className="nb" data-testid={`lead-create-cand-${String(l.id).slice(0, 8)}`} disabled={creating === l.id} onClick={() => doCreate(l)}
                              title="One tap: the candidate for this lead -- the existing one with the same phone or email, else a new one (never a duplicate)"
                              style={{ background:"#1d4ed8", color:"white", borderRadius:7, padding:"6px 11px", fontSize:12, fontWeight:600, opacity: creating === l.id ? 0.6 : 1 }}>{creating === l.id ? "Creating…" : "Create candidate"}</button>
                          : <button className="nb" onClick={() => setPromote(l)} style={{ background:"#1d4ed8", color:"white", borderRadius:7, padding:"6px 11px", fontSize:12, fontWeight:600 }}>Promote</button>
                      )}
                      {/* EVERY lead, whatever its source or form (CRM-TEXT-2026-10-16). */}
                      <TextButton lead={l} onOpen={setTextLead} />
                      {/* EMAIL-CRM-2026-10-22: Email from Matt through crm-mailer; "No email" when they opted out or bounced */}
                      <EmailButton testid="lead-email-btn" email={l.email} optedOut={emailOptedOut(l)} onOpen={() => openEmail(l)}
                        style={{ marginLeft:6, borderRadius:7, padding:"6px 11px", fontSize:12, fontWeight:600, border:"none",
                          background: l.email && !emailOptedOut(l) ? "#1d4ed8" : "#f1f5f9", color: l.email && !emailOptedOut(l) ? "white" : "#64748b",
                          cursor: l.email && !emailOptedOut(l) ? "pointer" : "not-allowed" }} />
                      {/* Mark handled opens a one-line form under the row; a handled lead
                          offers Undo directly (it restores processed = false and clears
                          the time and note). */}
                      {isHandled(l) ? (
                        <button className="nb" disabled={handledBusy} title="Set this lead back to unhandled (processed = false); it returns to the morning brief's lists."
                          onClick={() => doHandled([l.id], false)}
                          style={{ marginLeft:6, background:"#dcfce7", color:"#166534", borderRadius:7, padding:"6px 11px", fontSize:12, fontWeight:600, opacity: handledBusy ? 0.5 : 1 }}>
                          Undo handled
                        </button>
                      ) : (
                        <button className="nb" disabled={handledBusy} title="Mark this lead handled (processed = true). Nothing is sent; it drops off the morning brief."
                          onClick={() => setHandleOpen(v => (v === l.id ? null : l.id))}
                          style={{ marginLeft:6, background:"#f1f5f9", color:"#166534", borderRadius:7, padding:"6px 11px", fontSize:12, fontWeight:600 }}>
                          Mark handled
                        </button>
                      )}
                      {/* HIDDEN, not disabled, when the column is absent. A greyed-out
                          button invites a recruiter to wonder what is wrong with their
                          permissions; an absent one is simply a feature not turned on
                          yet. */}
                      {canMarkSpam && (() => { const b = spamButtonFor(l); return (
                        <button
                          className="nb"
                          disabled={spamBusy === l.id}
                          title={b.title}
                          onClick={() => doSpam(l, b.next)}
                          style={{ marginLeft:6, background: b.next ? "#f1f5f9" : "#fee2e2",
                                   color: b.next ? "#475569" : "#b91c1c", borderRadius:7,
                                   padding:"6px 11px", fontSize:12, fontWeight:600,
                                   opacity: spamBusy === l.id ? 0.5 : 1 }}>
                          {b.label}
                        </button>
                      ); })()}
                      {testCols && (
                        <button className="nb" data-testid={"lead-mark-test-" + String(l.id).slice(0, 8)} disabled={testBusy === l.id} onClick={() => doTest(l, !isTestMarked(l))}
                          title={isTestMarked(l) ? "Put it back in the lists and counts" : "Your own test submission: hide it from the lists and counts. Nothing is deleted; the agent's notifications skip it."}
                          style={{ marginLeft:6, background:"#ede9fe", color:"#5b21b6", borderRadius:7, padding:"6px 11px", fontSize:12, fontWeight:600, opacity: testBusy === l.id ? 0.5 : 1 }}>
                          {isTestMarked(l) ? "Not a test" : "Mark as test"}
                        </button>
                      )}
                      {conv && (
                        <button className="nb" onClick={() => { setConvDeepLink(l.phone); setView("conversations"); }} style={{ marginLeft:6, background:"#f1f5f9", color:"#4338ca", borderRadius:7, padding:"6px 11px", fontSize:12, fontWeight:600 }}>View conversation</button>
                      )}
                      {/* Same definition as the Conversations list and the worker's sweep:
                          the candidate spoke last and nothing on our side came after. */}
                      {conv && (() => {
                        const label = noReplyLabel(convMsgRefs, conv.id);
                        if (!label) return null;
                        const late = isOverdue(waitingMinutes(convMsgRefs, conv.id));
                        return <span className="pill" title="The candidate spoke last and nobody has replied." style={{ marginLeft:6, whiteSpace:"nowrap", ...(late ? { background:"#fee2e2", color:"#b91c1c", fontWeight:700 } : { background:"#fef3c7", color:"#92400e" }) }}>{label}</span>;
                      })()}
                      <LeadDraftButton draft={draftByLead[l.id]} onOpen={() => setDraftOpen((v) => (v === l.id ? null : l.id))} />
                      {/* An EMPTY decision log is the interesting state, not a
                          reason to hide the control: a lead nothing considered
                          was never seen by the webhook or the sweeper, and that
                          is the diagnosis the table exists to make possible. So
                          this button is always present, and says which it is. */}
                      <button
                        className="nb"
                        onClick={() => setOpenDecisions(v => (v === l.id ? null : l.id))}
                        title="What the agent decided about this lead (read-only)"
                        style={{ marginLeft:6, background: decs.length ? "#f1f5f9" : "#fff7ed", color: decs.length ? "#475569" : "#c2410c", borderRadius:7, padding:"6px 11px", fontSize:12, fontWeight:600 }}
                      >
                        {isOpen ? "Hide" : decisionsSummary(decs)}
                      </button>
                    </td>
                  </tr>
                  {draftOpen === l.id && draftByLead[l.id] && draftHandlers && (
                    <tr key={l.id + "-draft"}>
                      <td colSpan={COLS.length} style={{ padding:"0 12px", borderBottom:"1px solid #f1f5f9", background:"#f8fafc" }}>
                        <DraftPanel key={draftByLead[l.id].id + draftByLead[l.id].status + draftByLead[l.id].body} draft={draftByLead[l.id]} lead={l} busy={draftBusy === draftByLead[l.id].id} {...draftHandlers} />
                        <button className="nb" onClick={() => openDraft(draftByLead[l.id].id)} style={{ fontSize:12, color:"#1d4ed8", background:"transparent", marginBottom:8 }}>Open in Drafts</button>
                      </td>
                    </tr>
                  )}
                  {handleOpen === l.id && !isHandled(l) && (
                    <tr key={l.id + "-handled"}>
                      <td colSpan={COLS.length} style={{ padding:"0 12px", borderBottom:"1px solid #f1f5f9", background:"#f8fafc" }}>
                        <HandledRowForm lead={l} noteEnabled={handledCols === true} busy={handledBusy}
                          onConfirm={(id, note) => doHandled([id], true, note)}
                          onCancel={() => setHandleOpen(null)} />
                      </td>
                    </tr>
                  )}
                  {isOpen && (
                    <tr key={l.id + "-decisions"}>
                      <td colSpan={COLS.length} style={{ padding:"0 12px 14px", borderBottom:"1px solid #f1f5f9", background:"#f8fafc" }}>
                        <div style={{ fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:.3, padding:"10px 0 6px" }}>
                          agent_lead_decisions — read-only
                        </div>
                        {decs.length === 0 ? (
                          <div style={{ fontSize:13, color:"#c2410c" }}>
                            Nothing considered this lead. Neither the lead webhook nor the
                            sweeper recorded a decision about it, which usually means the
                            Supabase webhook call never arrived — not that the agent chose
                            to stay quiet.
                          </div>
                        ) : (
                          <table style={{ width:"100%", borderCollapse:"collapse" }}>
                            <thead><tr>
                              {["When","Source","Decision","Reason"].map(h => (
                                <th key={h} style={{ textAlign:"left", padding:"6px 10px", fontSize:10.5, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
                              ))}
                            </tr></thead>
                            <tbody>
                              {decs.map(d => {
                                const v = decisionDisplay(d);
                                const tone = DECISION_TONES[v.tone] || DECISION_TONES.neutral;
                                return (
                                  <tr key={d.id}>
                                    <td style={{ padding:"6px 10px", fontSize:12, color:"#64748b", whiteSpace:"nowrap" }}>{v.at ? fmtWhen(v.at) : "—"}</td>
                                    <td style={{ padding:"6px 10px", fontSize:12, color:"#64748b" }}>{v.source || "—"}</td>
                                    <td style={{ padding:"6px 10px" }}><span className="pill" style={tone}>{v.label}</span></td>
                                    <td style={{ padding:"6px 10px", fontSize:12, color:"#475569" }}>{v.reason || "—"}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {promote && (
        <AddCandidateModal
          prefill={{ name: promote.name || "", email: promote.email || "", phone: promote.phone || "", stage: "New Lead", priority: "Warm", tags: ["source:lead"] }}
          onSave={doPromote}
          onClose={() => setPromote(null)}
        />
      )}

      {textLead && (
        <TextCompose
          lead={textLead} focusBody waiting={waitingFor(textLead.id)} onCancelWaiting={cancelQueued}
          loadThread={async () => { const c = findConversationForPhone(textLead.phone, conversations); return c ? db.getConversationMessages(c.id) : []; }}
          loadTemplates={async () => parseTemplates(await db.getSetting(SMS_TEMPLATES_KEY))}
          saveTemplates={(list) => db.saveSettingChecked(SMS_TEMPLATES_KEY, serializeTemplates(list))}
          sendText={textOrQueue}
          onSent={(r) => toast(textToast(r))}
          onClose={() => setTextLead(null)}
          conversationId={(findConversationForPhone(textLead.phone, conversations) || {}).id || null}
          takeover={!!(findConversationForPhone(textLead.phone, conversations) || {}).human_takeover}
          onHandBack={async (id) => { await staff.release(id); }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── CONVERSATIONS VIEW (agent_conversations / agent_messages) — read-only ────
// ═══════════════════════════════════════════════════════════════════════════════
function ConversationsView({ conversations, leads = [], convMsgRefs, notifications, deepLinkPhone,
                            clearDeepLink, deepLinkId, clearDeepLinkId, onRefresh, refreshing, loadedAt }) {
  const [open, setOpen] = useState(null);        // selected conversation
  const [msgs, setMsgs] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  // ── Staff reply state ──────────────────────────────────────────────────────
  // `takeover` is tracked locally because the conversations array is loaded once at
  // app start; replying/releasing must update the banner immediately without a reload.
  const [takeover, setTakeover] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [staffErr, setStaffErr] = useState("");
  const [releasing, setReleasing] = useState(false);
  const [markingOffline, setMarkingOffline] = useState(false);
  // The control's OWN error line, drawn under the header on EVERY channel. It used to
  // share staffErr, which renders only inside the SMS-only reply card -- so on a chat
  // thread a failed "Handled off-system" reset the button and said nothing at all.
  const [offlineErr, setOfflineErr] = useState("");
  // "Copy review request" (2026-09-28): copies ONE fixed, neutral message for the
  // recruiter to send personally. Sends nothing. Wording + links: lib/reviewRequest.js.
  const [reviewCopied, setReviewCopied] = useState("");
  // Bulk "Handled off-system" (2026-09-28): only in the "Waiting on us" view, only on
  // threads that are waiting on us. Same worker call as the single button, once per
  // thread; sends nothing. Logic + tests: lib/bulkHandledOffline.js.
  const [picked, setPicked] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState("");
  const [bulkMsg, setBulkMsg] = useState("");

  // ── Filters ────────────────────────────────────────────────────────────────
  // Default to showing everything. The audit that prompted these found NO filter
  // hiding rows here — the view mapped straight over what it was handed — so the
  // point of adding filters is not to narrow by default, it is to make "you are
  // seeing everything" a stated fact rather than an assumption.
  const [filters, setFilters] = useState(NO_FILTERS);

  // Message count AND last activity per conversation. Not from last_message_at:
  // that column is written by nothing and equals started_at forever.
  const activity = activityByConversation(convMsgRefs);

  // ESCALATION-LOOP-SPEC E. Default the view to "waiting on us" WHEN ANY THREAD
  // QUALIFIES -- and only once, on the first load that has message refs to judge
  // from. Defaulting unconditionally would open on an empty list on a quiet day
  // and read as "the CRM is broken"; defaulting on every render would fight the
  // recruiter every time they cleared the filter.
  const defaultedRef = useRef(false);
  useEffect(() => {
    if (defaultedRef.current) return;
    if (!convMsgRefs || convMsgRefs.length === 0) return;
    defaultedRef.current = true;
    const anyWaiting = (conversations || []).some(cv => waitingOn(convMsgRefs, cv && cv.id) === WAITING_ON_US);
    if (anyWaiting) setFilters(f => ({ ...f, waiting: WAITING_ON_US }));
  }, [convMsgRefs, conversations]);
  // escalations per conversation
  const notesByConv = {};
  (notifications || []).forEach(n => { if (n.conversation_id) (notesByConv[n.conversation_id] = notesByConv[n.conversation_id] || []).push(n); });

  // deep-link: auto-open the conversation matching a phone (from the Leads tab)
  useEffect(() => {
    if (!deepLinkPhone) return;
    const cv = findConversationForPhone(deepLinkPhone, conversations);
    // Reset the filters before opening. A deep link from the Leads tab means
    // "show me THIS thread", and arriving at a filtered list that excludes it —
    // then being dropped back into that list on Back — is the exact confusion
    // these controls exist to prevent.
    setFilters(NO_FILTERS);
    if (cv) setOpen(cv);
    if (clearDeepLink) clearDeepLink();
  }, [deepLinkPhone]); // eslint-disable-line

  // ?conversation= deep link: the same "show me THIS thread" as the phone link above.
  useEffect(() => {
    if (!deepLinkId) return;
    const cv = findConversationById(conversations, deepLinkId);
    setFilters(NO_FILTERS);
    if (cv) setOpen(cv);
    if (clearDeepLinkId) clearDeepLinkId();
  }, [deepLinkId]); // eslint-disable-line

  useEffect(() => {
    if (!open) { setMsgs([]); return; }
    setLoadingMsgs(true);
    db.getConversationMessages(open.id).then(setMsgs).catch(() => setMsgs([])).finally(() => setLoadingMsgs(false));
  }, [open]);
  // CRM-LEADS-2026-10-16 §2: the open thread follows the live refresh -- a new message on it shows without reopening it
  const openRefCount = open ? (convMsgRefs || []).filter((r) => r.conversation_id === open.id).length : 0;
  const seenRefCount = useRef(null);
  useEffect(() => {
    if (!open) { seenRefCount.current = null; return; }
    if (seenRefCount.current === null) { seenRefCount.current = openRefCount; return; }
    if (openRefCount === seenRefCount.current) return;
    seenRefCount.current = openRefCount;
    db.getConversationMessages(open.id).then(setMsgs).catch(() => {});
  }, [open, openRefCount]);

  // Reset the reply box whenever a different thread is opened.
  useEffect(() => {
    setTakeover(!!(open && open.human_takeover));
    setDraft(""); setStaffErr(""); setSending(false); setReleasing(false); setMarkingOffline(false); setOfflineErr("");
  }, [open]);

  const sendStaffReply = async () => {
    const body = draft.trim();
    if (!open || !body || sending) return;
    setSending(true); setStaffErr("");
    try {
      const res = await staff.reply(open.id, body);
      // Optimistic append: the row exists server-side, but re-fetching the whole
      // transcript to show one message the recruiter just typed is wasted latency.
      // status:'sent' mirrors what the worker writes on a successful staff reply, so the
      // optimistic row classifies identically to the one a re-fetch would return. On a
      // failure we never reach here — staff.reply throws and the row stays server-side
      // as 'failed', where reopening the thread will show it as not delivered.
      setMsgs(m => [...m, { id: `local-${res.sid || Date.now()}`, role: "human", body, created_at: new Date().toISOString(), twilio_sid: res.sid || null, status: "sent" }]);
      setDraft("");
      setTakeover(true);           // sending always takes the thread over
      open.human_takeover = true;  // keep the list row consistent without a reload
    } catch (e) {
      setStaffErr(e.message || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const resumeAgent = async () => {
    if (!open || releasing) return;
    setReleasing(true); setStaffErr("");
    try {
      await staff.release(open.id);
      setTakeover(false);
      open.human_takeover = false;
      // ESCALATION-LOOP-SPEC F. The worker's /staff-release now also writes
      // status='active' (recruiter-agent, spec C). Mirror it locally so the LIST is
      // right without a reload: conversations are loaded once at app start, so a
      // thread released here would otherwise keep showing 'handed_off' -- and now
      // that the Waiting-on column reads the same rows, a stale status is visible
      // in two places instead of one.
      //
      // Harmless if C has not shipped yet: this is a local object the next load
      // overwrites from the server either way. It cannot make the UI claim
      // something the server will not confirm on the next refresh.
      open.status = "active";
    } catch (e) {
      setStaffErr(e.message || "Could not resume the agent.");
    } finally {
      setReleasing(false);
    }
  };

  // "Handled off-system" (2026-09-25). Matt sometimes texts or calls a candidate from
  // his own phone; without this the thread showed unanswered forever, the sweep
  // flagged it every tick, and the agent could answer on top of him. Sends NOTHING.
  // The worker takes the thread over, writes one role='human' [handled-offline] row
  // (which the "waiting on" definition already counts as an answer, by role) and
  // resolves the thread's open escalations with handled_by = 'offline'.
  const markHandledOffline = async () => {
    if (!open || markingOffline) return;
    if (!window.confirm("Mark this thread as handled off-system?\n\nNothing is sent. The agent is paused on this thread, a note is added, and its open escalations are marked handled.")) return;
    setMarkingOffline(true); setOfflineErr("");
    try {
      await staff.handledOffline(open.id);
      const at = new Date().toISOString();
      setMsgs(m => [...m, { id: `local-offline-${Date.now()}`, role: "human", body: "[handled-offline]", created_at: at, twilio_sid: null, status: HANDLED_OFFLINE }]);
      markOpenHandledOffline(notifications, open.id, at);
      setTakeover(true);
      open.human_takeover = true;
      open.status = "handed_off";
    } catch (e) {
      setOfflineErr(e.message || "Could not mark the thread handled.");
    } finally {
      setMarkingOffline(false);
    }
  };

  // Every channel gets its own badge, including the ones nobody expected. The old
  // two-way ternary drew 'voice', NULL and any future value as "📱 SMS", so a row
  // on a channel this CRM had never heard of was indistinguishable from a text.
  const CHANNEL_BADGES = {
    chat:  { label: "💬 chat",  style: { background:"#ede9fe", color:"#6d28d9" } },
    sms:   { label: "📱 SMS",   style: { background:"#dbeafe", color:"#1d4ed8" } },
    voice: { label: "📞 voice", style: { background:"#dcfce7", color:"#166534" } },
    other: { label: "? unknown", style: { background:"#fee2e2", color:"#b91c1c" } },
  };
  const channelBadge = (ch) => {
    const b = CHANNEL_BADGES[channelOf({ channel: ch })] || CHANNEL_BADGES.other;
    return <span className="pill" style={b.style}>{b.label}</span>;
  };
  const identOf = (cv) => cv.candidate_phone || cv.candidate_id || "—";
  const escOf = (cv) => notesByConv[cv.id] || [];
  const hasStar = (cv) => escOf(cv).some(n => n.reason === "contact_provided");

  if (open) {
    const esc = escOf(open);
    return (
      <div className="fade-in">
        <button className="nb" onClick={() => setOpen(null)} style={{ display:"flex", alignItems:"center", gap:6, color:"#64748b", fontSize:13, fontWeight:600, marginBottom:12 }}><I n="back" s={16} c="#64748b"/> Back to conversations</button>
        <div className="card" style={{ padding:18, marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            {channelBadge(open.channel)}
            <span style={{ fontSize:16, fontWeight:800, color:"#0f172a" }}>{identOf(open)}</span>
            <span className="pill" style={{ background:"#f1f5f9", color:"#475569" }}>{open.status || "—"}</span>
            {takeover && <span className="pill" style={{ background:"#fef3c7", color:"#92400e" }}>human takeover</span>}
            <button className="nb" onClick={markHandledOffline} disabled={markingOffline}
              title="You texted or called this candidate from your own phone. Sends nothing."
              style={{ marginLeft:"auto", padding:"5px 11px", borderRadius:8, border:"1px solid #cbd5e1", background: markingOffline ? "#f1f5f9" : "white", color:"#334155", fontSize:12, fontWeight:700, cursor: markingOffline ? "default" : "pointer", whiteSpace:"nowrap" }}>
              {markingOffline ? "Marking…" : "✓ Handled off-system"}
            </button>
            <button className="nb"
              onClick={() => {
                const text = reviewRequestText();
                const done = (msg) => { setReviewCopied(msg); setTimeout(() => setReviewCopied(""), 2500); };
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(text).then(() => done("Copied"), () => done("Copy failed"));
                } else {
                  done("Copy failed");
                }
              }}
              title="Copies one neutral review request (Facebook, Indeed, Glassdoor) for you to send personally. Same wording for everyone, no incentive. Sends nothing."
              style={{ padding:"5px 11px", borderRadius:8, border:"1px solid #cbd5e1", background:"white", color:"#334155", fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
              {reviewCopied || "Copy review request"}
            </button>
            <span style={{ fontSize:12, color:"#94a3b8" }}>started {fmtWhen(open.started_at)}</span>
          </div>
          {offlineErr && (
            <div role="alert" style={{ marginTop:10, background:"#fef2f2", border:"1px solid #fecaca", color:"#b91c1c", borderRadius:8, padding:"8px 10px", fontSize:12.5 }}>
              ⚠ Not marked handled: {offlineErr}
            </div>
          )}
          {esc.length > 0 && (
            <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #f1f5f9" }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", marginBottom:6 }}>Escalations</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {esc.map((n, i) => (
                  <span key={i} className="pill" style={escalationHandledLabel(n) ? { background:"#f1f5f9", color:"#64748b", textDecoration:"line-through" } : { background:n.reason==="contact_provided"?"#f3e8ff":"#fff7ed", color:n.reason==="contact_provided"?"#7c3aed":"#c2410c" }} title={`${shortAlertId(n) ? `alert ${shortAlertId(n)} — ` : ""}${n.detail || ""}`}>
                    {n.reason === "contact_provided" ? "⭐ " : ""}{n.reason}{escalationHandledLabel(n) ? ` · ${escalationHandledLabel(n)}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {takeover && (
          <div data-testid="conv-handback-banner" style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:12, padding:"12px 14px", marginBottom:14 }}>
            <span style={{ fontSize:18, lineHeight:1 }}>🚗</span>
            <div style={{ flex:1, minWidth:220 }}>
              <div style={{ fontSize:13.5, fontWeight:800, color:"#92400e" }}>You have this one.</div>
              <div style={{ fontSize:12, color:"#b45309", marginTop:2 }}>
                The agent will not reply on this thread. Candidate replies still reach you as escalations (SMS + email).
              </div>
            </div>
            <button className="nb" data-testid="conv-handback-btn" onClick={resumeAgent} disabled={releasing}
              style={{ minHeight:44, padding:"7px 14px", borderRadius:8, border:"1px solid #92400e", background: releasing ? "#fde68a" : "#92400e", color: releasing ? "#92400e" : "white", fontSize:12.5, fontWeight:700, cursor: releasing ? "default" : "pointer", whiteSpace:"nowrap" }}>
              {releasing ? "Handing back…" : "Hand back to agent"}
            </button>
          </div>
        )}

        <div className="card" style={{ padding:16 }}>
          {loadingMsgs ? <div style={{ color:"#94a3b8", fontSize:13, padding:12 }}>Loading transcript…</div>
            : msgs.length === 0 ? <div style={{ color:"#94a3b8", fontSize:13, padding:12 }}>No messages.</div>
            : msgs.map(m => {
                // role='human' = a recruiter typed this in the reply box below (the worker
                // reuses the schema's existing 'human' value rather than adding 'staff',
                // so no migration was needed). It sits on the same side as the agent —
                // both are us — but in a different colour, so "did a person write this?"
                // is answerable at a glance.
                const isStaff = m.role === "human";
                const isAgent = m.role === "agent" || isStaff;

                // agent_messages.status. Most rows are NULL (an ordinary logged turn)
                // and render exactly as they always have. The rows that are NOT NULL
                // are the point: a 'failed' or 'cancelled' row is an attempt nobody
                // received, and a 'queued' row is a draft parked for the quiet-hours
                // send window. Drawing either as a normal bubble tells a recruiter the
                // candidate has read something they were never sent. The lifecycle and
                // the colours live in src/lib/messageStatus.js.
                const state = deliveryStateOf(m.status);

                return (
                  <div key={m.id} style={{ display:"flex", justifyContent:isAgent?"flex-end":"flex-start", marginBottom:10 }}>
                    <div style={{ maxWidth:"72%" }}>
                      <div style={bubbleStyleFor(state, { isStaff, isAgent })}>{m.body}</div>
                      <div style={{ fontSize:10.5, color:"#94a3b8", marginTop:3, textAlign:isAgent?"right":"left" }}>
                        {state.kind !== "ok" && (
                          // The label sits FIRST in the meta line, ahead of the author,
                          // so "this did not land" is read before the message is
                          // attributed to anyone. Icon and colour per kind come from
                          // stateBadge (messageStatus.js), so a new kind cannot fall into
                          // another kind's look.
                          <strong style={{ color: stateBadge(state).color, marginRight:6 }}>
                            {stateBadge(state).icon}{state.label}
                          </strong>
                        )}
                        {isStaff ? <strong style={{ color:"#b45309" }}>{STAFF_SENDER_LABEL}</strong> : m.role} · {fmtWhen(m.created_at)}{m.twilio_sid ? <span style={{ marginLeft:6, fontFamily:"monospace", opacity:.7 }}>{m.twilio_sid}</span> : null}
                      </div>
                      {/* §5 (NIGHT-DIRECTSTAFF-2026-10-17): Sent ✓ / Delivered / Failed with the plain reason, from Twilio's callback */}
                      {isStaff && (() => { const dl = deliveryLine(m); return <div data-testid="msg-delivery" style={{ fontSize:11, color:TONE_COLOR[dl.tone], textAlign:"right", marginTop:1 }}>{dl.text}</div>; })()}
                    </div>
                  </div>
                );
              })}
        </div>

        {/* Reply box — SMS threads only. A web-chat visitor has no address to deliver
            to once the widget closes, so the worker rejects chat replies outright;
            showing the box there would just be a button that always fails. */}
        {open.channel === "sms" ? (
          <div className="card" style={{ padding:14, marginTop:14 }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", marginBottom:8 }}>
              Reply as staff {!takeover && <span style={{ textTransform:"none", fontWeight:600, color:"#b45309" }}>· sending pauses the agent</span>}
            </div>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendStaffReply(); }}
              placeholder={`Text ${identOf(open)} directly…`}
              rows={3}
              maxLength={1600}
              disabled={sending}
              style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", borderRadius:10, border:"1px solid #cbd5e1", fontSize:13.5, lineHeight:1.5, fontFamily:"inherit", resize:"vertical", color:"#0f172a" }}
            />
            {staffErr && (
              <div style={{ marginTop:8, background:"#fef2f2", border:"1px solid #fecaca", color:"#b91c1c", borderRadius:8, padding:"8px 10px", fontSize:12.5 }}>{staffErr}</div>
            )}
            <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:10 }}>
              <span style={{ fontSize:11.5, color: draft.length > 1500 ? "#b91c1c" : "#94a3b8" }}>{draft.length}/1600 · ⌘/Ctrl+Enter to send</span>
              {/* §7 (DAY-AGENTS-2026-10-03): the lead's local time beside Send, and when quiet hours there lift. A staff
                  reply is not held (it goes at once); this says what time it lands for them. */}
              <span style={{ marginLeft:"auto" }}>
                <LocalTimeBadge state={(leadForConversation(open, leads) || {}).state} showLift />
              </span>
              <button
                onClick={sendStaffReply}
                disabled={sending || !draft.trim()}
                style={{ marginLeft:8, padding:"8px 18px", borderRadius:8, border:"none", background: (sending || !draft.trim()) ? "#cbd5e1" : "#1d4ed8", color:"white", fontSize:13, fontWeight:700, cursor: (sending || !draft.trim()) ? "default" : "pointer" }}>
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding:14, marginTop:14, fontSize:12.5, color:"#94a3b8" }}>
            Staff replies are SMS-only — a web-chat visitor has no address to reach once the widget closes.
            {takeover && " Use Resume agent above to hand this thread back."}
          </div>
        )}
      </div>
    );
  }

  // Ordering is applied to the FULL set, then the filters narrow it. Doing it in
  // that order keeps ordering and filtering independent, which is what lets the
  // "All conversations" reset restore the list without touching the sort.
  const ordered = sortByActivity(conversations, activity);
  const filteredRows = applyFilters(ordered, filters, convMsgRefs);
  // Waiting on us (the default whenever anything is waiting): OLDEST WAIT FIRST. The
  // activity sort put the freshest thread on top, which is the one that has waited
  // least. Every other view keeps the activity order.
  const rows = filters.waiting === WAITING_ON_US
    ? orderOldestWaitingFirst(filteredRows, convMsgRefs)
    : filteredRows;
  const activeFilter = describeFilters(filters);
  const bulkMode = filters.waiting === WAITING_ON_US;
  const eligible = bulkMode ? new Set(eligibleIds(rows, convMsgRefs)) : new Set();
  const pickedNow = [...picked].filter(id => eligible.has(id));
  const togglePick = (id) => setPicked(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allPicked = eligible.size > 0 && pickedNow.length === eligible.size;
  const runBulkOffline = async () => {
    const ids = pickedNow;
    if (!ids.length || bulkBusy) return;
    if (!window.confirm(confirmText(ids.length))) return;
    setBulkMsg("");
    const at = new Date().toISOString();
    const r = await runBulk(ids, async (id) => {
      await staff.handledOffline(id);
      markOpenHandledOffline(notifications, id, at);
      const cv = (conversations || []).find(c => c.id === id);
      if (cv) { cv.human_takeover = true; cv.status = "handed_off"; }
    }, (i, n) => setBulkBusy(`Marking ${i}/${n}…`));
    setBulkBusy("");
    setPicked(new Set(r.failed.map(f => f.id)));
    setBulkMsg(resultText(r));
    if (onRefresh) onRefresh();
  };
  const td0 = { padding:"10px 12px", borderBottom:"1px solid #f1f5f9" };
  const selStyle = { fontSize:12, padding:"7px 9px", borderRadius:8, border:"1px solid #e2e8f0", background:"white", color:"#334155" };

  return (
    <div className="fade-in">
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:16 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:800, color:"#0f172a" }}>Conversations</h2>
          {/* The count is ALWAYS "shown of total". A single bare number could not
              distinguish "this is everything" from "this is what survived a
              filter", which is the ambiguity the whole audit turned on. */}
          <p style={{ fontSize:13, color:"#64748b", marginTop:2 }}>
            agent_conversations — every channel the recruiter agent talks on. {countLabel(rows.length, conversations.length)}.
            {/* A refresh button with no timestamp cannot tell you whether it worked. */}
            {loadedAt ? <> · read {fmtWhen(loadedAt.toISOString())}</> : null}
          </p>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          {/* The list is fetched once at mount. Without this, a tab left open all
              day shows the morning's data and looks exactly like an idle agent —
              which is the ambiguity the 2026-09-08 audit had to rule out first. */}
          <button
            className="nb"
            onClick={onRefresh}
            disabled={refreshing || !onRefresh}
            title="Re-read conversations, messages, escalations and lead decisions"
            style={{ background:"#f1f5f9", color:"#334155", borderRadius:8,
                     padding:"8px 14px", fontSize:12, fontWeight:700,
                     cursor: refreshing ? "default" : "pointer", opacity: refreshing ? 0.6 : 1 }}
          >
            {refreshing ? "Refreshing…" : "↻ Refresh"}
          </button>
          <button
            className="nb"
            onClick={() => setFilters(NO_FILTERS)}
            disabled={isUnfiltered(filters)}
            title="Clear every filter. Ordering is unaffected."
            style={{
              background: isUnfiltered(filters) ? "#f1f5f9" : "#1d4ed8",
              color: isUnfiltered(filters) ? "#94a3b8" : "white",
              borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700,
              cursor: isUnfiltered(filters) ? "default" : "pointer",
            }}
          >
            {isUnfiltered(filters) ? "Showing all conversations" : "Show all conversations"}
          </button>
          <select style={selStyle} value={filters.channel} onChange={e => setFilters(f => ({ ...f, channel: e.target.value }))}>
            <option value={ALL}>All channels</option>
            {channelsPresent(conversations).map(c => <option key={c} value={c}>{CHANNEL_BADGES[c] ? CHANNEL_BADGES[c].label : c}</option>)}
          </select>
          <select style={selStyle} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value={ALL}>All statuses</option>
            {statusesPresent(conversations).map(st => <option key={st} value={st}>{st}</option>)}
          </select>
          {/* ESCALATION-LOOP-SPEC E. Three values, and the two non-ALL ones are the
              same strings conversationTriage exports -- so the filter and the pill
              cannot drift apart about what "us" means. */}
          <select style={selStyle} value={filters.waiting || ALL} onChange={e => setFilters(f => ({ ...f, waiting: e.target.value }))}>
            <option value={ALL}>Waiting on anyone</option>
            <option value={WAITING_ON_US}>Waiting on us</option>
            <option value={WAITING_ON_THEM}>Waiting on candidate</option>
          </select>
        </div>
      </div>

      {bulkMode && (pickedNow.length > 0 || bulkBusy || bulkMsg) && (
        <div className="card" style={{ padding:"10px 14px", marginBottom:12, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          {pickedNow.length > 0 && (
            <button className="nb" onClick={runBulkOffline} disabled={!!bulkBusy}
              title="You handled these from your own phone or email. Sends nothing."
              style={{ padding:"6px 12px", borderRadius:8, border:"1px solid #cbd5e1", background: bulkBusy ? "#f1f5f9" : "white", color:"#334155", fontSize:12.5, fontWeight:700, cursor: bulkBusy ? "default" : "pointer" }}>
              {bulkBusy || `✓ Mark ${pickedNow.length} handled off-system`}
            </button>
          )}
          {bulkMsg && <span role="status" style={{ fontSize:12.5, color:"#334155" }}>{bulkMsg}</span>}
        </div>
      )}

      {rows.length === 0 ? (
        // Two genuinely different states, and the old code said the first thing
        // in both. "No conversations yet" is a claim about the DATABASE; this
        // component can only see an array, and when a filter emptied that array
        // the claim was simply false.
        <div className="card" style={{ padding:32, textAlign:"center", color:"#94a3b8", fontSize:14 }}>
          {activeFilter ? (
            <>
              <div style={{ color:"#475569", fontWeight:600 }}>
                No conversations match this filter ({activeFilter}).
              </div>
              <div style={{ marginTop:6 }}>
                {conversations.length} conversation{conversations.length === 1 ? "" : "s"} loaded.{" "}
                <button className="nb" onClick={() => setFilters(NO_FILTERS)} style={{ background:"none", color:"#1d4ed8", fontWeight:700, textDecoration:"underline", padding:0, fontSize:14 }}>
                  Show all conversations
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ color:"#475569", fontWeight:600 }}>No conversations loaded.</div>
              <div style={{ marginTop:6 }}>
                No filter is active, so this is the whole table as this session can read it.
                If you expect rows here, check the <code>authenticated</code> SELECT policy on
                <code> agent_conversations</code> before assuming the agent is idle.
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="card" style={{ overflow:"auto" }}>
          <table className="stack-table" style={{ width:"100%", borderCollapse:"collapse", minWidth:940 }}>
            <thead><tr>
              {bulkMode && (
                <th style={{ padding:"10px 12px", width:28 }}>
                  <input type="checkbox" aria-label="Select every thread waiting on us" checked={allPicked} disabled={eligible.size === 0 || !!bulkBusy}
                    onChange={() => setPicked(allPicked ? new Set() : new Set(eligible))} />
                </th>
              )}
              {["","Channel","Identifier","Status","Waiting on","Started","Last activity","Messages","Escalation"].map((h,i) => <th key={i} style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", borderBottom:"1px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map(cv => {
                const esc = escOf(cv);
                const act = activityFor(activity, cv.id);
                return (
                  <tr key={cv.id} className="card-hover" style={{ cursor:"pointer" }} onClick={() => setOpen(cv)}>
                    {bulkMode && (
                      <td style={{ ...td0, width:28 }} onClick={e => e.stopPropagation()}>
                        {eligible.has(cv.id) && (
                          <input type="checkbox" aria-label="Select this thread" checked={picked.has(cv.id)} disabled={!!bulkBusy}
                            onChange={() => togglePick(cv.id)} />
                        )}
                      </td>
                    )}
                    <td style={{ ...td0, width:24 }}>{hasStar(cv) ? "⭐" : ""}</td>
                    <td style={td0} data-label="Channel">{channelBadge(cv.channel)}</td>
                    <td style={{ ...td0, fontWeight:600, fontSize:13, color:"#1e293b", whiteSpace:"nowrap" }} data-label="Identifier">{identOf(cv)}</td>
                    <td style={td0} data-label="Status"><span className="pill" style={{ background:"#f1f5f9", color:"#475569" }}>{cv.status || "—"}</span></td>
                    <td style={td0} data-label="Waiting on">{(() => {
                      // waitingPill is never null and an unknown state says so, so this
                      // cell always renders something rather than collapsing.
                      const st = waitingOn(convMsgRefs, cv.id);
                      // Minutes, not whole hours, and a marker past WAITING_ALERT_MINUTES.
                      const pill = waitingBadge(st, waitingMinutes(convMsgRefs, cv.id));
                      const tone = pill.tone === "red"
                        ? { background:"#fee2e2", color:"#b91c1c", fontWeight:700 }
                        : pill.tone === "amber"
                        ? { background:"#fef3c7", color:"#92400e" }
                        : { background:"#f1f5f9", color:"#64748b" };
                      return <span className="pill" title={pill.title} style={{ ...tone, whiteSpace:"nowrap" }}>{pill.label}</span>;
                    })()}</td>
                    <td style={{ ...td0, fontSize:12, color:"#64748b", whiteSpace:"nowrap" }} data-label="Started">{fmtWhen(cv.started_at)}</td>
                    {/* From max(agent_messages.created_at) — NOT last_message_at,
                        which nothing writes. An em dash means no messages, which
                        is a real and interesting state, not a missing value. */}
                    <td style={{ ...td0, fontSize:12, color: act.lastAt ? "#334155" : "#cbd5e1", whiteSpace:"nowrap" }} data-label="Last activity">
                      {act.lastAt ? fmtWhen(act.lastAt) : "—"}
                    </td>
                    <td style={{ ...td0, fontSize:13, color:"#1e293b" }} data-label="Messages">{act.count}</td>
                    <td style={td0} data-label="Escalation">
                      {esc.length === 0 ? <span style={{ color:"#cbd5e1", fontSize:12 }}>—</span>
                        : <span className="pill" style={{ background:hasStar(cv)?"#f3e8ff":"#fff7ed", color:hasStar(cv)?"#7c3aed":"#c2410c" }}>{hasStar(cv) ? "⭐ contact" : esc.length + " alert" + (esc.length>1?"s":"")}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
